/**
 * The Executive Report — a board paper, not a data dump.
 *
 * A board paper answers five questions in order: what is the decision, what is
 * it worth, why does it work, what has to be true, and what do we do next. So
 * that is the structure: the executive pages, then the technical appendices a
 * director will never read but an operations manager will pick apart.
 *
 * DESIGN SYSTEM (Claude Design spec, 2026-08-21 — "Season Operating Plan"):
 * one brand palette (deep green / gold / cream), one shared page chrome
 * (header band, kicker, H1, gold rule, footer), ONE autotable theme for every
 * table, flat colour only (tints are pre-mixed hexes — jsPDF has no alpha),
 * Helvetica only, WinAnsi glyphs only (– · › ± and hyphen-minus; never → − ★).
 *
 * Charts are REAL VECTORS: each is built as an SVG and handed to svg2pdf, the
 * same route the carriage-label builder uses. Everything is dynamically
 * imported so ~1.4MB of jsPDF/svg2pdf stays out of the main bundle.
 *
 * Two rules the document must never break:
 *   - the headline is a RANGE, because Mode B is a counterfactual this railway
 *     has never run; and
 *   - every assumption is labelled as an assumption, in Appendix D.
 */
import type { jsPDF } from 'jspdf'
import type {
  CostBreakdown, CostModel, CrossingConflict, Departure, FleetCaps, Network,
  PathUsage, Scenario, StockImbalance, TransferSummary,
} from './types'
import type { BacktestResult } from './backtest'
import type { AdvisorRunLog } from './advisorClient'
import { costLines } from './costs'
import { fmtMin } from './demand'
import { journeyMin } from './network'
import { yearTotals, monthFigures, type MonthFigures, type YearRow } from './YearComparePanel'
import type { TtExportWorking } from '../researchTimetableXlsx'

// ---------------------------------------------------------------------------
//  Palette — flat, pre-mixed; no transparency anywhere (Design spec §2.1)
// ---------------------------------------------------------------------------

const BRAND = '#0b3d2e'      // header band, table heads, decision panel, H1
const BRAND_MID = '#375f50'  // "Current" sub-band on split tables
const GOLD = '#c89b3c'       // header rule, hero number on green, accents
const GOLD_TEXT = '#8a6a1f'  // kickers / small-caps labels on white or cream
const INK = '#1d1d1b'
const MUTED = '#6b6b6b'
const RULE = '#d8d3c6'       // card borders, footer rule
const RULE_LIGHT = '#efece3' // table row rules, chart gridlines
const CREAM = '#f2efe4'      // stat strips, month bands, totals rows
const PAPER = '#faf9f5'      // zebra stripe
const POS = '#2e7d32'        // positive money, PASS chips, load >=55%
const NEG = '#b23b2e'        // negative money, "withdrawn", load <10%
const WARN_BG = '#f6ecdd'
const WARN_INK = '#b45309'
const PALE_GREEN = '#b9cabf' // "today" bars when paired against proposed
const POSTER_TEAL = '#9cc5b4'
const HEADER_SUB = '#bcd0c5' // right-hand text on the brand header band
const PANEL_RULE = '#2c5545' // hairline inside the decision panel
const GREY_KIND = '#9b968a'  // Req/Dep/Arr kinds, ECS rows, no-service days
const CLAUDE_INK = '#6d5bd0'
const CLAUDE_BG = '#ece7fb'

const gbp0 = (v: number) => `${v < 0 ? '-' : ''}£${Math.round(Math.abs(v)).toLocaleString('en-GB')}`
const num0 = (v: number) => Math.round(v).toLocaleString('en-GB')
const signed = (v: number) => `${v >= 0 ? '+' : '-'}£${Math.round(Math.abs(v)).toLocaleString('en-GB')}`
const gbpK = (n: number) => {
  const a = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (a >= 1_000_000) return `${sign}£${(a / 1_000_000).toFixed(2)}m`
  if (a >= 1_000) return `${sign}£${(a / 1_000).toFixed(0)}k`
  return `${sign}£${Math.round(a)}`
}
const kLabel = (v: number) => {
  const a = Math.abs(v)
  return a < 500 ? `${v < 0 ? '-' : '+'}£${Math.round(a)}` : `${v < 0 ? '-' : '+'}£${Math.round(a / 1000)}k`
}

/** jsPDF's core Helvetica is WinAnsi-only: arrows, the Unicode minus and
 *  dingbats render as garbage. Data-borne strings (narratives, sensitivity
 *  labels, assumption notes) are sanitised at the door. */
const WA_SWAPS: [RegExp, string][] = [
  [/\u2192/g, ' \u203a '], [/\u2190/g, ' \u2039 '], [/\u2194/g, ' \u2013 '],
  [/\u2212/g, '-'], [/[\u2605\u2606]/g, '*'], [/\u2205/g, 'none'],
  [/[\u26A0\uFE0F\u2713\u2714\u2717]/g, ''], [/\u00A0/g, ' '], [/\u2026/g, '...'],
  [/\u2265/g, '>='], [/\u2264/g, '<='], [/[\u2191\u2B06]/g, 'up'], [/[\u2193\u2B07]/g, 'down'],
]
function waStr(s: string): string {
  let out = s
  for (const [re, r] of WA_SWAPS) out = out.replace(re, r)
  return out.replace(/  +/g, ' ')
}
/** Round the float noise out of generated basis strings (£19.549999...). */
const tidyBasis = (s: string): string => s.replace(/(\d+\.\d{3,})/g, (m) => Number(m).toFixed(2))
function sanitiseWinAnsi(obj: unknown, seen = new WeakSet<object>()): void {
  if (obj == null || typeof obj !== 'object') return
  if (seen.has(obj as object)) return
  seen.add(obj as object)
  const rec = obj as Record<string, unknown>
  for (const k of Object.keys(rec)) {
    const v = rec[k]
    if (typeof v === 'string') rec[k] = waStr(v)
    else if (v && typeof v === 'object') sanitiseWinAnsi(v, seen)
  }
}

// ---------------------------------------------------------------------------
//  Input
// ---------------------------------------------------------------------------

export interface ExecSide {
  label: string
  scenario: Scenario
  deps: Departure[]
  costs: CostBreakdown
  /** Costs for the whole period (day or week), i.e. per-day cost x running days. */
  periodCosts: number
  /** Revenue for the whole period including the untimed walk-up uplift. */
  periodRevenue: number
  paths: PathUsage[]
  conflicts: CrossingConflict[]
  imbalances: StockImbalance[]
  transfers: TransferSummary[]
  setsInTraffic: number
  /** Per-working set assignment (departure id -> set label, e.g. "W1"), for the
   *  "Working turns and capacity spread" panel. Optional: only the sides built
   *  for that panel carry it. */
  setByDep?: Record<string, string>
  /** Per-working loco (departure id -> { label:"Steam 1", code:"S1" }). */
  locoByDep?: Record<string, { label: string; code: string }>
}

export interface ExecMonthRow { label: string; current: number; proposed: number }

/** One day-of-week line of the month view (Appendix G), one side. */
export interface ExecMonthViewSide {
  services: number; steamN: number; dieselN: number
  pax: number; revenue: number; costs: number; profit: number
}
export interface ExecMonthViewRow { dow: string; cur: ExecMonthViewSide | null; mod: ExecMonthViewSide | null }

export interface ExecSensitivity { label: string; contribution: number; note: string }

// ---- The season, day by day (Chris, 2026-08-21: the executive business plan) ----

/** One public poster working: the 9 timing tokens + the published traction flag. */
export interface PosterWork { t: (string | null)[]; flag: string }
/** One PROPOSED pattern for the poster and the diagram appendix. */
export interface PosterPattern {
  id: string
  name: string
  colour: string
  workingsPerDay: number
  daysUsed: number
  monthsUsed: string[]
  N: PosterWork[]
  S: PosterWork[]
}
export interface PosterMonthCell { day: number; colour: string | null; special?: string }
export interface PosterMonth { label: string; cells: (PosterMonthCell | null)[] }
export interface SeasonPoster {
  seasonLabel: string
  months: PosterMonth[]
  patterns: PosterPattern[]
  specials: { label: string; days: number }[]
}

/** One (month x timetable colour x day-type) group of the cuts analysis. */
export interface CutGroupRow {
  month: string
  colour: string
  dayClass: string
  days: number
  planName: string
  curSvc: number
  propSvc: number
  curLocos: string
  propLocos: string
  curSets: number
  propSets: number
  curCostD: number
  propCostD: number
  curCarriedD: number
  propCarriedD: number
  /** Current workings with no near-match in the proposal (same journey ±20 min). */
  withdrawn: { time: string; route: string }[]
  retimed: number
  kept: number
}

/** The season roll-up for the executive summary's uplift block. */
export interface SeasonSummary {
  days: number
  turnsCur: number
  turnsProp: number
  costCur: number
  costProp: number
  revenueCur: number
  revenueProp: number
  profitCur: number
  profitProp: number
  carriedCur: number
  carriedProp: number
  locosCur: number
  locosProp: number
  monthsCovered: number
  monthsTotal: number
}

/** One proposed pattern's TTBuilder-format workings for the diagram appendix. */
export interface PlanWttPattern {
  name: string
  colour: string
  monthsUsed: string[]
  daysUsed: number
  N: TtExportWorking[]
  S: TtExportWorking[]
}

export interface MonthPnlRow {
  colour: string
  dayClass: string
  days: number
  planName: string
  svcCur: number
  svcProp: number
  revD: number
  costD: number
  profitD: number
  curProfitD: number
}
export interface MonthPnl { month: string; days: number; rows: MonthPnlRow[] }

export interface ExecAssumption {
  name: string
  value: string
  provenance: 'measured' | 'assumed' | 'claude'
  note: string
  sensitivity?: string
}

// ---- Report A (Chris, 2026-08-27) — the full board paper ----
export interface ExecDaySide {
  services: number; steamN: number; dieselN: number
  pax: number; revenue: number; costs: number; profit: number
  /** Carriage sets in traffic that day. */
  sets?: number
  /** True when pax/revenue are the date's REAL sales from the basis season
   *  (unoptimised side), not a day-type average. */
  actual?: boolean
  /** True when the like-for-like basis day ran NO standard services (gala /
   *  event / not running): 0 services, 0 passengers, fixed cost only —
   *  every variable cost removed (Chris, 2026-09-03). */
  noService?: boolean
  /** A running date NO timetable pattern covers (a Diner/gala colour, a colour
   *  thebigplan does not publish): fixed cost only, nothing to model. */
  unscorable?: boolean
  /** The like-for-like basis day this date's figures come from. */
  basisDate?: string
  /** A Firecon day (Chris, 2026-09-11): steam stood down, which day of the run,
   *  and the sales % down on the like day. Modelled side only. */
  firecon?: { day: number; pct: number }
  /** The availability calendar's effect on this date (fleetAvailability.ts):
   *  steam turns run diesel for want of a steam engine, turns with no engine,
   *  the engines out that day. Modelled side only. */
  fleet?: { swapped: number; noEngine: number; out: { label: string; reason: string }[] }
  /** A FAILURE DAY: no engine of any traction for a turn — valued as a
   *  non-running day, printed red (Chris, 2026-09-11). */
  failure?: boolean
}
/** One month of the fleet's availability for the board paper: the engines out
 *  (with days and reasons), the worst-day availability by traction, and what it
 *  did to the design — steam turns run diesel, turns with no engine. */
export interface ExecFleetMonth {
  label: string; days: number
  outDays: { label: string; days: number; reasons: string[] }[]
  swapped: number; noEngine: number
  steamMin: number | null; dieselMin: number | null
  /** Dates the timetable could not be engined at all — non-running, red. */
  failureDays?: number
}
/** One engine's season on the roster (engineRoster.ts): contracted minimum,
 *  miles rostered, days worked, how many blocks it worked in. */
export interface ExecFleetEngine { label: string; traction: string; minimum: number | null; miles: number; days: number; blocks: number }
/** One month's cost by category, from the Costs tab (post-build). `perDay` is
 *  the modelled (proposed) £/day; `curPerDay` is today's, for before/after. */
export interface ExecMonthCatCosts {
  key: string; label: string; days: number
  perDay: { loco: number; carriages: number; crew: number; infra: number; overhead: number }
  curPerDay?: { loco: number; carriages: number; crew: number; infra: number; overhead: number }
}
export interface ExecExampleDayService {
  time: string; route: string; arr: string; set: string; capacity: number; loco: string; cost: number
}
/** One worked running day, costed service by service (the Costs & Modelling
 *  section's example — Chris wants the £/day total exploded). */
export interface ExecExampleDay {
  label: string; monthLabel: string
  services: ExecExampleDayService[]
  ticketSplit: { item: string; tickets: number; net: number; share: number }[]
  /** The day's cost line items — how the £/day total is made up. On the P&L
   *  basis these are costLines.ts::pnlDayLines: every fixed line's share of the
   *  month and every variable line at its rate × the day's activity. */
  costLines: { label: string; amount: number; detail?: string }[]
  /** Which model priced this day: the Management P&L ('pnl'), or the legacy rate
   *  card only when no P&L basis is loaded. */
  costBasis?: 'pnl' | 'rate-card'
  /** The day's share of its month's FIXED lines and the per-running-day charge —
   *  the DAY's cost, never one train's (law 1). */
  fixedShare?: number
  runningDayCharge?: number
  /** Empty-stock moves this day: they carry nobody but burn train-miles, which
   *  the day's variable cost includes — listed so the services add up to the day. */
  ecsMoves?: { count: number; miles: number; cost: number }
  optimised: ExecDaySide
  unoptimised: ExecDaySide
}
/** One day-cell in a Weekly Service Schedule calendar grid. */
export interface ExecWssCell {
  date: number; colour: string | null; dayClass: string; side: ExecDaySide | null
  /** Pullman dining trafficking through this date (like-for-like basis day,
   *  filtered by the Daytime/Night-time running toggles). */
  pullman?: { services: number; covers: number; net: number } | null
}
export interface ExecWssWeek { label: string; cells: (ExecWssCell | null)[]; total: ExecDaySide | null }
export interface ExecWssMonth {
  key: string; label: string; optimised: ExecWssWeek[]; unoptimised: ExecWssWeek[]
  /** Which season's ACTUAL sales price the unoptimised side (Chris, 2026-09-02:
   *  "clearly state which season this is pulling the data from"). */
  basis?: string
  /** Design dates with no like-for-like basis day — shown at the day-type
   *  average instead, and said so. */
  unmatchedDates?: number
  /** Dates whose like-for-like basis day ran NO standard services. */
  noServiceDates?: number
  /** Firecon days this month (steam stood down, diesel only) and the sales
   *  reductions by day of the run, for the note under the grid. */
  firecon?: { dates: number; reductions: number[] } | null
  /** Pullman dining: `workings` is the number of SERVICES (one = out and back);
   *  `legs` are its own paths in the working timetable, by colour; `dates` how
   *  many dates this month it runs on the like-day programme. */
  pullman?: { workings: number; conflicts: number; description?: string; legs?: { colour: string; time: string; from: string; to: string; arr: string }[]; dates?: number } | null
}

/** Everything a per-month APPENDIX block needs — a representative day of the
 *  month scored both ways, its demand profile, working timetable, running
 *  diagrams and public poster (Chris's full per-month appendix, 2026-08-27). */
export interface ExecAppendixMonth {
  key: string; label: string; dayClassLabel: string
  current: ExecSide; proposed: ExecSide
  demandProfile: { time: string; north: number; south: number }[]
  wtt: { name: string; N: TtExportWorking[]; S: TtExportWorking[] }[]
  diagrams: PlanWttPattern[]
  posterMonth: PosterMonth | null
  posterPatterns: PosterPattern[]
}

/** The accounts-anchored cost model for the board paper (Management P&L basis). */
export interface ExecCostPnlRow { label: string; behaviour: string; rate: string | null; basis: number; model: number; delta: number; included: boolean }
export interface ExecCostPnl {
  basis: string
  hasModel: boolean
  flexCap: number
  basisTotal: number
  modelTotal: number
  groups: { title: string; rows: ExecCostPnlRow[] }[]
}
/** The example month's cost LINE BY LINE on the P&L, per running day, today
 *  against the proposal (costLines.ts::pnlDayLines — the same lines and rates
 *  that price the season table, so this table is that table ÷ running days). */
export interface ExecPnlDayLine { label: string; kind: 'fixed' | 'variable'; detail: string; amount: number }
export interface ExecExampleMonthPnl {
  label: string; runningDays: number
  today: ExecPnlDayLine[]; proposed: ExecPnlDayLine[]
  todayActivity: { services: number; carriedPerDay: number; miles: number }
  proposedActivity: { services: number; carriedPerDay: number; miles: number }
}

export interface ExecReportData {
  /** Report A = the full board paper (Chris, 2026-08-27); 'B' (default) is the
   *  original. Report A reorders the body and adds Costs & Modelling, the
   *  season-table page, the Weekly Service Schedule and a per-month appendix. */
  variant?: 'A' | 'B'
  /** Report A — the accounts-anchored Modelling Costs (Management P&L basis):
   *  Table 1 (source) vs Table 2 (after the build), with the per-line change. */
  costModelPnl?: ExecCostPnl | null
  /** The example month per running day, line by line on the P&L. */
  exampleMonthPnl?: ExecExampleMonthPnl | null
  /** Report A — per-month cost by category (the Costs tab, post-build). */
  monthCosts?: ExecMonthCatCosts[]
  /** Report A — one worked running day (August), costed service by service. */
  exampleDay?: ExecExampleDay | null
  /** Report A — the fleet month by month: engines out, worst-day availability,
   *  steam turns run diesel, turns with no engine (fleetAvailability.ts). */
  fleetMonths?: ExecFleetMonth[]
  /** Report A — each engine's season on the roster (engineRoster.ts). */
  fleetEngines?: ExecFleetEngine[]
  /** Report A — the Weekly Service Schedule grid per month (optimised +
   *  unoptimised). August prints in the body; every month in the appendix. */
  wssMonths?: ExecWssMonth[]
  /** Report A — per-month appendix data (representative day, WTT, diagrams,
   *  poster) for the complete per-month appendix blocks. */
  appendixMonths?: ExecAppendixMonth[]
  net: Network
  season: string
  monthLabel: string
  dayClassLabel: string
  period: 'day' | 'week'
  runningDays: number
  /** Multiplier from one period to a whole season, for the annualised headline. */
  periodsPerSeason: number
  preparedBy: string
  dateIso: string
  dataAsAt: string
  scenarioId: number | null
  scenarioName: string

  current: ExecSide
  proposed: ExecSide

  /** Demand by departure time, both directions, for the "one picture" page. */
  demandProfile: { time: string; north: number; south: number }[]
  monthly: ExecMonthRow[]
  /** Season-by-month, both sides (the year summary view) — Chris, 2026-08-20. */
  yearRows?: YearRow[]
  /** The modelled month by day of week, both sides (Appendix G). */
  monthView?: ExecMonthViewRow[] | null
  /** The PROPOSED season timetable in thebigplan TTBuilder format — one
   *  pattern per genuinely different day (Weekday/Saturday/Sunday…), each with
   *  headcodes, loco, set, the 9 timing slots and the Forms chain. Printed as
   *  the working-timetable pages of Appendix A. */
  seasonWtt?: { name: string; N: TtExportWorking[]; S: TtExportWorking[] }[]
  sensitivity: ExecSensitivity[]
  assumptions: ExecAssumption[]

  /** The season, day by day — the executive business plan sections
   *  (Chris, 2026-08-21). All optional: a month-only session still prints. */
  poster?: SeasonPoster | null
  cuts?: CutGroupRow[]
  seasonSummary?: SeasonSummary | null
  planWtt?: PlanWttPattern[]
  monthPnl?: MonthPnl[]

  rates: CostModel
  fleet: FleetCaps
  backtest: BacktestResult | null
  advisor: AdvisorRunLog | null
  /** "What's changed" executive bullets — Chris-edited, printed on the
   *  recommendation page. */
  whatsChanged: string[]
  findings: string[]
  risks: string[]
  nextSteps: string[]
  censoringNote: string | null
}

// ---------------------------------------------------------------------------
//  SVG helpers — hand-built so there is no React/DOM render step.
//  Convention: every chart SVG is drawn 520 units wide and rendered at 178mm,
//  so 1 SVG unit = 0.342mm and font sizes in SVG units read as ~points.
// ---------------------------------------------------------------------------

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const txt = (
  x: number, y: number, s: string,
  o: { size?: number; fill?: string; anchor?: 'start' | 'middle' | 'end'; weight?: number } = {}
): string =>
  `<text x="${x}" y="${y}" font-family="Helvetica, Arial, sans-serif" font-size="${o.size ?? 9}" ` +
  `fill="${o.fill ?? INK}" text-anchor="${o.anchor ?? 'start'}"${o.weight ? ` font-weight="${o.weight}"` : ''}>${esc(s)}</text>`

const rect = (x: number, y: number, w: number, h: number, fill: string, extra = ''): string =>
  `<rect x="${x}" y="${y}" width="${Math.max(0, w)}" height="${Math.max(0, h)}" fill="${fill}" ${extra}/>`

const line = (x1: number, y1: number, x2: number, y2: number, stroke = RULE_LIGHT, width = 0.6, dash = ''): string =>
  `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`

const svgWrap = (w: number, h: number, body: string): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`

/** Single-series monthly profit-and-loss bars (p2) — pos/neg by sign, value
 *  labels above/below every bar, labelled zero line. Design spec §2.5.1. */
function chartProfitBars(rows: { label: string; v: number | null }[], w: number, h: number): string {
  const vals = rows.filter((r) => r.v != null).map((r) => Math.abs(r.v as number))
  if (vals.length === 0) return svgWrap(w, h, txt(w / 2, h / 2, 'No monthly data available', { anchor: 'middle', fill: MUTED }))
  const maxV = Math.max(1, ...vals)
  const padL = 30, padR = 4, padT = 16, padB = 16
  const plotH = h - padT - padB
  const half = plotH / 2
  const zeroY = padT + half
  const bw = (w - padL - padR) / rows.length
  const barW = Math.min(35, bw * 0.62)
  let body = line(padL, zeroY, w - padR, zeroY, INK, 1)
  body += txt(padL - 4, zeroY + 3, '£0', { size: 6.5, fill: MUTED, anchor: 'end' })
  rows.forEach((r, i) => {
    const x = padL + i * bw + (bw - barW) / 2
    const cx = x + barW / 2
    if (r.v != null) {
      const neg = r.v < 0
      const bh = Math.max(2, (Math.abs(r.v) / maxV) * (half - 12))
      const fill = neg ? NEG : POS
      body += rect(x, neg ? zeroY : zeroY - bh, barW, bh, fill)
      body += txt(cx, neg ? zeroY + bh + 9 : zeroY - bh - 4, kLabel(r.v), { size: 8, weight: 700, fill, anchor: 'middle' })
    } else {
      body += txt(cx, zeroY - 4, '–', { size: 8, fill: MUTED, anchor: 'middle' })
    }
    body += txt(cx, h - 4, r.label, { size: 8.5, fill: MUTED, anchor: 'middle' })
  })
  return svgWrap(w, h, body)
}

/** Paired bars, today (pale) against the proposal (solid) — p3's cost chart
 *  and the month-only fallback contribution chart. Design spec §2.5.2. */
function chartPairedBars(rows: { label: string; cur: number | null; mod: number | null }[], w: number, h: number): string {
  const vals = rows.flatMap((r) => [r.cur ?? 0, r.mod ?? 0]).map(Math.abs)
  if (rows.length === 0 || Math.max(...vals) === 0) {
    return svgWrap(w, h, txt(w / 2, h / 2, 'No monthly data available', { anchor: 'middle', fill: MUTED }))
  }
  const maxV = Math.max(1, ...vals)
  const padL = 30, padR = 4, padT = 10, padB = 16
  const plotH = h - padT - padB
  const baseY = padT + plotH
  const bw = (w - padL - padR) / rows.length
  const barW = Math.min(17.5, bw * 0.3)
  let body = line(padL, baseY, w - padR, baseY, INK, 1)
  body += line(padL, padT, w - padR, padT, RULE_LIGHT, 0.6)
  body += txt(padL - 4, padT + 3, gbpK(maxV), { size: 6.5, fill: MUTED, anchor: 'end' })
  rows.forEach((r, i) => {
    const x0 = padL + i * bw + (bw - barW * 2 - 3) / 2
    if (r.cur != null) {
      const bh = Math.max(2, (Math.abs(r.cur) / maxV) * plotH)
      body += rect(x0, baseY - bh, barW, bh, PALE_GREEN)
    }
    if (r.mod != null) {
      const bh = Math.max(2, (Math.abs(r.mod) / maxV) * plotH)
      body += rect(x0 + barW + 3, baseY - bh, barW, bh, BRAND)
    }
    body += txt(padL + i * bw + bw / 2, h - 4, r.label, { size: 8.5, fill: MUTED, anchor: 'middle' })
  })
  return svgWrap(w, h, body)
}

/**
 * Demand by time of day with the two timetables' capacity over it — the chart
 * that started this whole project: mornings hot, afternoons hauling air.
 * Legend row ABOVE the chart, horizontal tick labels, one dashed style only
 * (seats today); proposed seats are a solid red outline. Design spec §2.5.3.
 */
function chartDemandVsTimetable(d: ExecReportData, w: number, h: number): string {
  const prof = d.demandProfile
  if (prof.length === 0) return svgWrap(w, h, txt(w / 2, h / 2, 'No demand profile available', { anchor: 'middle', fill: MUTED }))
  const padL = 34, padR = 6, padT = 26, padB = 18
  const plotW = w - padL - padR
  const plotH = h - padT - padB
  const capAt = (deps: Departure[], time: string) =>
    deps.filter((x) => x.enabled && x.time === time)
      // Assigned set's measured seats; the coach heuristic only as fallback (D6).
      .reduce((t, x) => t + (x.capacity ?? (x.coaches === 0 ? 48 : Math.max(0, x.coaches) * 64)), 0)
  const maxV = Math.max(
    1,
    ...prof.map((p) => Math.max(p.north, p.south)),
    ...prof.map((p) => Math.max(capAt(d.current.deps, p.time), capAt(d.proposed.deps, p.time)))
  )
  const bw = plotW / prof.length
  const barW = Math.max(2, Math.min(11, bw * 0.24))
  const outW = Math.min(26, bw * 0.8)
  let body = ''
  // legend row above the chart
  let lx = padL
  const legend = (swatch: string, label: string) => {
    body += swatch
    body += txt(lx + 12, 12, label, { size: 7.5, fill: INK })
    lx += 12 + label.length * 3.8 + 14
  }
  legend(rect(lx, 5, 8, 8, BRAND), 'Demand north')
  legend(rect(lx, 5, 8, 8, GOLD), 'Demand south')
  legend(`<rect x="${lx}" y="5" width="8" height="8" fill="none" stroke="${MUTED}" stroke-width="1" stroke-dasharray="2.4 1.8"/>`, 'Seats today')
  legend(`<rect x="${lx}" y="5" width="8" height="8" fill="none" stroke="${NEG}" stroke-width="1.4"/>`, 'Seats proposed')
  // gridlines at max and half, labelled
  for (const f of [1, 0.5]) {
    const y = padT + plotH - plotH * f
    body += line(padL, y, w - padR, y, RULE_LIGHT, 0.6)
    body += txt(padL - 4, y + 3, num0(maxV * f), { size: 6.5, fill: MUTED, anchor: 'end' })
  }
  body += line(padL, padT + plotH, w - padR, padT + plotH, INK, 1)
  prof.forEach((p, i) => {
    const x0 = padL + i * bw
    const cx = x0 + bw / 2
    const pairX = cx - barW - 0.5
    const nH = (p.north / maxV) * plotH
    const sH = (p.south / maxV) * plotH
    body += rect(pairX, padT + plotH - nH, barW, nH, BRAND)
    body += rect(pairX + barW + 1, padT + plotH - sH, barW, sH, GOLD)
    const cc = capAt(d.current.deps, p.time)
    const pc = capAt(d.proposed.deps, p.time)
    if (cc > 0) {
      const y = padT + plotH - (cc / maxV) * plotH
      body += `<rect x="${cx - outW / 2}" y="${y}" width="${outW}" height="${padT + plotH - y}" fill="none" stroke="${MUTED}" stroke-width="0.9" stroke-dasharray="3.2 2.3"/>`
    }
    if (pc > 0) {
      const y = padT + plotH - (pc / maxV) * plotH
      body += `<rect x="${cx - outW / 2}" y="${y}" width="${outW}" height="${padT + plotH - y}" fill="none" stroke="${NEG}" stroke-width="1.2"/>`
    }
    if (prof.length <= 13 || i % 2 === 0) {
      body += txt(cx, h - 4, p.time, { size: 6.5, fill: MUTED, anchor: 'middle' })
    }
  })
  return svgWrap(w, h, body)
}

/** One demand-vs-seats panel for a single side — the "now" and "after"
 *  pictures the Compare tab shows, printed side by side. */
function chartSeatsPanel(d: ExecReportData, side: 'current' | 'proposed', w: number, h: number, title: string): string {
  const prof = d.demandProfile
  if (prof.length === 0) return svgWrap(w, h, txt(w / 2, h / 2, 'No demand profile', { anchor: 'middle', fill: MUTED }))
  const deps = side === 'current' ? d.current.deps : d.proposed.deps
  const padL = 30, padR = 6, padT = 16, padB = 26
  const plotW = w - padL - padR, plotH = h - padT - padB
  const capAt = (time: string) =>
    deps.filter((x) => x.enabled && x.time === time)
      .reduce((t, x) => t + (x.capacity ?? (x.coaches === 0 ? 48 : Math.max(0, x.coaches) * 64)), 0)
  const maxV = Math.max(1, ...prof.map((p) => Math.max(p.north + p.south, capAt(p.time))))
  const bw = plotW / prof.length
  let body = txt(padL, 10, title, { size: 9, weight: 700, fill: BRAND })
  for (const f of [1, 0.5]) {
    const y = padT + plotH - plotH * f
    body += line(padL, y, w - padR, y, RULE_LIGHT, 0.6)
    body += txt(padL - 3, y + 3, num0(maxV * f), { size: 6.5, fill: MUTED, anchor: 'end' })
  }
  body += line(padL, padT + plotH, w - padR, padT + plotH, INK, 1)
  prof.forEach((p, i) => {
    const x0 = padL + i * bw
    const dm = p.north + p.south
    const dH = (dm / maxV) * plotH
    body += rect(x0 + bw * 0.2, padT + plotH - dH, Math.max(2, bw * 0.4), dH, BRAND)
    const cap = capAt(p.time)
    if (cap > 0) {
      const y = padT + plotH - (cap / maxV) * plotH
      body += `<rect x="${x0 + bw * 0.1}" y="${y}" width="${bw * 0.68}" height="${padT + plotH - y}" fill="none" stroke="${side === 'current' ? MUTED : NEG}" stroke-width="1"${side === 'current' ? ' stroke-dasharray="3 2"' : ''}/>`
    }
    if (prof.length <= 9 || i % 2 === 0) {
      body += txt(x0 + bw / 2, padT + plotH + 9, p.time, { size: 6, fill: MUTED, anchor: 'middle' })
    }
  })
  const ly = h - 4
  body += rect(padL, ly - 6, 7, 7, BRAND) + txt(padL + 10, ly, 'Demand (both directions)', { size: 6.5, fill: MUTED })
  body += `<rect x="${padL + 92}" y="${ly - 6}" width="7" height="7" fill="none" stroke="${side === 'current' ? MUTED : NEG}"${side === 'current' ? ' stroke-dasharray="2 1"' : ''}/>` +
    txt(padL + 102, ly, side === 'current' ? 'Seats today' : 'Seats proposed', { size: 6.5, fill: MUTED })
  return svgWrap(w, h, body)
}

/** Load factor before/after — the "empty seats" story in one bar pair. */
function chartLoadFactor(d: ExecReportData, w: number, h: number): string {
  const loadOf = (s: ExecSide) => {
    const cap = s.scenario.perDep.reduce((t, p) => t + p.capacity, 0)
    const use = s.scenario.perDep.reduce((t, p) => t + p.allocated, 0)
    return cap > 0 ? use / cap : 0
  }
  const rows = [
    { label: 'Today', v: loadOf(d.current), fill: PALE_GREEN, ink: MUTED },
    { label: 'Proposed', v: loadOf(d.proposed), fill: BRAND, ink: BRAND },
  ]
  const padL = 58, padT = 16
  const plotW = w - padL - 44
  const bh = 16
  let body = txt(0, 9, 'AVERAGE SEAT OCCUPANCY', { size: 7, weight: 700, fill: MUTED })
  rows.forEach((r, i) => {
    const y = padT + i * (bh + 8)
    body += txt(padL - 6, y + bh * 0.68, r.label, { size: 8, anchor: 'end', fill: MUTED })
    body += rect(padL, y, plotW, bh, RULE_LIGHT)
    body += rect(padL, y, plotW * Math.min(1, r.v), bh, r.fill)
    body += txt(padL + plotW + 6, y + bh * 0.68, `${Math.round(r.v * 100)}%`, { size: 9, weight: 700, fill: r.ink })
  })
  return svgWrap(w, h, body)
}

/** Waterfall from current contribution to proposed — connector rules, value
 *  labels on every step, a zero delta drawn as a tick. Design spec §2.5.4. */
function chartWaterfall(d: ExecReportData, w: number, h: number): string {
  const cur = d.current.periodRevenue - d.current.periodCosts
  const prop = d.proposed.periodRevenue - d.proposed.periodCosts
  const dRev = d.proposed.periodRevenue - d.current.periodRevenue
  const dCost = -(d.proposed.periodCosts - d.current.periodCosts)
  const steps = [
    { label: 'Today', value: cur, kind: 'total' as const },
    { label: 'Revenue', value: dRev, kind: 'delta' as const },
    { label: dCost >= 0 ? 'Costs removed' : 'Costs added', value: dCost, kind: 'delta' as const },
    { label: 'Proposed', value: prop, kind: 'total' as const },
  ]
  const padL = 14, padT = 18, padB = 44
  const plotW = w - padL - 10
  const plotH = h - padT - padB
  const lo = Math.min(0, cur, prop, cur + dRev)
  const hi = Math.max(0, cur, prop, cur + dRev)
  const span = Math.max(1, hi - lo)
  const yOf = (v: number) => padT + plotH - ((v - lo) / span) * plotH
  const bw = plotW / steps.length
  const barW = Math.min(85, bw * 0.66)
  let body = line(padL, yOf(0), w - 10, yOf(0), INK, 1)
  let running = 0
  const edges: { x: number; y: number }[] = []
  steps.forEach((s, i) => {
    const x = padL + i * bw + (bw - barW) / 2
    const cx = x + barW / 2
    let labelY: number
    if (s.kind === 'total') {
      const y0 = yOf(Math.max(0, s.value))
      const y1 = yOf(Math.min(0, s.value))
      const fill = s.value < 0 ? NEG : BRAND
      body += rect(x, y0, barW, Math.max(1.5, y1 - y0), fill)
      labelY = s.value < 0 ? Math.min(y1 + 10, padT + plotH + 10) : y0 - 6
      body += txt(cx, labelY, gbp0(s.value), { size: 10.5, anchor: 'middle', weight: 700, fill })
      running = s.value
      edges.push({ x: x + barW, y: yOf(s.value) })
    } else if (Math.abs(s.value) < 0.5) {
      // a zero delta is a tick with its label, never an invisible bar
      body += rect(x, yOf(running) - 1, barW, 2, MUTED)
      body += txt(cx, yOf(running) - 8, '+£0', { size: 10.5, anchor: 'middle', weight: 700, fill: MUTED })
      edges.push({ x: x + barW, y: yOf(running) })
    } else {
      const from = running
      const to = running + s.value
      const y0 = yOf(Math.max(from, to))
      const y1 = yOf(Math.min(from, to))
      const fill = s.value >= 0 ? POS : NEG
      body += rect(x, y0, barW, Math.max(1.5, y1 - y0), fill)
      body += txt(cx, y0 - 6, signed(s.value), { size: 10.5, anchor: 'middle', weight: 700, fill })
      running = to
      edges.push({ x: x + barW, y: yOf(to) })
    }
    body += txt(cx, h - 8, s.label, { size: 9.5, anchor: 'middle', fill: MUTED })
    if (i > 0) {
      const prev = edges[i - 1]
      body += line(prev.x, prev.y, x, prev.y, RULE, 0.8)
    }
  })
  return svgWrap(w, h, body)
}

// ---------------------------------------------------------------------------
//  Composition — the shared page chrome (Design spec §2.3)
// ---------------------------------------------------------------------------

interface Ctx {
  doc: jsPDF
  autoTable: (doc: jsPDF, opts: Record<string, unknown>) => void
  svg2pdf: (el: Element, doc: jsPDF, opts: Record<string, unknown>) => Promise<unknown>
  host: HTMLElement
  d: ExecReportData
}

const PW = 210, PH = 297, M = 16
const CW = PW - 2 * M // 178mm content width
const FOOT_Y = 283    // nothing may draw below this

/** Autotable cell hooks are typed loosely — the library's own types fight
 *  the per-cell styling this design needs. */
interface ATCell { x: number; y: number; width: number; height: number; text: string[]; styles: Record<string, unknown> }
interface ATHook { section: 'head' | 'body' | 'foot'; row: { index: number }; column: { index: number }; cell: ATCell; pageNumber: number }

function drawHeaderBand(c: Ctx): void {
  const { doc, d } = c
  doc.setFillColor(BRAND)
  doc.rect(0, 0, PW, 12, 'F')
  doc.setFillColor(GOLD)
  doc.rect(0, 12, PW, 0.8, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8.5)
  doc.setTextColor('#ffffff')
  doc.text('North Yorkshire Moors Railway — Season Operating Plan', M, 7.5)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(HEADER_SUB)
  doc.text((c.d.variant === 'A' || c.d.seasonSummary) ? `${d.season} season` : `${d.monthLabel ?? d.season} · ${d.dayClassLabel ?? ''}`, PW - M, 7.5, { align: 'right' })
  doc.setTextColor(INK)
}

/** Kicker at y22, H1 at y30, 12mm gold rule beneath; returns the content y. */
function pageStart(c: Ctx, kicker: string, title: string, h1 = 21): number {
  drawHeaderBand(c)
  const { doc } = c
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(GOLD_TEXT)
  doc.text(kicker.toUpperCase(), M, 22, { charSpace: 0.4 })
  doc.setFontSize(h1)
  doc.setTextColor(BRAND)
  const lines = doc.splitTextToSize(title, CW) as string[]
  doc.text(lines, M, 30)
  const y = 30 + (lines.length - 1) * h1 * 0.42
  doc.setFillColor(GOLD)
  doc.rect(M, y + 3, 12, 0.8, 'F')
  doc.setTextColor(INK)
  return y + 10
}

function newPage(c: Ctx): void {
  c.doc.addPage()
}

/** Footers land in ONE pass at the end, so every page (including pages
 *  autotable adds mid-table) carries "Page n of N". Never "Unsaved scenario"
 *  on a board paper (Design spec §2.3). */
function drawFooters(c: Ctx): void {
  const { doc, d } = c
  const total = doc.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    doc.setDrawColor(RULE)
    doc.setLineWidth(0.2)
    doc.line(M, FOOT_Y, PW - M, FOOT_Y)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(MUTED)
    doc.text(`Commercial in confidence · Prepared by ${d.preparedBy} · data as at ${d.dataAsAt}`, M, 287)
    const right = d.scenarioId != null
      ? `Scenario "${d.scenarioName}" · Page ${i} of ${total}`
      : `Page ${i} of ${total}`
    doc.text(right, PW - M, 287, { align: 'right' })
  }
  doc.setPage(total)
  doc.setTextColor(INK)
}

// ---------------------------------------------------------------------------
//  Text helpers
// ---------------------------------------------------------------------------

function para(
  c: Ctx, text: string, y: number, size = 8,
  o: { width?: number; leading?: number; color?: string; x?: number } = {}
): number {
  const { doc } = c
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(size)
  doc.setTextColor(o.color ?? INK)
  const lines = doc.splitTextToSize(text, o.width ?? CW) as string[]
  doc.text(lines, o.x ?? M, y)
  doc.setTextColor(INK)
  return y + lines.length * size * 0.3528 * (o.leading ?? 1.45) + 2
}

/** The standfirst — 10pt on a 14.5pt leading, measure <= 160mm. */
function standfirst(c: Ctx, text: string, y: number): number {
  return para(c, text, y, 10, { width: 160, leading: 1.45 })
}

/** Mixed bold/normal runs with word wrapping — the mock's <strong> figures. */
function richPara(
  c: Ctx, segs: { t: string; b?: boolean }[], y: number, size = 10,
  o: { width?: number; leading?: number; x?: number } = {}
): number {
  const { doc } = c
  const x0 = o.x ?? M
  const width = o.width ?? 160
  const lineH = size * 0.3528 * (o.leading ?? 1.45)
  doc.setFontSize(size)
  doc.setTextColor(INK)
  let x = x0
  let yy = y
  for (const seg of segs) {
    doc.setFont('helvetica', seg.b ? 'bold' : 'normal')
    for (const word of seg.t.split(/\s+/)) {
      if (!word) continue
      const ww = doc.getTextWidth(`${word} `)
      if (x + ww > x0 + width && x > x0) { x = x0; yy += lineH }
      doc.text(word, x, yy)
      x += ww
    }
  }
  doc.setFont('helvetica', 'normal')
  return yy + lineH * 0.6 + 2.4
}

/** Small-caps section label (H3) — 7pt bold muted, letterspaced. */
function sectionLabel(c: Ctx, text: string, y: number, x = M): number {
  const { doc } = c
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  doc.setTextColor(MUTED)
  doc.text(text.toUpperCase(), x, y, { charSpace: 0.3 })
  doc.setTextColor(INK)
  return y + 4
}

/** Gold "·" bullets — the paper's only bullet glyph. */
function goldBullets(
  c: Ctx, items: string[], y: number,
  o: { x?: number; width?: number; size?: number; leading?: number } = {}
): number {
  const { doc } = c
  const x0 = o.x ?? M
  const width = o.width ?? CW
  const size = o.size ?? 8
  const lineH = size * 0.3528 * (o.leading ?? 1.5)
  let yy = y
  doc.setFontSize(size)
  for (const it of items) {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(GOLD)
    doc.text('·', x0 + 1, yy)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(INK)
    const lines = doc.splitTextToSize(it, width - 5) as string[]
    doc.text(lines, x0 + 5, yy)
    yy += lines.length * lineH + 1.6
  }
  return yy
}

/** Numbered list with gold numerals. */
function numberedList(
  c: Ctx, items: string[], y: number,
  o: { x?: number; width?: number; size?: number; leading?: number } = {}
): number {
  const { doc } = c
  const x0 = o.x ?? M
  const width = o.width ?? CW
  const size = o.size ?? 8
  const lineH = size * 0.3528 * (o.leading ?? 1.5)
  let yy = y
  doc.setFontSize(size)
  items.forEach((it, i) => {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(GOLD_TEXT)
    doc.text(String(i + 1), x0 + 1, yy)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(INK)
    const lines = doc.splitTextToSize(it, width - 6) as string[]
    doc.text(lines, x0 + 6, yy)
    yy += lines.length * lineH + 1.6
  })
  return yy
}

/** A bordered column header ("In this paper", "What actually changes"). */
function colHeader(c: Ctx, title: string, x: number, w: number, y: number): number {
  const { doc } = c
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8.3)
  doc.setTextColor(BRAND)
  doc.text(title, x, y)
  doc.setDrawColor(RULE)
  doc.setLineWidth(0.25)
  doc.line(x, y + 2, x + w, y + 2)
  doc.setTextColor(INK)
  return y + 7
}

async function chart(c: Ctx, svg: string, x: number, y: number, w: number, h: number): Promise<void> {
  // svg2pdf is more reliable against elements attached to the DOM, so the host
  // is a real (off-screen) container rather than a detached fragment.
  c.host.innerHTML = svg
  const el = c.host.firstElementChild
  if (!el) return
  await c.svg2pdf(el, c.doc, { x, y, width: w, height: h })
  c.host.innerHTML = ''
}

// ---------------------------------------------------------------------------
//  THE table theme — one language for every table (Design spec §2.4)
// ---------------------------------------------------------------------------

function lastAutoY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY
}

/** Base theme: brand header band, zebra body, hairline row rules only. */
function baseTable(c: Ctx, opts: Record<string, unknown>): number {
  const { doc, autoTable } = c
  const merged: Record<string, unknown> = {
    theme: 'plain',
    styles: {
      font: 'helvetica', fontSize: 7.5, textColor: INK,
      cellPadding: { top: 1.8, bottom: 1.8, left: 2.8, right: 2.8 },
      lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 },
    },
    headStyles: {
      fillColor: BRAND, textColor: '#ffffff', fontSize: 6.5, fontStyle: 'bold',
      cellPadding: { top: 2.2, bottom: 2.2, left: 2.8, right: 2.8 }, lineWidth: 0,
    },
    alternateRowStyles: { fillColor: PAPER },
    footStyles: { fillColor: CREAM, textColor: INK, fontStyle: 'bold', fontSize: 7.5, lineWidth: 0 },
    margin: { left: M, right: M, top: 20, bottom: PH - FOOT_Y + 2 },
    didDrawPage: (dta: { pageNumber: number }) => {
      // pages autotable adds itself still get the brand band
      if (dta.pageNumber > 1) drawHeaderBand(c)
    },
    ...opts,
  }
  // deep-merge styles the caller partially overrides
  for (const k of ['styles', 'headStyles', 'margin'] as const) {
    if (opts[k]) merged[k] = { ...(baseStylesFor(k, c)), ...(opts[k] as Record<string, unknown>) }
  }
  autoTable(doc, merged)
  return lastAutoY(doc)
}
function baseStylesFor(k: 'styles' | 'headStyles' | 'margin', c: Ctx): Record<string, unknown> {
  void c
  if (k === 'styles') {
    return {
      font: 'helvetica', fontSize: 7.5, textColor: INK,
      cellPadding: { top: 1.8, bottom: 1.8, left: 2.8, right: 2.8 },
      lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 },
    }
  }
  if (k === 'headStyles') {
    return {
      fillColor: BRAND, textColor: '#ffffff', fontSize: 6.5, fontStyle: 'bold',
      cellPadding: { top: 2.2, bottom: 2.2, left: 2.8, right: 2.8 }, lineWidth: 0,
    }
  }
  return { left: M, right: M, top: 20, bottom: PH - FOOT_Y + 2 }
}

/** Compose several didParseCell / didDrawCell hooks into one. */
function hooks(...fns: ((dta: ATHook) => void)[]): (dta: ATHook) => void {
  return (dta) => { for (const f of fns) f(dta) }
}

/** Totals row: cream fill, bold, and a 0.6mm brand rule drawn above it. */
function totalsFill(lastIdx: number): (dta: ATHook) => void {
  return (dta) => {
    if (dta.section === 'body' && dta.row.index === lastIdx) {
      dta.cell.styles.fontStyle = 'bold'
      dta.cell.styles.fillColor = CREAM
    }
  }
}
function totalsRule(c: Ctx, lastIdx: number): (dta: ATHook) => void {
  return (dta) => {
    if (dta.section === 'body' && dta.row.index === lastIdx && dta.column.index === 0) {
      c.doc.setDrawColor(BRAND)
      c.doc.setLineWidth(0.6)
      c.doc.line(dta.cell.x, dta.cell.y, dta.cell.x + CW, dta.cell.y)
    }
  }
}

/** Money cells: sign decides the ink; always bold. "—" stays muted. */
function signCols(cols: number[]): (dta: ATHook) => void {
  return (dta) => {
    if (dta.section !== 'body' || !cols.includes(dta.column.index)) return
    const t = dta.cell.text[0] ?? ''
    if (t === '+£0' || t === '-£0') { dta.cell.styles.textColor = MUTED }
    else if (t.startsWith('+')) { dta.cell.styles.textColor = POS; dta.cell.styles.fontStyle = 'bold' }
    else if (t.startsWith('-')) { dta.cell.styles.textColor = NEG; dta.cell.styles.fontStyle = 'bold' }
    else if (t === '—' || t === '–') { dta.cell.styles.textColor = MUTED }
  }
}

/** Secondary columns (workings, basis) read muted. */
function mutedCols(cols: number[]): (dta: ATHook) => void {
  return (dta) => {
    if (dta.section === 'body' && cols.includes(dta.column.index)) {
      dta.cell.styles.textColor = MUTED
    }
  }
}

/** A rounded chip (PASS / MEASURED / ASSUMED …) drawn straight onto the page. */
function drawChip(c: Ctx, x: number, yMid: number, label: string, bg: string, ink: string): number {
  const { doc } = c
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(5.5)
  const w = doc.getTextWidth(label) + 4
  doc.setFillColor(bg)
  doc.roundedRect(x, yMid - 2.1, w, 4.2, 1, 1, 'F')
  doc.setTextColor(ink)
  doc.text(label, x + 2, yMid + 1.4)
  doc.setTextColor(INK)
  return w
}

/** Mix a hex colour toward white — pre-mixed tints (no alpha in jsPDF). */
function tint(hex: string, w: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return '#FFFFFF'
  const n = parseInt(m[1], 16)
  const ch = (v: number) => Math.round(v + (255 - v) * w).toString(16).padStart(2, '0')
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`
}
/** Mix a hex colour toward black — the poster sub-bands' dark text. */
function shade(hex: string, w: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return INK
  const n = parseInt(m[1], 16)
  const ch = (v: number) => Math.round(v * (1 - w)).toString(16).padStart(2, '0')
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`
}

/** The railway's calendar colours, for the cuts table's day-type dots. */
const CAL_DOT: Record<string, string> = {
  yellow: '#e0b400', orange: '#e07b39', pink: '#d55a8f', blue: '#2f6f8f',
  red: '#c94f38', purple: '#8757a3', teal: '#2f8f83', green: '#2e7d32',
  lilac: '#8757a3', brown: '#8a6a1f', grey: '#6b6b6b', gray: '#6b6b6b',
}
const calDot = (colour: string): string => CAL_DOT[(colour || '').toLowerCase()] ?? MUTED

// ---------------------------------------------------------------------------
//  The public season poster (Design spec §2.8) — the reference layout is
//  thebigplan's published poster (Chris's Spring/Summer upload): LEFT column
//  62mm = the teal "services calendar" panel, 2-up month grids and the plan
//  legend; RIGHT column 108mm = the colour-banded timetables. Continuation
//  pages flow the remaining pattern blocks in two dense columns. Patterns
//  arrive already merged by identical PUBLIC times, largest first.
// ---------------------------------------------------------------------------

type PosterRow = [string, string, (w: PosterWork) => string]
const posterTokMin = (t: string | null): number | null => {
  if (!t) return null
  const p4 = String(t).replace(/[^0-9]/g, '')
  if (p4.length < 3) return null
  const s4 = p4.padStart(4, '0')
  return Number(s4.slice(0, 2)) * 60 + Number(s4.slice(2))
}
const posterFmt = (m: number | null): string => (m == null ? '-' : `${String(Math.floor(m / 60)).padStart(2, '0')}${String(m % 60).padStart(2, '0')}`)
function posterRowsFor(works: PosterWork[], dir: 'N' | 'S'): { rows: PosterRow[]; dest: string } {
  const has = (i: number) => works.some((w) => w.t[i] != null)
  if (dir === 'N') {
    const rows: PosterRow[] = [
      ['Pickering', 'Dep', (w) => posterFmt(posterTokMin(w.t[0]))],
      ['Levisham', 'Dep', (w) => posterFmt(posterTokMin(w.t[2] ?? w.t[1]))],
      ['Newtondale', 'Req', (w) => { const b = posterTokMin(w.t[2] ?? w.t[1]); return b == null ? '-' : posterFmt(b + 5) }],
    ]
    if (!has(6) && !has(7) && !has(8)) { rows.push(['Goathland', 'Arr', (w) => posterFmt(posterTokMin(w.t[4] ?? w.t[5]))]); return { rows, dest: 'Goathland' } }
    rows.push(['Goathland', 'Dep', (w) => posterFmt(posterTokMin(w.t[5] ?? w.t[4]))])
    if (!has(8)) { rows.push(['Grosmont', 'Arr', (w) => posterFmt(posterTokMin(w.t[6] ?? w.t[7]))]); return { rows, dest: 'Grosmont' } }
    rows.push(
      ['Grosmont', 'Arr', (w) => posterFmt(posterTokMin(w.t[6]))],
      ['Grosmont', 'Dep', (w) => posterFmt(posterTokMin(w.t[7]))],
      ['Whitby', 'Arr', (w) => posterFmt(posterTokMin(w.t[8]))],
    )
    return { rows, dest: 'Whitby' }
  }
  const rows: PosterRow[] = []
  if (has(0)) {
    rows.push(
      ['Whitby', 'Dep', (w) => posterFmt(posterTokMin(w.t[0]))],
      ['Grosmont', 'Arr', (w) => posterFmt(posterTokMin(w.t[1]))],
      ['Grosmont', 'Dep', (w) => posterFmt(posterTokMin(w.t[2]))],
    )
  } else if (has(1) || has(2)) {
    rows.push(['Grosmont', 'Dep', (w) => posterFmt(posterTokMin(w.t[2] ?? w.t[1]))])
  }
  rows.push(
    ['Goathland', 'Dep', (w) => posterFmt(posterTokMin(w.t[4] ?? w.t[3]))],
    ['Newtondale', 'Req', (w) => { const b = posterTokMin(w.t[4] ?? w.t[3]); return b == null ? '-' : posterFmt(b + 10) }],
    ['Levisham', 'Dep', (w) => posterFmt(posterTokMin(w.t[7] ?? w.t[6]))],
    ['Pickering', 'Arr', (w) => posterFmt(posterTokMin(w.t[8]))],
  )
  return { rows, dest: 'Pickering' }
}

/** Block metrics: 6mm colour band + per direction (4.5mm sub-band + 3.6mm rows). */
function posterBlockHeight(pat: PosterPattern): number {
  let h = 6
  for (const [dir, works] of [['N', pat.N], ['S', pat.S]] as const) {
    if (works.length === 0) continue
    h += 4.5 + posterRowsFor(works, dir).rows.length * 3.6
  }
  return h + 3
}

/** One banded pattern block, drawn by hand at (x, y0) in width w. */
function drawPosterBlock(c: Ctx, pat: PosterPattern, x: number, w: number, y0: number): number {
  const { doc } = c
  const dark = shade(pat.colour, 0.45)
  const bandTint = tint(pat.colour, 0.88) // pre-mixed 12% tint — no alpha
  let y = y0
  doc.setFillColor(pat.colour)
  doc.rect(x, y, w, 6, 'F')
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor('#ffffff')
  doc.text(`${pat.name.toUpperCase()} TIMETABLE`, x + 2.5, y + 4.1)
  doc.setFontSize(6.5)
  doc.text(`${pat.daysUsed} day${pat.daysUsed === 1 ? '' : 's'} · ${pat.monthsUsed.join(', ')}`, x + w - 2.5, y + 4.1, { align: 'right' })
  y += 6
  const labW = Math.min(30, w * 0.3)
  for (const [dir, works] of [['N', pat.N], ['S', pat.S]] as const) {
    if (works.length === 0) continue
    const { rows, dest } = posterRowsFor(works, dir)
    const colW = (w - labW) / works.length
    doc.setFillColor(bandTint)
    doc.rect(x, y, w, 4.5, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(dark)
    doc.text(`To ${dest}`, x + 2.5, y + 3.1)
    doc.setFontSize(6)
    works.forEach((wk, i) => doc.text(wk.flag, x + labW + i * colW + colW / 2, y + 3.1, { align: 'center' }))
    y += 4.5
    rows.forEach((r, ri) => {
      if (ri % 2 === 1) { doc.setFillColor(PAPER); doc.rect(x, y, w, 3.6, 'F') }
      doc.setDrawColor(RULE_LIGHT); doc.setLineWidth(0.2)
      doc.line(x, y, x + w, y)
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(INK)
      doc.text(r[0], x + 2.5, y + 2.65)
      const stW = doc.getTextWidth(r[0])
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(GREY_KIND)
      doc.text(r[1], x + 2.5 + stW + 1.4, y + 2.65)
      doc.setFontSize(6.5)
      works.forEach((wk, i) => {
        const v = r[2](wk)
        if (v === '-') { doc.setTextColor(GREY_KIND); doc.setFont('helvetica', 'normal') }
        else { doc.setTextColor(INK); doc.setFont('helvetica', 'normal') }
        doc.text(v === '-' ? '–' : v, x + labW + i * colW + colW / 2, y + 2.65, { align: 'center' })
      })
      y += 3.6
    })
  }
  // the block's card border, over the finished content
  doc.setDrawColor(RULE)
  doc.setLineWidth(0.25)
  doc.roundedRect(x, y0, w, y - y0, 1.5, 1.5, 'S')
  doc.setTextColor(INK)
  return y - y0 + 3
}

const POSTER_KEY =
  'X Scheduled to be hauled by a steam locomotive; if operational needs require, part or all of the service may be ' +
  'substituted by a diesel locomotive. D Scheduled to be hauled by heritage diesel. Auto The Electric Autocar railcar ' +
  '(limited seating). Req Stops on request. – No service. Times are public times derived from the working timetable: ' +
  'departures round up to the next 5 minutes, arrivals to the nearest 5. Days without a coloured box run no standard ' +
  'service; days outlined in gold keep their special published programme (galas, diners).'

function posterPages(c: Ctx, poster: SeasonPoster): void {
  const { doc } = c
  const y = pageStart(c, 'The public season timetable', `The proposed ${poster.seasonLabel} season, as the public would see it`, 18)
  const leftX = M, leftW = 62
  const rightX = M + leftW + 6, rightW = PW - M - rightX
  let ly = y
  // the teal services-calendar panel
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11)
  const tealTitle = doc.splitTextToSize(`${poster.seasonLabel} SERVICES CALENDAR`, leftW - 7) as string[]
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7)
  const tealBody = doc.splitTextToSize('Our colour-coded calendar with the detailed timetables adjacent. Choose a visiting date and refer to the corresponding timetable.', leftW - 7) as string[]
  const tealH = 4 + tealTitle.length * 4.6 + 2 + tealBody.length * 3.1 + 3.5
  doc.setFillColor(POSTER_TEAL)
  doc.roundedRect(leftX, ly, leftW, tealH, 2, 2, 'F')
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(BRAND)
  doc.text(tealTitle, leftX + 3.5, ly + 7)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor('#1d3d31')
  doc.text(tealBody, leftX + 3.5, ly + 7 + (tealTitle.length - 1) * 4.6 + 5)
  ly += tealH + 3
  // 2-up month grids: day cells >= 3.2mm, white 5.5pt bold day numbers
  const mw2 = (leftW - 4) / 2
  const cw = mw2 / 7, chH = 3.5
  let mcol = 0, mrowY = ly, mrowH = 0
  for (const mth of poster.months) {
    const x0 = leftX + mcol * (mw2 + 4)
    let my = mrowY
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.8); doc.setTextColor(INK)
    doc.text(mth.label.toUpperCase(), x0, my + 2.4)
    my += 3.6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(MUTED)
    const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
    WD.forEach((wd, i) => doc.text(wd, x0 + i * cw + cw / 2, my + 1.8, { align: 'center' }))
    my += 2.8
    const weeks = Math.ceil(mth.cells.length / 7)
    for (let wk = 0; wk < weeks; wk++) {
      for (let dcol = 0; dcol < 7; dcol++) {
        const cell = mth.cells[wk * 7 + dcol]
        if (!cell) continue
        const cx = x0 + dcol * cw
        if (cell.colour) {
          doc.setFillColor(cell.colour)
          doc.roundedRect(cx + 0.2, my + 0.15, cw - 0.4, chH - 0.3, 0.5, 0.5, 'F')
          doc.setFont('helvetica', 'bold'); doc.setFontSize(5.5); doc.setTextColor('#ffffff')
          doc.text(String(cell.day), cx + cw / 2, my + chH / 2 + 0.75, { align: 'center' })
        } else if (cell.special) {
          doc.setDrawColor(GOLD); doc.setLineWidth(0.3)
          doc.roundedRect(cx + 0.2, my + 0.15, cw - 0.4, chH - 0.3, 0.5, 0.5, 'S')
          doc.setFont('helvetica', 'bold'); doc.setFontSize(5.5); doc.setTextColor(GOLD_TEXT)
          doc.text(String(cell.day), cx + cw / 2, my + chH / 2 + 0.75, { align: 'center' })
        } else {
          doc.setFillColor(RULE_LIGHT)
          doc.roundedRect(cx + 0.2, my + 0.15, cw - 0.4, chH - 0.3, 0.5, 0.5, 'F')
          doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(GREY_KIND)
          doc.text(String(cell.day), cx + cw / 2, my + chH / 2 + 0.75, { align: 'center' })
        }
      }
      my += chH
    }
    mrowH = Math.max(mrowH, my - mrowY)
    mcol += 1
    if (mcol === 2) { mcol = 0; mrowY += mrowH + 3; mrowH = 0 }
  }
  ly = mrowY + (mcol > 0 ? mrowH + 3 : 0) + 2.5
  // plan legend, two columns
  doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(MUTED)
  doc.text('PROPOSED STANDARD SERVICES', leftX, ly, { charSpace: 0.3 }); ly += 3.4
  const legW = (leftW - 3) / 2
  poster.patterns.forEach((p, i) => {
    const lx = leftX + (i % 2) * (legW + 3)
    if (i % 2 === 0 && i > 0) ly += 3.4
    doc.setFillColor(p.colour)
    doc.roundedRect(lx, ly - 2, 2.2, 2.2, 0.4, 0.4, 'F')
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(INK)
    doc.text(`${p.name} · ${p.workingsPerDay}/day · ${p.daysUsed} days`, lx + 3.4, ly)
  })
  ly += 5
  if (poster.specials.length > 0) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(MUTED)
    const spTxt = `SPECIAL SERVICES (unchanged): ${poster.specials.map((sp) => `${sp.label} · ${sp.days} day${sp.days === 1 ? '' : 's'}`).join(' · ')}`
    doc.text(doc.splitTextToSize(spTxt, leftW) as string[], leftX, ly)
  }
  // right column: the colour-banded timetable blocks
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(BRAND)
  doc.text(`${poster.seasonLabel} TIMETABLES`, rightX, y + 2)
  let ry = y + 6
  const keyTop = 268
  const queue = [...poster.patterns]
  while (queue.length > 0 && ry + posterBlockHeight(queue[0]) <= keyTop - 2) {
    const pat = queue.shift()!
    ry += drawPosterBlock(c, pat, rightX, rightW, ry)
  }
  doc.setDrawColor(RULE); doc.setLineWidth(0.2)
  doc.line(M, keyTop, PW - M, keyTop)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(MUTED)
  const keyText = queue.length > 0
    ? `${POSTER_KEY} The full ${poster.patterns.length}-plan set continues on the following pages.`
    : POSTER_KEY
  doc.text(doc.splitTextToSize(keyText, CW) as string[], M, keyTop + 3)
  doc.setTextColor(INK)
  newPage(c)
  if (queue.length > 0) {
    let yc = pageStart(c, 'The public season timetable', `${poster.seasonLabel} timetables (continued)`, 18)
    const colWidth = (CW - 6) / 2
    const colX = [M, M + colWidth + 6]
    let col = 0
    let cy = yc
    while (queue.length > 0) {
      const pat = queue[0]
      const h = posterBlockHeight(pat)
      if (cy + h > FOOT_Y - 4) {
        if (col === 0) { col = 1; cy = yc; continue }
        newPage(c)
        yc = pageStart(c, 'The public season timetable', `${poster.seasonLabel} timetables (continued)`, 18)
        col = 0; cy = yc
        continue
      }
      queue.shift()
      cy += drawPosterBlock(c, pat, colX[col], colWidth, cy)
    }
    newPage(c)
  }
}

/** Report A's "proposed season" (Chris, 2026-08-27): the BUILD-TAB view, not the
 *  public timetable — every month's calendar with each operating day coloured by
 *  its pattern and showing that pattern's workings/day, plus the pattern legend.
 *  All months, reformatted to a couple of pages. Same graphics as on screen. */
export function buildSeasonPosterPages(c: Ctx, poster: SeasonPoster): void {
  const { doc } = c
  let y = pageStart(c, 'The proposed season', `The proposed ${poster.seasonLabel} season — every day, its timetable`, 18)
  y = para(c, 'The season exactly as designed on the build screen: every operating day is coloured by the timetable pattern it runs, and shows that pattern\'s number of workings that day. The pattern key is below; the full public timetables are the working timetable in the appendix.', y, 7.5)
  // Pattern legend chips (name · workings/day · days · months) — wrapping rows.
  const wpdByColour = new Map(poster.patterns.map((p) => [p.colour, p.workingsPerDay]))
  let lx = M, ly = y + 2
  for (const p of poster.patterns) {
    const label = `${p.name} · ${p.workingsPerDay}/day · ${p.daysUsed} day${p.daysUsed === 1 ? '' : 's'}${p.monthsUsed.length ? ` · ${p.monthsUsed.join(', ')}` : ''}`
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.4)
    const w = doc.getTextWidth(label) + 9
    if (lx + w > PW - M) { lx = M; ly += 6 }
    doc.setDrawColor(RULE); doc.setLineWidth(0.3); doc.roundedRect(lx, ly - 3.2, w, 5, 2.5, 2.5, 'S')
    doc.setFillColor(p.colour); doc.circle(lx + 3, ly - 0.7, 1.4, 'F')
    doc.setTextColor(INK); doc.text(label, lx + 5.6, ly)
    lx += w + 3
  }
  y = ly + 7
  // Month calendars, 3-up, each running day coloured with its workings/day.
  const perRow = 3, gap = 6
  const mw = (CW - gap * (perRow - 1)) / perRow
  const cw = mw / 7, cellH = 8
  const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  let col = 0, rowY = y, rowH = 0
  for (const mth of poster.months) {
    const monthH = 4.5 + 3 + Math.ceil(mth.cells.length / 7) * cellH + 4
    if (col === 0 && rowY + monthH > FOOT_Y) { newPage(c); rowY = pageStart(c, 'The proposed season', `The proposed ${poster.seasonLabel} season (continued)`, 18); rowH = 0 }
    const x0 = M + col * (mw + gap)
    let my = rowY
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(BRAND); doc.text(mth.label, x0, my + 2.6); my += 4.6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(MUTED)
    WD.forEach((wd, i) => doc.text(wd, x0 + i * cw + cw / 2, my + 1.8, { align: 'center' }))
    my += 3
    const weeks = Math.ceil(mth.cells.length / 7)
    for (let wk = 0; wk < weeks; wk++) {
      for (let dc = 0; dc < 7; dc++) {
        const cell = mth.cells[wk * 7 + dc]
        if (!cell) continue
        const cx = x0 + dc * cw
        doc.setDrawColor(RULE_LIGHT); doc.setLineWidth(0.15); doc.rect(cx, my, cw, cellH, 'S')
        if (cell.colour) {
          doc.setFillColor(cell.colour); doc.rect(cx, my, cw, cellH, 'F')
          doc.setFont('helvetica', 'normal'); doc.setFontSize(4.6); doc.setTextColor('#ffffff'); doc.text(String(cell.day), cx + 0.8, my + 2.5)
          const wpd = wpdByColour.get(cell.colour)
          if (wpd != null) { doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor('#ffffff'); doc.text(String(wpd), cx + cw / 2, my + cellH - 1.7, { align: 'center' }) }
        } else if (cell.special) {
          doc.setDrawColor(GOLD); doc.setLineWidth(0.3); doc.rect(cx + 0.15, my + 0.15, cw - 0.3, cellH - 0.3, 'S')
          doc.setFont('helvetica', 'normal'); doc.setFontSize(4.6); doc.setTextColor(GOLD_TEXT); doc.text(String(cell.day), cx + 0.8, my + 2.5)
        } else {
          doc.setFont('helvetica', 'normal'); doc.setFontSize(4.6); doc.setTextColor(GREY_KIND); doc.text(String(cell.day), cx + 0.8, my + 2.5)
        }
      }
      my += cellH
    }
    rowH = Math.max(rowH, my - rowY)
    col += 1
    if (col === perRow) { col = 0; rowY += rowH + 6; rowH = 0 }
  }
  doc.setTextColor(INK)
  newPage(c)
}

// ---------------------------------------------------------------------------
//  What we cut, in detail — month-band subheaders, calendar-colour dots,
//  "withdrawn" in red, savings green (Design spec p13).
// ---------------------------------------------------------------------------

function cutsSection(c: Ctx, cuts: CutGroupRow[]): void {
  const { doc } = c
  let y = pageStart(c, 'The changes in detail', 'What we cut, why, and what it saves')
  y = standfirst(c,
    'The optimiser holds fares and steam haulage fixed and asks one question of every working: does it pay its own marginal ' +
    'cost, and are its passengers carried if it goes? A working is only withdrawn when the remaining departures absorb its ' +
    'demand with nobody left unreturned.', y)
  y += 1
  type CutCell = string | { content: string; colSpan?: number; styles?: Record<string, unknown> }
  const body: CutCell[][] = []
  const dotByRow = new Map<number, string>()
  const monthRows = new Set<number>()
  let lastMonth = ''
  for (const r of cuts) {
    if (r.month !== lastMonth) {
      monthRows.add(body.length)
      body.push([{
        content: r.month.toUpperCase(),
        colSpan: 8,
        styles: { fillColor: CREAM, textColor: BRAND, fontStyle: 'bold', fontSize: 6.5, cellPadding: { top: 1.6, bottom: 1.6, left: 2.8, right: 2.8 } },
      }])
      lastMonth = r.month
    }
    dotByRow.set(body.length, calDot(r.colour))
    const saving = -(r.curCostD - r.propCostD)
    body.push([
      `${r.colour} · ${r.dayClass}`,
      String(r.days),
      r.propSvc === 0 ? 'withdrawn' : r.planName,
      `${r.curSvc} › ${r.propSvc}`,
      `${r.curSets} › ${r.propSets}`,
      `${gbp0(r.curCostD)} › ${gbp0(r.propCostD)}`,
      signed(saving),
      signed(saving * r.days),
    ])
  }
  y = baseTable(c, {
    startY: y,
    head: [[
      { content: 'DAY-TYPE', styles: { halign: 'left' } },
      { content: 'DAYS', styles: { halign: 'right' } },
      { content: 'BECOMES', styles: { halign: 'left' } },
      { content: 'WORKINGS', styles: { halign: 'center' } },
      { content: 'SETS', styles: { halign: 'center' } },
      { content: 'COST/DAY', styles: { halign: 'right' } },
      { content: 'CHANGE/DAY', styles: { halign: 'right' } },
      { content: 'OVER THE DAYS', styles: { halign: 'right' } },
    ]],
    body,
    styles: { fontSize: 7.2, cellPadding: { top: 1.6, bottom: 1.6, left: 2.8, right: 2.8 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
    columnStyles: {
      0: { cellWidth: 34 }, 1: { halign: 'right', cellWidth: 12 }, 2: { cellWidth: 24, fontStyle: 'bold' },
      3: { halign: 'center', cellWidth: 20 }, 4: { halign: 'center', cellWidth: 14 },
      5: { halign: 'right' }, 6: { halign: 'right', cellWidth: 22 }, 7: { halign: 'right', cellWidth: 26 },
    },
    didParseCell: hooks(
      signCols([6, 7]),
      mutedCols([3, 4, 5]),
      (dta) => {
        if (dta.section !== 'body' || monthRows.has(dta.row.index)) return
        if (dta.column.index === 0) {
          dta.cell.styles.fontStyle = 'bold'
          dta.cell.styles.cellPadding = { top: 1.6, bottom: 1.6, left: 6.6, right: 2.8 }
        }
        if (dta.column.index === 2 && dta.cell.text[0] === 'withdrawn') {
          dta.cell.styles.textColor = NEG
        }
      },
    ),
    didDrawCell: (dta: ATHook) => {
      if (dta.section === 'body' && dta.column.index === 0 && dotByRow.has(dta.row.index)) {
        doc.setFillColor(dotByRow.get(dta.row.index)!)
        doc.circle(dta.cell.x + 4, dta.cell.y + dta.cell.height / 2, 1.1, 'F')
      }
    },
  }) + 5
  y = para(c,
    'Cost savings are shown green; new cost (added services) red. Every day-type the proposal changes is listed; the roll-up ' +
    'below counts the individual workings.', y, 7, { color: MUTED })
  // One quarter-page rollup, not pages of one-liners (Chris, 2026-08-21).
  const wDaily = cuts.reduce((t, r) => t + r.withdrawn.length, 0)
  const wSeason = cuts.reduce((t, r) => t + r.withdrawn.length * r.days, 0)
  const rDaily = cuts.reduce((t, r) => t + r.retimed, 0)
  const rSeason = cuts.reduce((t, r) => t + r.retimed * r.days, 0)
  const kDaily = cuts.reduce((t, r) => t + r.kept, 0)
  const kSeason = cuts.reduce((t, r) => t + r.kept * r.days, 0)
  if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, 'The changes in detail', 'What we cut (continued)') }
  y = sectionLabel(c, 'The changes, rolled up', y + 2)
  y = baseTable(c, {
    startY: y,
    head: [[
      { content: 'CHANGE', styles: { halign: 'left' } },
      { content: 'DAILY WORKINGS', styles: { halign: 'right' } },
      { content: 'SEASON WORKINGS', styles: { halign: 'right' } },
      { content: 'WHY', styles: { halign: 'left' } },
    ]],
    body: [
      ['Withdrawn', String(wDaily), num0(wSeason), 'Marginal — they did not cover their own running cost. Their passengers are carried on the remaining departures, with nobody left unreturned.'],
      ['Retimed', String(rDaily), num0(rSeason), 'The same journey kept, moved to where the demand actually is (within ±20 minutes).'],
      ['Kept as today', String(kDaily), num0(kSeason), 'These workings earn their keep and run unchanged.'],
    ],
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 28 }, 1: { halign: 'right', cellWidth: 26 }, 2: { halign: 'right', cellWidth: 30 } },
  }) + 5
  para(c, 'Operational complexity falls with the cuts: fewer engine diagrams means fewer footplate crews to roster, fewer light-ups and disposals, fewer sets to marshal, and a simpler day for signalling. Special-event days are untouched.', y, 7.5, { color: MUTED })
  newPage(c)
}

// ---------------------------------------------------------------------------
//  Executive summary — the four uplift tiles (2x2, tile 1 filled brand), the
//  written case, the change bullets in two columns (Design spec §2.7).
// ---------------------------------------------------------------------------

function execSummaryPage(c: Ctx, d: ExecReportData): void {
  const { doc } = c
  let y = pageStart(c, 'The decision', 'Executive summary')
  const s = d.seasonSummary
  if (s) {
    const tiles = [
      { label: 'FEWER LOCOMOTIVE WORKINGS', value: `-${num0(s.turnsCur - s.turnsProp)}`,
        sub: `${num0(s.turnsCur)} down to ${num0(s.turnsProp)} hauled workings a season (-${Math.round(((s.turnsCur - s.turnsProp) / Math.max(1, s.turnsCur)) * 100)}%)`,
        fill: BRAND, border: BRAND, kicker: GOLD, ink: '#ffffff', subInk: HEADER_SUB },
      { label: 'SEASON COST REDUCTION', value: gbpK(s.costCur - s.costProp),
        sub: `${gbpK(s.costCur)} down to ${gbpK(s.costProp)} across ${s.days} running days`,
        fill: '#ffffff', border: RULE, kicker: GOLD_TEXT, ink: BRAND, subInk: MUTED },
      { label: 'SEASON PROFIT UPLIFT', value: `+${gbpK(s.profitProp - s.profitCur)}`,
        sub: `${gbpK(s.profitCur)} up to ${gbpK(s.profitProp)}`,
        fill: '#ffffff', border: RULE, kicker: GOLD_TEXT, ink: POS, subInk: MUTED },
      { label: 'PASSENGERS STILL CARRIED', value: `${Math.round((s.carriedProp / Math.max(1, s.carriedCur)) * 100)}%`,
        sub: `${num0(s.carriedProp)} vs ${num0(s.carriedCur)} today — the same footfall, fewer trains`,
        fill: '#ffffff', border: RULE, kicker: GOLD_TEXT, ink: BRAND, subInk: MUTED },
    ]
    const tw = (CW - 6) / 2
    tiles.forEach((tile, i) => {
      const x = M + (i % 2) * (tw + 6)
      const ty = y + Math.floor(i / 2) * 40
      doc.setFillColor(tile.fill)
      doc.setDrawColor(tile.border)
      doc.setLineWidth(0.25)
      doc.roundedRect(x, ty, tw, 34, 2, 2, 'FD')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(tile.kicker)
      doc.text(tile.label, x + 6, ty + 7, { charSpace: 0.35 })
      doc.setFontSize(28); doc.setTextColor(tile.ink)
      doc.text(tile.value, x + 6, ty + 19)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(tile.subInk)
      doc.text(doc.splitTextToSize(tile.sub, tw - 12) as string[], x + 6, ty + 25)
    })
    y += 40 * 2 + 2
    doc.setTextColor(INK)
    if (s.monthsCovered < s.monthsTotal) {
      y = para(c, `Figures cover the ${s.monthsCovered} designed months of ${s.monthsTotal} — nothing is assumed for the rest.`, y, 7, { color: MUTED })
    }
  }
  // The season, month by month — today against the proposal. Chris wants this
  // table ON the summary page (2026-08-26); the per-category cost detail is the
  // "season by month & costs" section.
  if (d.yearRows && d.yearRows.length > 0) {
    if (y > FOOT_Y - 70) { newPage(c); y = pageStart(c, 'The decision', 'Executive summary (continued)') }
    y = sectionLabel(c, 'The season, month by month — today against the proposal', y + 2)
    const yr = d.yearRows
    const rowsB = yr.map((r) => {
      const fc = monthFigures(r.cur, r.runningDays), fm = monthFigures(r.mod, r.runningDays)
      return [r.monthLabel, r.runningDays != null ? String(r.runningDays) : '—',
        fc ? gbp0(fc.revenue) : '—', fc ? signed(fc.profit) : '—',
        fm ? gbp0(fm.revenue) : '—', fm ? signed(fm.profit) : '—',
        (fc && fm) ? signed(fm.profit - fc.profit) : '—']
    })
    const tot = yearTotals(yr)
    rowsB.push(['SEASON', String(tot.runningDays),
      tot.cur ? gbp0(tot.cur.revenue) : '—', tot.cur ? signed(tot.cur.profit) : '—',
      tot.mod ? gbp0(tot.mod.revenue) : '—', tot.mod ? signed(tot.mod.profit) : '—',
      (tot.cur && tot.mod) ? signed(tot.mod.profit - tot.cur.profit) : '—'])
    y = baseTable(c, {
      startY: y,
      head: [[
        { content: 'MONTH', styles: { halign: 'left' } }, { content: 'DAYS', styles: { halign: 'right' } },
        { content: 'TODAY REVENUE', styles: { halign: 'right' } }, { content: 'TODAY PROFIT', styles: { halign: 'right' } },
        { content: 'MODELLED REVENUE', styles: { halign: 'right' } }, { content: 'MODELLED PROFIT', styles: { halign: 'right' } },
        { content: 'CHANGE', styles: { halign: 'right' } },
      ]],
      body: rowsB,
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
      didParseCell: hooks(signCols([3, 5, 6]), totalsFill(rowsB.length - 1)),
      didDrawCell: totalsRule(c, rowsB.length - 1),
    }) + 4
  }
  // ★ NO free-form narrative in the board paper (Chris, 2026-09-02: "I can't
  // trust the app to generate free-form conclusions"). The exec summary is
  // computed figures only — the auto-written "case for the change" prose (and
  // the planner's on-screen notes) never print here.
  // "What actually changes" and "Supporting findings" are dropped from Report A's
  // executive summary (Chris, 2026-08-27): the former reads as waffle here and the
  // latter is fed by the live-slice advisor, which scores a designed season at zero
  // trains — printing nonsense ("services go from 12 to 0", "+£0 per week"). Both
  // stay in Report B (the original scenario paper), where the slice is real.
  if (d.variant !== 'A' && d.whatsChanged.length > 0) {
    if (y > FOOT_Y - 55) { newPage(c); y = pageStart(c, 'The decision', 'Executive summary (continued)') }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(BRAND)
    doc.text('What actually changes', M, y + 2); y += 8
    const colW = (CW - 10) / 2
    const half = Math.ceil(d.whatsChanged.length / 2)
    const y1 = goldBullets(c, d.whatsChanged.slice(0, half), y, { x: M, width: colW, size: 7.5 })
    const y2 = goldBullets(c, d.whatsChanged.slice(half), y, { x: M + colW + 10, width: colW, size: 7.5 })
    y = Math.max(y1, y2) + 2
  }
  if (d.variant !== 'A' && d.findings.length > 0) {
    if (y > FOOT_Y - 45) { newPage(c); y = pageStart(c, 'The decision', 'Executive summary (continued)') }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(BRAND)
    doc.text('Supporting findings', M, y + 2); y += 8
    goldBullets(c, d.findings, y, { size: 7.5 })
  }
  newPage(c)
}

// ---------------------------------------------------------------------------
//  Appendix H — each month's plan in full: its day-type rows and P&L.
// ---------------------------------------------------------------------------

function monthPnlAppendix(c: Ctx, months: MonthPnl[]): void {
  const { doc } = c
  let y = pageStart(c, 'The plan in detail', 'The plan, month by month')
  for (const m of months) {
    if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, 'Appendix H', 'The plan, month by month (continued)') }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(BRAND)
    doc.text(`${m.month} — ${m.days} running days`, M, y); y += 4
    const totProfit = m.rows.reduce((t, r) => t + r.profitD * r.days, 0)
    const totCur = m.rows.reduce((t, r) => t + r.curProfitD * r.days, 0)
    const body = [
      ...m.rows.map((r) => [`${r.colour} · ${r.dayClass}`, r.svcProp === 0 ? 'withdrawn' : r.planName, String(r.days), `${r.svcCur} › ${r.svcProp}`,
        gbp0(r.revD), gbp0(r.costD), signed(r.profitD), signed(r.profitD * r.days), signed(r.curProfitD * r.days)]),
      ['MONTH TOTAL', '', String(m.days), '', '', '', '', signed(totProfit), signed(totCur)],
    ]
    y = baseTable(c, {
      startY: y,
      head: [[
        { content: 'WAS', styles: { halign: 'left' } },
        { content: 'BECOMES', styles: { halign: 'left' } },
        { content: 'DAYS', styles: { halign: 'right' } },
        { content: 'WORKINGS', styles: { halign: 'center' } },
        { content: 'REVENUE/DAY', styles: { halign: 'right' } },
        { content: 'COST/DAY', styles: { halign: 'right' } },
        { content: 'PROFIT/DAY', styles: { halign: 'right' } },
        { content: 'MONTH PROFIT', styles: { halign: 'right' } },
        { content: 'TODAY', styles: { halign: 'right' } },
      ]],
      body,
      styles: { fontSize: 6.8, cellPadding: { top: 1.5, bottom: 1.5, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: {
        0: { fontStyle: 'bold' }, 1: { fontStyle: 'bold' }, 2: { halign: 'right' }, 3: { halign: 'center' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' },
      },
      didParseCell: hooks(
        signCols([6, 7, 8]),
        mutedCols([3]),
        totalsFill(body.length - 1),
        (dta) => {
          if (dta.section === 'body' && dta.column.index === 1 && dta.cell.text[0] === 'withdrawn') {
            dta.cell.styles.textColor = NEG
          }
        },
      ),
      didDrawCell: totalsRule(c, body.length - 1),
    }) + 6
  }
}

// ---------------------------------------------------------------------------
//  Appendix I — the running diagrams for every proposed pattern, in the
//  TTBuilder working-timetable format the railway plans in. One table
//  language: brand header band, gold headcodes, cream meta rows.
// ---------------------------------------------------------------------------

const TP_ROWS_NS: Record<'N' | 'S', [string, string][]> = {
  N: [['Pickering', 'd'], ['Levisham', 'a'], ['Levisham', 'd'], ['Newtondale', 'f'], ['Goathland', 'a'], ['Goathland', 'd'], ['Grosmont', 'a'], ['Grosmont', 'd'], ['Whitby', 'a']],
  S: [['Whitby', 'd'], ['Grosmont', 'a'], ['Grosmont', 'd'], ['Goathland', 'a'], ['Goathland', 'd'], ['Newtondale', 'f'], ['Levisham', 'a'], ['Levisham', 'd'], ['Pickering', 'a']],
}

/** The shared TTBuilder-style table: gold headcodes on the brand band, cream
 *  Loco/Set/Forms meta rows, zebra timing rows. */
function wttTable(c: Ctx, works: TtExportWorking[], dir: 'N' | 'S', y: number, reqLabel: string): number {
  const rowsSpec = TP_ROWS_NS[dir]
  const used = rowsSpec.map((_, i) => works.some((w) => (w.t[i] ?? '') !== '' && w.t[i] != null))
  const tokTxt = (tok: string | null): string => {
    if (!tok) return '·'
    if (tok === 'r' || tok === 'ns' || tok === 'RR') return tok
    const s = String(tok).replace(/x/i, '')
    const p4 = s.replace(/[^0-9]/g, '').padStart(4, '0')
    return `${p4.slice(0, 2)}:${p4.slice(2)}${/x/i.test(tok) ? 'x' : ''}`
  }
  const body: string[][] = [
    ['Loco', ...works.map((w) => w.loco || '—')],
    ['Set', ...works.map((w) => w.set || '—')],
    ...rowsSpec.map(([st, kind], i) => (used[i]
      ? [`${st} ${kind === 'f' ? reqLabel : kind}`, ...works.map((w) => tokTxt(w.t[i]))]
      : null)).filter((x): x is string[] => x !== null),
    ['Forms', ...works.map((w) => w.forms || '—')],
  ]
  const metaRows = new Set([0, 1, body.length - 1])
  return baseTable(c, {
    startY: y,
    head: [[
      { content: 'HEADCODE', styles: { halign: 'left', textColor: '#ffffff', fontSize: 6.5 } },
      ...works.map((w) => ({ content: w.headcode, styles: { halign: 'center', textColor: GOLD, fontSize: 7.5 } })),
    ]],
    body,
    styles: { fontSize: 6.8, cellPadding: { top: 1.3, bottom: 1.3, left: 2.4, right: 2.4 }, halign: 'center', lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 26 } },
    didParseCell: (dta: ATHook) => {
      if (dta.section !== 'body') return
      if (metaRows.has(dta.row.index)) {
        dta.cell.styles.fillColor = CREAM
        if (dta.column.index === 0) { dta.cell.styles.textColor = GOLD_TEXT }
        else { dta.cell.styles.textColor = MUTED }
      }
    },
  })
}

function planWttAppendix(c: Ctx, patterns: PlanWttPattern[]): void {
  const { doc } = c
  let y = pageStart(c, 'Appendix I', 'Running diagrams — every proposed pattern')
  for (const pat of patterns) {
    if (y > FOOT_Y - 80) { newPage(c); y = pageStart(c, 'Appendix I', 'Running diagrams (continued)') }
    doc.setFillColor(pat.colour)
    doc.roundedRect(M, y - 3.2, 3.6, 3.6, 0.6, 0.6, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(BRAND)
    doc.text(`${pat.name} — ${pat.daysUsed} days (${pat.monthsUsed.join(', ')})`, M + 5.5, y)
    y += 4
    for (const [dirKey, dirLabel, works] of [['N', 'Northbound', pat.N], ['S', 'Southbound', pat.S]] as const) {
      if (works.length === 0) continue
      if (y > FOOT_Y - 65) {
        newPage(c); y = pageStart(c, 'Appendix I', 'Running diagrams (continued)')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(BRAND)
        doc.text(`${pat.name} (continued)`, M, y); y += 4
      }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(MUTED)
      doc.text(dirLabel.toUpperCase(), M, y, { charSpace: 0.3 }); y += 2.5
      y = wttTable(c, works as TtExportWorking[], dirKey, y, 'req') + 4
    }
    y += 3
  }
}

// ---------------------------------------------------------------------------
//  The money page's drawn components (Design spec §2.5.5 + p5 callout)
// ---------------------------------------------------------------------------

/** Horizontal sensitivity bars in a bordered card, with a dashed
 *  recommended-case line across all bars and a how-to-read caption inside. */
function sensitivityCard(c: Ctx, y: number): number {
  const { doc, d } = c
  const rows = d.sensitivity
  const base = d.proposed.periodRevenue - d.proposed.periodCosts
  const labelW = 40
  const valueW = 22
  const trackX = M + labelW + 4
  const trackW = CW - labelW - valueW - 14
  const rowH = 8
  const caption = `The dashed line is the recommended case (${gbp0(base)}). Each bar shows where contribution lands if one ` +
    'assumption moves. Where a bar crosses the line, the assumption is load-bearing and should be confirmed before committing.'
  doc.setFontSize(7)
  const capLines = doc.splitTextToSize(caption, CW - 12) as string[]
  const cardH = 8 + rows.length * rowH + capLines.length * 2.9 + 6
  doc.setDrawColor(RULE)
  doc.setLineWidth(0.25)
  doc.roundedRect(M, y, CW, cardH, 2, 2, 'S')
  const vals = [base, ...rows.map((r) => r.contribution)]
  const lo2 = Math.min(0, ...vals)
  const span = Math.max(1, Math.max(...vals) - lo2)
  const wOf = (v: number) => Math.max(2, ((v - lo2) / span) * trackW * 0.92)
  let yy = y + 7
  for (const r of rows) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(INK)
    doc.text(r.label, M + labelW, yy + 3.6, { align: 'right' })
    doc.setFillColor(PAPER)
    doc.roundedRect(trackX, yy, trackW, 5.5, 1, 1, 'F')
    const fill = r.contribution < 0 ? NEG
      : Math.abs(r.contribution - base) <= Math.abs(base) * 0.005 ? BRAND
        : r.contribution > base ? POS : GOLD
    doc.setFillColor(fill)
    doc.roundedRect(trackX, yy, wOf(r.contribution), 5.5, 1, 1, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.9); doc.setTextColor(BRAND)
    doc.text(gbp0(r.contribution), M + CW - 6, yy + 3.6, { align: 'right' })
    yy += rowH
  }
  // the dashed recommended-case line, across every bar
  const bx = trackX + wOf(base)
  doc.setDrawColor(INK)
  doc.setLineWidth(0.4)
  doc.setLineDashPattern([1.1, 0.8], 0)
  doc.line(bx, y + 5, bx, y + 7 + rows.length * rowH - 1)
  doc.setLineDashPattern([], 0)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(MUTED)
  doc.text(capLines, M + 6, yy + 2.5)
  doc.setTextColor(INK)
  return y + cardH + 5
}

/** The gold-barred cream callout — "How to read this page." */
function howToReadBox(c: Ctx, y: number, lead: string, text: string): number {
  const { doc } = c
  doc.setFontSize(8.3)
  const lines = doc.splitTextToSize(`${lead} ${text}`, CW - 16) as string[]
  const h = lines.length * 4.4 + 8
  doc.setFillColor(CREAM)
  doc.roundedRect(M, y, CW, h, 2, 2, 'F')
  doc.setFillColor(GOLD)
  doc.roundedRect(M + 4, y + 4, 1.4, h - 8, 0.7, 0.7, 'F')
  richPara(c, [{ t: lead, b: true }, { t: text }], y + 6, 8.3, { x: M + 9, width: CW - 16, leading: 1.55 })
  return y + h + 5
}

// ---------------------------------------------------------------------------
//  A day in the life — the two days side by side with load bars (spec p8)
// ---------------------------------------------------------------------------

interface DayRow { dep: string; journey: string; carried: string; pct: number; load: string; ecs: boolean }

function dayRowsFor(c: Ctx, side: ExecSide): DayRow[] {
  const { d } = c
  return side.deps
    .filter((x) => x.enabled)
    .sort((a, b) => a.minutes - b.minutes)
    .map((x) => {
      const r = side.scenario.perDep.find((p) => p.id === x.id)
      const cap = r?.capacity ?? x.capacity ?? 0
      const pct = r && cap > 0 ? Math.round((r.allocated / cap) * 100) : 0
      return {
        dep: x.time,
        journey: `${d.net.stations[x.o].name} – ${d.net.stations[x.d].name}${x.positioning ? ' (empty stock)' : ''}`,
        carried: x.positioning ? 'ECS' : num0(r?.allocated ?? 0),
        pct,
        load: `${pct}%`,
        ecs: !!x.positioning,
      }
    })
}

/** One half-width schedule table under its coloured header band. */
function dayScheduleTable(
  c: Ctx, x: number, w: number, y: number,
  bandFill: string, metaInk: string, title: string, meta: string, rows: DayRow[]
): number {
  const { doc } = c
  doc.setFillColor(bandFill)
  doc.roundedRect(x, y, w, 6.5, 1.5, 1.5, 'F')
  doc.rect(x, y + 3.5, w, 3, 'F') // square the band's lower corners
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor('#ffffff')
  doc.text(title, x + 3, y + 4.3)
  doc.setFontSize(6.5); doc.setTextColor(metaInk)
  doc.text(meta, x + w - 3, y + 4.3, { align: 'right' })
  doc.setTextColor(INK)
  const loadRows = rows.map((r) => ({ pct: r.pct, label: r.load }))
  const ecsRows = new Set(rows.map((r, i) => (r.ecs ? i : -1)).filter((i) => i >= 0))
  return baseTable(c, {
    startY: y + 6.5,
    head: [[
      { content: 'DEP', styles: { halign: 'left' } },
      { content: 'JOURNEY', styles: { halign: 'left' } },
      { content: 'CARRIED', styles: { halign: 'right' } },
      { content: 'LOAD', styles: { halign: 'left' } },
    ]],
    body: rows.map((r) => [r.dep, r.journey, r.carried, '']),
    margin: { left: x, right: PW - x - w },
    tableWidth: w,
    styles: { fontSize: 6.8, cellPadding: { top: 1.5, bottom: 1.5, left: 2.2, right: 2.2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
    headStyles: {
      fillColor: CREAM, textColor: MUTED, fontSize: 6, fontStyle: 'bold',
      cellPadding: { top: 1.8, bottom: 1.8, left: 2.2, right: 2.2 }, lineWidth: 0,
    },
    columnStyles: { 0: { cellWidth: 11, fontStyle: 'bold' }, 2: { halign: 'right', cellWidth: 14 }, 3: { cellWidth: 21 } },
    didParseCell: (dta: ATHook) => {
      if (dta.section === 'body' && ecsRows.has(dta.row.index)) {
        if (dta.column.index === 1 || dta.column.index === 2) dta.cell.styles.textColor = GREY_KIND
      }
    },
    didDrawCell: (dta: ATHook) => {
      if (dta.section !== 'body' || dta.column.index !== 3) return
      const lr = loadRows[dta.row.index]
      if (!lr) return
      const bx = dta.cell.x + 2
      const by = dta.cell.y + dta.cell.height / 2 - 0.8
      doc.setFillColor(RULE_LIGHT)
      doc.roundedRect(bx, by, 10, 1.6, 0.6, 0.6, 'F')
      const fill = lr.pct >= 55 ? POS : lr.pct >= 10 ? GOLD : NEG
      doc.setFillColor(fill)
      doc.roundedRect(bx, by, Math.max(0.6, Math.min(10, (lr.pct / 100) * 10)), 1.6, 0.6, 0.6, 'F')
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.2); doc.setTextColor(MUTED)
      doc.text(lr.label, bx + 12, dta.cell.y + dta.cell.height / 2 + 1)
      doc.setTextColor(INK)
    },
  })
}

/** One cream stat tile (the p2 hero strip). */
function creamStat(c: Ctx, x: number, w: number, y: number, label: string, value: string, ink: string, sub: string): void {
  const { doc } = c
  doc.setFillColor(CREAM)
  doc.roundedRect(x, y, w, 18, 1.5, 1.5, 'F')
  doc.setFont('helvetica', 'bold'); doc.setFontSize(6.4); doc.setTextColor(GOLD_TEXT)
  doc.text(label, x + 4, y + 5, { charSpace: 0.25 })
  doc.setFontSize(15); doc.setTextColor(ink)
  doc.text(value, x + 4, y + 11.5)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(MUTED)
  doc.text(sub, x + 4, y + 15.5)
  doc.setTextColor(INK)
}

// ---------------------------------------------------------------------------
//  REPORT A (Chris, 2026-08-27) — extra sections
// ---------------------------------------------------------------------------

/** Section 2: the Build-tab season roll-up, current vs modelled, on its own
 *  page — the "£237k / £1.78m" table, exactly as the studio shows it. */
export function seasonRollupPage(c: Ctx, d: ExecReportData): void {
  const { doc } = c
  const yr = d.yearRows ?? []
  let y = pageStart(c, 'The season at a glance', 'Season by month — current vs modelled', 18)
  if (yr.length === 0) { para(c, 'No season programme yet — design month slices to fill this table.', y, 9); newPage(c); return }
  const tot = yearTotals(yr)
  const halfW = (CW - 6) / 2
  const banner = (x: number, label: string, side: typeof tot.cur, fill: string, ink: string) => {
    doc.setFillColor(fill); doc.roundedRect(x, y, halfW, 26, 2, 2, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(label.startsWith('CURRENT') ? MUTED : GOLD)
    doc.text(label, x + 6, y + 8, { charSpace: 0.3 })
    doc.setFontSize(20); doc.setTextColor(ink)
    doc.text(side ? gbpK(side.profit) : '—', x + 6, y + 19)
    if (side) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(label.startsWith('CURRENT') ? MUTED : HEADER_SUB)
      doc.text(`profit · rev ${gbpK(side.revenue)} · cost ${gbpK(side.costs)} · ${num0(side.carried)} carried · up to ${side.maxLocos} locos`, x + 6, y + 24)
    }
  }
  banner(M, `CURRENT — SEASON TOTAL (${tot.runningDays} RUNNING DAYS)`, tot.cur, CREAM, BRAND)
  banner(M + halfW + 6, 'MODELLED — SEASON TOTAL', tot.mod, BRAND, '#ffffff')
  doc.setTextColor(INK)
  y += 26 + 6
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(MUTED)
  doc.text("Every month of the season is listed, running or not. FIXED is the P&L's fixed lines for THAT month - the line runs all year, so a month with no standard services carries them with nothing else, on both sides (the Cost model sliders are how they flex). VARIABLE is the cost of running the trains: per running day, per turn, per train-mile, per passenger.", M, y, { maxWidth: CW })
  doc.setTextColor(INK)
  y += 8
  const sideCells = (sv: YearRow['cur'], days: number | null): string[] => {
    const f = monthFigures(sv, days)
    if (!sv || !f || days == null) return ['—', '—', '—', '—', '—', '—']
    const runs = sv.services > 0
    return [
      runs ? `${sv.servicesRange ? `${sv.servicesRange[0]}-${sv.servicesRange[1]}` : sv.services}` : (days === 0 ? '—' : '0'),
      runs ? `${sv.steamN}S${sv.dieselN > 0 ? `+${sv.dieselN}D` : ''}` : '—',
      num0(f.pax), f.fixed == null ? '—' : gbp0(f.fixed), f.variable == null ? '—' : gbp0(f.variable), signed(f.profit),
    ]
  }
  const body: (string | { content: string; styles?: Record<string, unknown> })[][] = yr.map((r) => {
    const cur = sideCells(r.cur, r.runningDays), mod = sideCells(r.mod, r.runningDays)
    const fc = monthFigures(r.cur, r.runningDays), fm = monthFigures(r.mod, r.runningDays)
    const dp = fc && fm ? signed(fm.profit - fc.profit) : '—'
    return [r.monthLabel, r.runningDays != null ? String(r.runningDays) : '—', ...cur, ...mod, dp]
  })
  const totCells = (t: typeof tot.cur): string[] => t
    ? ['', `${t.maxLocos} locos`, num0(t.carried), t.fixed == null ? '—' : gbp0(t.fixed), t.variable == null ? '—' : gbp0(t.variable), signed(t.profit)]
    : ['—', '—', '—', '—', '—', '—']
  const totRow = ['SEASON', String(tot.runningDays), ...totCells(tot.cur), ...totCells(tot.mod), (tot.cur && tot.mod) ? signed(tot.mod.profit - tot.cur.profit) : '—']
  body.push(totRow)
  baseTable(c, {
    startY: y,
    head: [
      [{ content: '', colSpan: 2, styles: { fillColor: '#ffffff' } },
       { content: 'CURRENT', colSpan: 6, styles: { halign: 'center', fillColor: BRAND_MID } },
       { content: 'MODELLED', colSpan: 6, styles: { halign: 'center' } },
       { content: '', styles: { fillColor: '#ffffff' } }],
      [{ content: 'MONTH', styles: { halign: 'left' } }, { content: 'DAYS', styles: { halign: 'right' } },
       { content: 'TURNS/DAY', styles: { halign: 'left', fillColor: BRAND_MID } }, { content: 'LOCO', styles: { halign: 'left', fillColor: BRAND_MID } }, { content: 'PAX', styles: { halign: 'right', fillColor: BRAND_MID } }, { content: 'FIXED', styles: { halign: 'right', fillColor: BRAND_MID } }, { content: 'VARIABLE', styles: { halign: 'right', fillColor: BRAND_MID } }, { content: 'PROFIT', styles: { halign: 'right', fillColor: BRAND_MID } },
       { content: 'TURNS/DAY', styles: { halign: 'left' } }, { content: 'LOCO', styles: { halign: 'left' } }, { content: 'PAX', styles: { halign: 'right' } }, { content: 'FIXED', styles: { halign: 'right' } }, { content: 'VARIABLE', styles: { halign: 'right' } }, { content: 'PROFIT', styles: { halign: 'right' } },
       { content: 'CHANGE', styles: { halign: 'right' } }],
    ],
    body,
    styles: { fontSize: 5.8, cellPadding: { top: 1.3, bottom: 1.3, left: 1.2, right: 1.2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
    headStyles: { fontSize: 5.2, cellPadding: { top: 2, bottom: 2, left: 1.2, right: 1.2 } },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 13 }, 1: { halign: 'right', cellWidth: 7 }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 10: { halign: 'right' }, 11: { halign: 'right' }, 12: { halign: 'right' }, 13: { halign: 'right' }, 14: { halign: 'right' } },
    didParseCell: hooks(signCols([7, 13, 14]), totalsFill(body.length - 1)),
    didDrawCell: totalsRule(c, body.length - 1),
  })
  newPage(c)
}

/** Section 6: Costs & modelling data — the Costs-tab table (post-build), then a
 *  worked August day costed service by service, its ticket split, and how the
 *  £/day total is made up (Chris asked for the breakdown repeatedly). */
export function costsModellingSection(c: Ctx, d: ExecReportData): void {
  const { doc } = c
  const CATS: { key: 'loco' | 'carriages' | 'crew' | 'infra' | 'overhead'; label: string }[] = [
    { key: 'loco', label: 'Locomotives (fuel + running maint + footplate + overhaul prov.)' },
    { key: 'carriages', label: 'Carriages (maint + cleaning + restoration prov.)' },
    { key: 'crew', label: 'Train crew (guards + TTI + on-costs)' },
    { key: 'infra', label: 'Infrastructure (P-way + S&T + NR access)' },
    { key: 'overhead', label: 'Fixed overheads (boxes, stations, insurance, marketing)' },
  ]
  let y = pageStart(c, 'Costs & modelling data', 'The costs behind the plan', 18)
  // ONE cost model in the paper (Chris, 2026-09-04): when the Management P&L is
  // loaded, every cost table here is the P&L — the rate card is never printed
  // beside it. The rate-card tables survive only for a report with no P&L basis.
  const pnlYr = d.costModelPnl ? (d.yearRows ?? []).filter((r) => (r.runningDays ?? 0) > 0 && (r.cur || r.mod)) : []
  y = standfirst(c, d.costModelPnl
    ? `These are the costs the model used to generate every figure in this report: the ${d.costModelPnl.basis} Management P&L, line by line. FIXED lines are charged to their own month whether or not trains run (a running day carries its share); VARIABLE lines are the trains' cost at the basis season's own rate — per working turn, per train-mile, per passenger, per running day. No rate card is used for any figure in this report.`
    : 'These are the costs the model used to generate every figure in this report — the post-build cost model, month by month. A train is a locomotive plus a set of carriages, each costed separately; infrastructure is per mile by segment.', y)
  const mc = d.monthCosts ?? []
  if (pnlYr.length > 0) {
    const per = (v: number | null | undefined, days: number) => (v == null ? '—' : gbp0(v / Math.max(1, days)))
    const body = pnlYr.map((r) => {
      const days = r.runningDays ?? 0
      const fc = monthFigures(r.cur, days), fm = monthFigures(r.mod, days)
      return [r.monthLabel, String(days), per(fm?.fixed ?? fc?.fixed, days), per(fc?.variable, days), per(fm?.variable, days), fc ? gbp0(fc.costs / Math.max(1, days)) : '—', fm ? gbp0(fm.costs / Math.max(1, days)) : '—']
    })
    y = baseTable(c, {
      startY: y + 1,
      head: [[{ content: 'MONTH', styles: { halign: 'left' } }, { content: 'RUNNING DAYS', styles: { halign: 'right' } },
        { content: 'FIXED £/DAY', styles: { halign: 'right' } }, { content: 'VARIABLE £/DAY TODAY', styles: { halign: 'right' } }, { content: 'VARIABLE £/DAY PROPOSED', styles: { halign: 'right' } },
        { content: 'TOTAL £/DAY TODAY', styles: { halign: 'right' } }, { content: 'TOTAL £/DAY PROPOSED', styles: { halign: 'right' } }]],
      body,
      styles: { fontSize: 6.6, cellPadding: { top: 1.3, bottom: 1.3, left: 1.6, right: 1.6 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    }) + 2
    y = para(c, "FIXED £/DAY is the month's own fixed lines divided by its running days (the proposal's flex sliders applied); VARIABLE £/DAY is the trains' cost per running day at the basis rates. Multiplied by the running days these are the season table's FIXED and VARIABLE columns exactly.", y, 6.8, { color: MUTED }) + 4
  } else if (mc.length > 0) {
    const catVal = (m: ExecMonthCatCosts, k: typeof CATS[number]['key']) => m.perDay[k] * m.days
    const body = CATS.map((cat) => [
      cat.label,
      gbp0(mc.reduce((t, m) => t + m.perDay[cat.key], 0) / mc.length),
      ...mc.map((m) => gbp0(catVal(m, cat.key))),
      gbp0(mc.reduce((t, m) => t + catVal(m, cat.key), 0)),
    ])
    body.push(['Total', gbp0(mc.reduce((t, m) => t + CATS.reduce((s, cat) => s + m.perDay[cat.key], 0), 0) / mc.length),
      ...mc.map((m) => gbp0(CATS.reduce((s, cat) => s + catVal(m, cat.key), 0))),
      gbp0(mc.reduce((t, m) => t + CATS.reduce((s, cat) => s + catVal(m, cat.key), 0), 0))])
    y = baseTable(c, {
      startY: y + 1,
      head: [[{ content: 'Category', styles: { halign: 'left' } }, { content: '£/day', styles: { halign: 'right' } },
        ...mc.map((m) => ({ content: m.label, styles: { halign: 'right' as const } })),
        { content: 'Season', styles: { halign: 'right' } }]],
      body,
      styles: { fontSize: 6.2, cellPadding: { top: 1.3, bottom: 1.3, left: 1.6, right: 1.6 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 46 }, 1: { halign: 'right' } },
      didParseCell: totalsFill(body.length - 1),
      didDrawCell: totalsRule(c, body.length - 1),
    }) + 6
  }

  const ex = d.exampleDay
  if (ex) {
    if (y > FOOT_Y - 90) { newPage(c); y = pageStart(c, 'Costs & modelling data', 'A worked day — ' + ex.label, 18) }
    else y = sectionLabel(c, `A worked running day — ${ex.label}`, y + 1)
    y = para(c, `Every running day in the season has been cost-mapped like this one: each service costed, the traffic and capacity compared against the same day last year, and the timetable remapped to carry the same passengers for less. Below is one August day in full. The optimised (proposed) day is shown against today's unoptimised day; the drill-downs that follow are for the optimised day.`, y, 7.5)

    // optimised vs unoptimised headline boxes
    const halfW = (CW - 6) / 2
    const dayBox = (x: number, title: string, s: ExecDaySide, fill: string, ink: string, sub: string) => {
      doc.setFillColor(fill); doc.setDrawColor(RULE); doc.setLineWidth(0.25); doc.roundedRect(x, y, halfW, 26, 2, 2, 'FD')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(ink === '#ffffff' ? GOLD : GOLD_TEXT)
      doc.text(title, x + 5, y + 6.5, { charSpace: 0.3 })
      doc.setFontSize(9); doc.setTextColor(ink)
      doc.text(`${s.services} workings · ${s.steamN}S${s.dieselN > 0 ? `+${s.dieselN}D` : ''}`, x + 5, y + 13)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5)
      doc.text(`${num0(s.pax)} carried · rev ${gbp0(s.revenue)} · cost ${gbp0(s.costs)}`, x + 5, y + 19)
      doc.setFont('helvetica', 'bold'); doc.setTextColor(s.profit >= 0 ? POS : NEG)
      doc.text(`profit ${signed(s.profit)}`, x + 5, y + 24)
      void sub
    }
    dayBox(M, 'OPTIMISED — PROPOSED DAY', ex.optimised, CREAM, BRAND, '')
    dayBox(M + halfW + 6, 'UNOPTIMISED — TODAY', ex.unoptimised, '#ffffff', MUTED, '')
    doc.setTextColor(INK); y += 26 + 6

    // how the £/day is made up — the pro-rated cost line items
    if (ex.costLines.length > 0) {
      if (y > FOOT_Y - 40) { newPage(c); y = pageStart(c, 'Costs & modelling data', 'A worked day (continued)', 18) }
      y = sectionLabel(c, `How the £${num0(Math.round(ex.optimised.costs))} cost of this day is made up`, y + 1)
      const clBody = ex.costLines.map((l) => [l.label, tidyBasis(l.detail ?? ''), gbp0(l.amount)])
      clBody.push(['Total for the day', '', gbp0(ex.costLines.reduce((t, l) => t + l.amount, 0))])
      y = baseTable(c, {
        startY: y, head: [[{ content: 'COST LINE', styles: { halign: 'left' } }, { content: 'BASIS', styles: { halign: 'left' } }, { content: 'THIS DAY', styles: { halign: 'right' } }]],
        body: clBody,
        styles: { fontSize: 7, cellPadding: { top: 1.5, bottom: 1.5, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 46 }, 1: { textColor: MUTED }, 2: { halign: 'right' } },
        didParseCell: totalsFill(clBody.length - 1), didDrawCell: totalsRule(c, clBody.length - 1),
      }) + 6
    }

    // the day's services, costed
    if (ex.services.length > 0) {
      if (y > FOOT_Y - 50) { newPage(c); y = pageStart(c, 'Costs & modelling data', 'A worked day (continued)', 18) }
      const pnlDay = ex.costBasis === 'pnl'
      y = sectionLabel(c, pnlDay ? 'The running services this day — each at its P&L variable cost' : 'The running services this day — each costed', y + 1)
      if (pnlDay) y = para(c, "Each working is priced from the Management P&L: the per-working-turn lines, plus the per-train-mile lines for its own miles, plus the per-passenger lines for the people who board it. The per-running-day charge and the day's share of the month's fixed lines are the day's cost, never one train's, and are added beneath so the total is the cost of the day.", y, 7, { color: MUTED })
      const svcSum = ex.services.reduce((t, s) => t + s.cost, 0)
      const svcBody = ex.services.map((s) => [s.time, s.route, s.arr, s.set, num0(s.capacity), s.loco, gbp0(s.cost)])
      if (pnlDay) {
        const ecs = ex.ecsMoves
        if (ecs && ecs.count > 0) svcBody.push(['', `Empty-stock moves — ${ecs.count} × ${Math.round(ecs.miles)} train-miles at the per-mile lines`, '', '', '', '', gbp0(ecs.cost)])
        svcBody.push(['', 'Running-day charge (P&L lines per running day)', '', '', '', '', gbp0(ex.runningDayCharge ?? 0)])
        svcBody.push(['', `Day's share of ${ex.monthLabel}'s fixed lines`, '', '', '', '', gbp0(ex.fixedShare ?? 0)])
        svcBody.push(['', 'TOTAL — the cost of the day', '', '', '', '', gbp0(svcSum + (ecs?.cost ?? 0) + (ex.runningDayCharge ?? 0) + (ex.fixedShare ?? 0))])
      } else {
        svcBody.push(['', 'TOTAL', '', '', '', '', gbp0(svcSum)])
      }
      y = baseTable(c, {
        startY: y, head: [[{ content: 'DEP', styles: { halign: 'left' } }, { content: 'ROUTE', styles: { halign: 'left' } }, { content: 'ARR', styles: { halign: 'left' } }, { content: 'SET', styles: { halign: 'left' } }, { content: 'SEATS', styles: { halign: 'right' } }, { content: 'LOCO', styles: { halign: 'left' } }, { content: pnlDay ? 'VARIABLE COST' : 'COST', styles: { halign: 'right' } }]],
        body: svcBody,
        styles: { fontSize: 7, cellPadding: { top: 1.4, bottom: 1.4, left: 2.2, right: 2.2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold' }, 4: { halign: 'right' }, 6: { halign: 'right' } },
        didParseCell: totalsFill(svcBody.length - 1), didDrawCell: totalsRule(c, svcBody.length - 1),
      }) + 6
    }

    // the tickets used
    if (ex.ticketSplit.length > 0) {
      if (y > FOOT_Y - 50) { newPage(c); y = pageStart(c, 'Costs & modelling data', 'A worked day (continued)', 18) }
      y = sectionLabel(c, 'The tickets used in the modelling — this day', y + 1)
      const tkBody = ex.ticketSplit.map((t) => [t.item, num0(t.tickets), gbp0(t.net), `${(t.share * 100).toFixed(1)}%`])
      // Total row — tickets and revenue summed for the day (Chris, 2026-08-27).
      const tkTotTickets = ex.ticketSplit.reduce((t, x) => t + x.tickets, 0)
      const tkTotNet = ex.ticketSplit.reduce((t, x) => t + x.net, 0)
      tkBody.push(['Total for the day', num0(tkTotTickets), gbp0(tkTotNet), '100.0%'])
      y = baseTable(c, {
        startY: y, head: [[{ content: 'TICKET / ITEM', styles: { halign: 'left' } }, { content: 'TICKETS', styles: { halign: 'right' } }, { content: 'NET £', styles: { halign: 'right' } }, { content: '% OF REVENUE', styles: { halign: 'right' } }]],
        body: tkBody,
        styles: { fontSize: 7, cellPadding: { top: 1.3, bottom: 1.3, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { cellWidth: 90 }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        didParseCell: totalsFill(tkBody.length - 1),
        didDrawCell: totalsRule(c, tkBody.length - 1),
      }) + 6
    }
    // A day in the life — the example August day, the proposed day against the
    // same day today, DEP by DEP with load bars (Chris, 2026-08-27), at the end
    // of the worked-day section.
    const augAm = (d.appendixMonths ?? []).find((am) => /Aug/i.test(am.label))
    if (augAm) {
      // Its own page: the two full-day schedules are a big block, and the
      // preceding tickets table leaves y stale (it is the section's last table),
      // so anything short of a page break overprints it (Chris, 2026-08-27).
      newPage(c); y = pageStart(c, 'Costs & modelling data', `A day in the life — ${ex.label}`, 18)
      y = para(c, 'The proposed running day against the same day run today — every departure, who it carries and how full it is.', y, 7.5)
      const halfW = (CW - 6) / 2
      const propRows = dayRowsFor(c, augAm.proposed)
      const curRows = dayRowsFor(c, augAm.current)
      const yP = dayScheduleTable(c, M, halfW, y, BRAND, GOLD, 'THE PROPOSED DAY', `${propRows.filter((r) => !r.ecs).length} workings`, propRows)
      const yC = dayScheduleTable(c, M + halfW + 6, halfW, y, MUTED, '#e6e2d6', 'THE SAME DAY TODAY', `${curRows.filter((r) => !r.ecs).length} workings`, curRows)
      y = Math.max(yP, yC) + 4
    }
  }
  newPage(c)
}

/**
 * Modelling Costs — the accounts-anchored cost model (Management P&L basis), the
 * PRIMARY cost view of the board paper: Table 1 (source, the basis-season P&L) vs
 * Table 2 (after the build), line by line with the change. WinAnsi only.
 */
export function modellingCostsPnlSection(c: Ctx, d: ExecReportData): void {
  const p = d.costModelPnl
  if (!p || p.groups.every((g) => g.rows.filter((r) => r.included).length === 0)) return
  let y = pageStart(c, 'Modelling costs - the Management P&L', `Cost basis: ${p.basis} actuals`, 18)
  y = standfirst(c, `Costs in this plan are anchored to the ${p.basis} Management P&L - every direct and indirect expense line and departmental labour cost, carried at its real monthly figure and re-priced by the designed timetable's activity. ${p.hasModel ? `The design changes turns by ${Math.round(p.flexCap * 100)}%; fixed lines are held at 100% unless flexed, and never below that ceiling.` : 'The season has not been built, so the modelled column mirrors the basis.'}`, y)

  const change = p.modelTotal - p.basisTotal
  y = sectionLabel(c, 'Season cost - source vs modelled', y + 1)
  const headBody = [
    [`Total cost (${p.basis} basis)`, gbp0(p.basisTotal)],
    ['Total cost (modelled)', gbp0(p.modelTotal)],
    ['Change', signed(change)],
  ]
  y = baseTable(c, {
    startY: y, head: [[{ content: 'MEASURE', styles: { halign: 'left' } }, { content: 'GBP / SEASON', styles: { halign: 'right' } }]],
    body: headBody,
    styles: { fontSize: 8, cellPadding: { top: 1.6, bottom: 1.6, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 90 }, 1: { halign: 'right' } },
    didParseCell: totalsFill(headBody.length - 1), didDrawCell: totalsRule(c, headBody.length - 1),
  }) + 6

  for (const g of p.groups) {
    const rows = g.rows.filter((r) => r.included)
    if (rows.length === 0) continue
    if (y > FOOT_Y - 40) { newPage(c); y = pageStart(c, 'Modelling costs - the Management P&L', g.title, 18) }
    else y = sectionLabel(c, g.title, y + 1)
    // RATE = the one per-unit £ that prices BOTH columns (basis-season £ ÷
    // basis-season driver units) — the planner's double-check that the modelled
    // column is the same rate applied to the designed activity, nothing else.
    const body: (string)[][] = rows.map((r) => [r.label, r.behaviour, r.rate ?? '-', gbp0(r.basis), gbp0(r.model), Math.abs(r.delta) < 0.5 ? '-' : signed(r.delta)])
    const sB = rows.reduce((t, r) => t + r.basis, 0), sM = rows.reduce((t, r) => t + r.model, 0)
    body.push(['Subtotal', '', '', gbp0(sB), gbp0(sM), signed(sM - sB)])
    y = baseTable(c, {
      startY: y, head: [[{ content: 'LINE', styles: { halign: 'left' } }, { content: 'BEHAVIOUR', styles: { halign: 'left' } },
        { content: 'RATE', styles: { halign: 'left' } },
        { content: 'SOURCE', styles: { halign: 'right' } }, { content: 'MODELLED', styles: { halign: 'right' } }, { content: 'CHANGE', styles: { halign: 'right' } }]],
      body,
      styles: { fontSize: 6.6, cellPadding: { top: 1.2, bottom: 1.2, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 62 }, 1: { textColor: MUTED, cellWidth: 26 }, 2: { textColor: MUTED, cellWidth: 32 }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
      didParseCell: totalsFill(body.length - 1), didDrawCell: totalsRule(c, body.length - 1),
    }) + 6
  }
  newPage(c)
}

const WSS_COLOURS: Record<string, string> = {
  RED: '#B83A2B', YELLOW: '#A6771C', GOLD: '#A6771C', PINK: '#C25F94',
  BLUE: '#2B7A8C', GREEN: '#2E7D4F', PURPLE: '#6B4FA0', DINER: '#4E4459', BLACK: '#333333',
}
const wssHex = (name: string | null): string => (name ? WSS_COLOURS[name.trim().toUpperCase()] ?? '#6b6b5e' : '#c9c4b6')

/** The Weekly Service Schedule calendar grid for one side (optimised or today)
 *  — a month of day cells with each day's P&L, exactly the studio's grid. */
function wssGrid(c: Ctx, weeks: ExecWssWeek[], y0: number): number {
  const { doc } = c
  const DOW = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
  const colW = (CW) / 8       // 7 day columns + a week-total column
  const rowH = 25             // room for a labelled line per figure + the Pullman marker
  let y = y0
  // header row
  doc.setFillColor(BRAND); doc.rect(M, y, CW, 5, 'F')
  doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor('#ffffff')
  DOW.forEach((d, i) => doc.text(d, M + i * colW + 2, y + 3.4))
  doc.text('WEEK', M + 7 * colW + 2, y + 3.4)
  y += 5
  for (const wk of weeks) {
    if (y + rowH > FOOT_Y) { newPage(c); drawHeaderBand(c); y = 22 }
    wk.cells.forEach((cell, i) => {
      const x = M + i * colW
      doc.setDrawColor(RULE_LIGHT); doc.setLineWidth(0.2); doc.rect(x, y, colW, rowH, 'S')
      if (!cell) return
      // colour band + date
      if (cell.side) { doc.setFillColor(wssHex(cell.colour)); doc.rect(x, y, colW, 2, 'F') }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(INK)
      doc.text(String(cell.date), x + 1.5, y + 6)
      if (cell.side?.firecon) {
        // "F" — a Firecon day: steam stood down, diesel only (Chris, 2026-09-11).
        doc.setTextColor(NEG); doc.setFontSize(5.5); doc.text('F', x + 6.5, y + 6)
      }
      if (cell.colour) { doc.setTextColor(wssHex(cell.colour)); doc.setFontSize(5); doc.text(cell.colour, x + colW - 1.5, y + 6, { align: 'right' }) }
      if (cell.side && cell.side.failure) {
        // A FAILURE DAY: no engine of any traction for a turn — the timetable
        // cannot run as designed; valued as a non-running day; RED.
        const s = cell.side
        doc.setFillColor(NEG); doc.rect(x, y, colW, 2, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(5); doc.setTextColor(NEG)
        doc.text('FAILURE DAY', x + 1.5, y + 9)
        doc.setFont('helvetica', 'normal'); doc.setFontSize(4.6)
        doc.text(`no engine for ${s.fleet?.noEngine ?? 1} turn${(s.fleet?.noEngine ?? 1) === 1 ? '' : 's'}`, x + 1.5, y + 12)
        doc.text('modelled as non-running', x + 1.5, y + 15)
        doc.setTextColor(MUTED); doc.text(`fixed cost ${gbpK(s.costs)}`, x + 1.5, y + 18)
        doc.setFont('helvetica', 'bold'); doc.setTextColor(NEG)
        doc.text(`profit ${kLabel(s.profit)}`, x + 1.5, y + 21)
      } else if (cell.side && cell.side.noService) {
        // The like-for-like basis day ran no standard services: 0 services,
        // fixed cost only (Chris, 2026-09-03) — said in words, not as a zero.
        const s = cell.side
        doc.setFont('helvetica', 'bold'); doc.setFontSize(5); doc.setTextColor(GREY_KIND)
        doc.text(s.unscorable ? 'no timetable pattern' : 'no standard services', x + 1.5, y + 9)
        doc.setFont('helvetica', 'normal'); doc.setFontSize(4.6)
        doc.text(s.unscorable ? `${cell.colour ?? 'this'} day - fixed cost only` : `on like day ${s.basisDate ?? ''}`.trim(), x + 1.5, y + 12)
        doc.setTextColor(MUTED); doc.text(`fixed cost ${gbpK(s.costs)}`, x + 1.5, y + 18)
        doc.setFont('helvetica', 'bold'); doc.setTextColor(NEG)
        doc.text(`profit ${kLabel(s.profit)}`, x + 1.5, y + 21)
        if (cell.pullman) {
          doc.setFontSize(4.6); doc.setTextColor('#6B4FA0')
          doc.text(`P x${cell.pullman.services} · ${num0(cell.pullman.covers)} covers`, x + 1.5, y + 24)
        }
      } else if (cell.side) {
        // Every figure is labelled so no reader has to guess (Chris, 2026-08-27).
        const s = cell.side
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5); doc.setTextColor(MUTED)
        doc.text(`${s.services} svc · ${s.steamN}S${s.dieselN > 0 ? `+${s.dieselN}D` : ''}`, x + 1.5, y + 9)
        doc.text(`${num0(s.pax)} pax`, x + 1.5, y + 12)
        doc.setTextColor(INK); doc.text(`rev ${gbpK(s.revenue)}`, x + 1.5, y + 15)
        doc.setTextColor(MUTED); doc.text(`cost ${gbpK(s.costs)}`, x + 1.5, y + 18)
        doc.setFont('helvetica', 'bold'); doc.setTextColor(s.profit >= 0 ? POS : NEG)
        doc.text(`profit ${kLabel(s.profit)}`, x + 1.5, y + 21)
        if (cell.pullman) {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(4.6); doc.setTextColor('#6B4FA0')
          doc.text(`P x${cell.pullman.services} · ${num0(cell.pullman.covers)} covers`, x + 1.5, y + 24)
        } else if (s.firecon) {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(4.6); doc.setTextColor(NEG)
          doc.text(`Firecon day ${s.firecon.day}${s.firecon.pct > 0 ? ` · sales -${s.firecon.pct}%` : ''}`, x + 1.5, y + 24)
        } else if (s.fleet && (s.fleet.swapped > 0 || s.fleet.noEngine > 0)) {
          // The availability calendar bit: a steam turn run diesel, or a turn with no engine.
          doc.setFont('helvetica', 'bold'); doc.setFontSize(4.6); doc.setTextColor(s.fleet.noEngine > 0 ? NEG : MUTED)
          doc.text(s.fleet.noEngine > 0 ? `no engine for ${s.fleet.noEngine} turn${s.fleet.noEngine === 1 ? '' : 's'}` : `${s.fleet.swapped} steam turn${s.fleet.swapped === 1 ? '' : 's'} run diesel`, x + 1.5, y + 24)
        } else if (s.actual) {
          doc.setFont('helvetica', 'normal'); doc.setFontSize(4.6); doc.setTextColor(GREY_KIND)
          doc.text('actual sales', x + 1.5, y + 24)
        }
      } else if (cell.date) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5); doc.setTextColor(GREY_KIND)
        doc.text('no service', x + 1.5, y + 11)
      }
    })
    // week total column
    const tx = M + 7 * colW
    doc.setFillColor(CREAM); doc.rect(tx, y, colW, rowH, 'F'); doc.setDrawColor(RULE_LIGHT); doc.rect(tx, y, colW, rowH, 'S')
    if (wk.total) {
      const t = wk.total
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(BRAND); doc.text(wk.label, tx + 1.5, y + 6)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(5); doc.setTextColor(MUTED)
      doc.text(`${t.services} svc`, tx + 1.5, y + 9)
      doc.text(`${num0(t.pax)} pax`, tx + 1.5, y + 12)
      doc.setTextColor(INK); doc.text(`rev ${gbpK(t.revenue)}`, tx + 1.5, y + 15)
      doc.setTextColor(MUTED); doc.text(`cost ${gbpK(t.costs)}`, tx + 1.5, y + 18)
      doc.setFont('helvetica', 'bold'); doc.setTextColor(t.profit >= 0 ? POS : NEG); doc.text(`profit ${kLabel(t.profit)}`, tx + 1.5, y + 21)
    }
    y += rowH
  }
  doc.setTextColor(INK)
  return y + 2
}

/** The on-screen "Working turns" panel, rebuilt for the page: each working with
 *  its set, loco, a capacity bar, and the capacity spread
 *  `originating+return / capacity · load% (out% / ret%)`, split Northbound /
 *  Southbound — exactly the build-tab panel (Chris, 2026-08-27). Returns the y
 *  after the panel. Draws its own page break if it will not fit where asked. */
export function workingTurnsPanel(c: Ctx, side: ExecSide, kicker: string, y0: number, opts: { title: string; subtitle?: string }): number {
  const { doc, d } = c
  const net = d.net
  const stn = (i: number) => net.stations[i]?.name ?? net.stations[i]?.tt ?? '?'
  const isNorth = (x: Departure) => x.d > x.o
  const works = side.deps.filter((x) => x.enabled).sort((a, b) => a.minutes - b.minutes)
  const north = works.filter(isNorth)
  const south = works.filter((x) => !isNorth(x))
  const rowsHigh = Math.max(north.length, south.length)
  const needed = 12 + rowsHigh * 12
  let y = y0
  if (y + needed > FOOT_Y) { newPage(c); y = pageStart(c, kicker, opts.title, 18) }
  else y = sectionLabel(c, opts.title, y + 1)
  y = para(c, opts.subtitle ?? 'Each working: departure time and route, then set · loco. The bar is seats filled; the figures are originating + return passengers / capacity · load% (originating% / return%). ECS = empty-stock positioning move.', y, 6.6, { color: MUTED })
  const halfW = (CW - 8) / 2
  const drawCol = (items: Departure[], x: number, title: string): number => {
    let cy = y + 1
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(BRAND); doc.text(title, x, cy); cy += 4.5
    if (items.length === 0) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(GREY_KIND); doc.text('no workings this direction', x, cy); return cy + 4
    }
    for (const w of items) {
      const r = side.scenario.perDep.find((q) => q.id === w.id)
      const set = side.setByDep?.[w.id] || w.setCode || '—'
      const loco = side.locoByDep?.[w.id]?.label || w.locoLabel || '—'
      const cap = r?.capacity ?? w.capacity ?? 0
      const out = r ? Math.round(r.outAlloc) : 0
      const ret = r ? Math.round(r.retAlloc) : 0
      const alloc = r ? Math.round(r.allocated) : 0
      const util = cap > 0 ? Math.round((alloc / cap) * 100) : 0
      const outPct = alloc > 0 ? Math.round((out / alloc) * 100) : 0
      const retPct = alloc > 0 ? 100 - outPct : 0
      // line 1: time + the route (origin to destination — Chris, 2026-08-27)
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6.8); doc.setTextColor(w.positioning ? MUTED : INK)
      doc.text(w.time, x, cy)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.4); doc.setTextColor(w.positioning ? MUTED : INK)
      doc.text(`${stn(w.o)} to ${stn(w.d)}${w.positioning ? ' (empty stock)' : ''}`, x + 11, cy)
      // line 2: set · loco
      cy += 3.1
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.2); doc.setTextColor(MUTED)
      doc.text(`${set} · ${cap} seats · ${loco}`, x, cy)
      // line 3: capacity bar + the spread figures
      cy += 3.1
      const barW = 22, barH = 2
      doc.setFillColor('#e6e2d6'); doc.rect(x, cy - 1.7, barW, barH, 'F')
      const fillW = Math.max(0, Math.min(1, util / 100)) * barW
      if (fillW > 0 && !w.positioning) { doc.setFillColor(retPct > outPct ? NEG : POS); doc.rect(x, cy - 1.7, fillW, barH, 'F') }
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(MUTED)
      const fig = w.positioning ? 'positioning · 0%' : `${out}+${ret}/${cap} · ${util}% (${outPct}/${retPct})`
      doc.text(fig, x + barW + 2.5, cy)
      cy += 4.8
    }
    return cy
  }
  const yN = drawCol(north, M, 'Northbound')
  const yS = drawCol(south, M + halfW + 8, 'Southbound')
  doc.setTextColor(INK)
  return Math.max(yN, yS) + 4
}

/** One month's Weekly Service Schedule block: optimised grid then today's. */
export function wssMonthSection(c: Ctx, m: ExecWssMonth, kicker: string, intro?: string, exampleDay?: ExecExampleDay | null, sides?: { proposed: ExecSide; current: ExecSide } | null): void {
  const { doc } = c
  let y = pageStart(c, kicker, `Weekly service schedule — ${m.label}`, 18)
  if (intro) y = para(c, intro, y, 8)
  // The costs assigned to the picked example day, line by line and pro-rated to
  // that one day (Chris asked for this repeatedly, at the start of this section):
  // exactly how the day's cost total is made up, element by element.
  if (exampleDay && exampleDay.costLines.length > 0) {
    const ex = exampleDay
    const dayTotal = ex.costLines.reduce((t, l) => t + l.amount, 0)
    y = sectionLabel(c, `The costs assigned to the example day — ${ex.label}`, y + 1)
    y = para(c, `Every cost element the model charged to this one running day, pro-rated to the day. The lines total £${num0(Math.round(dayTotal))} — the cost assigned to this example day.`, y, 7.5)
    const clBody = ex.costLines.map((l) => [l.label, tidyBasis(l.detail ?? ''), gbp0(l.amount)])
    clBody.push(['Total for the day', '', gbp0(dayTotal)])
    y = baseTable(c, {
      startY: y, head: [[{ content: 'COST LINE', styles: { halign: 'left' } }, { content: 'BASIS', styles: { halign: 'left' } }, { content: 'THIS DAY', styles: { halign: 'right' } }]],
      body: clBody,
      styles: { fontSize: 7, cellPadding: { top: 1.5, bottom: 1.5, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 46 }, 1: { textColor: MUTED }, 2: { halign: 'right' } },
      didParseCell: totalsFill(clBody.length - 1), didDrawCell: totalsRule(c, clBody.length - 1),
    }) + 6
    if (y > FOOT_Y - 70) { newPage(c); y = pageStart(c, kicker, `Weekly service schedule — ${m.label} (continued)`, 18) }
  }
  const fcNote = m.firecon && m.firecon.dates > 0
    ? ` "F" marks a FIRECON day - steam stood down for fire risk, the timetable run diesel only (an oil-fired steam engine may still run): ${m.firecon.dates} such day${m.firecon.dates === 1 ? '' : 's'} this month, on the proposed side. Sales against the like day: ${m.firecon.reductions.map((r, i) => `day ${i + 1} ${r > 0 ? `-${r}%` : 'as normal'}`).join(', ')}${m.firecon.reductions.length > 0 ? ', later days as the last' : ''}.`
    : ''
  y = para(c, `Each date shows ITS OWN figures, day for day: the optimised side is the designed timetable valued on its like-for-like basis day's ACTUAL demand; the unoptimised side's passengers and takings are that day's ACTUAL sales${m.basis ? ` from ${m.basis}` : ' from the sales-basis season'}${m.unmatchedDates && m.unmatchedDates > 0 ? ` (${m.unmatchedDates} day${m.unmatchedDates === 1 ? '' : 's'} had no like-for-like basis day and show the day-type average)` : ''}. Passengers are admissions - people, counted once. A date whose like day sold no timed departure ran NO standard services (a gala, an event, or not running): both sides show 0 services and the fixed cost only${m.noServiceDates && m.noServiceDates > 0 ? ` (${m.noServiceDates} such date${m.noServiceDates === 1 ? '' : 's'} this month)` : ''}. "P" marks Pullman dining trafficking through that day - its covers and takings sit outside the daily-services P&L.${m.pullman ? ` Pullman this month: ${m.pullman.workings} service${m.pullman.workings === 1 ? '' : 's'} (${m.pullman.description ?? 'out and back'}), ${m.pullman.conflicts === 0 ? 'no crossing conflicts with the design' : `${m.pullman.conflicts} crossing conflict(s) with the design - adjust`}.` : ''}${fcNote}`, y, 7)
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(BRAND)
  doc.text('OPTIMISED — the proposed timetable', M, y + 2); y += 5
  y = wssGrid(c, m.optimised, y)
  if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, kicker, `Weekly service schedule — ${m.label} (continued)`, 18) }
  else y += 3
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(MUTED)
  doc.text(`UNOPTIMISED — what we run today${m.basis ? ` (actual sales: ${m.basis})` : ''}`, M, y + 2); y += 5
  y = wssGrid(c, m.unoptimised, y)
  // The working turns and capacity spread for this month, BOTH sides (Chris,
  // 2026-08-27): the actual departures behind the grid above, with their sets,
  // locos and how full each runs — proposed, then what we run today.
  if (sides) {
    newPage(c)
    y = pageStart(c, kicker, `Working turns and capacity spread — ${m.label}`, 18)
    y = workingTurnsPanel(c, sides.proposed, kicker, y, { title: 'Optimised — the proposed timetable' })
    y = workingTurnsPanel(c, sides.current, kicker, y + 2, { title: 'Unoptimised — what we run today' })
  }
  // Where the Pullman runs through this month's timetable (Chris, 2026-09-04):
  // its own path in the working timetable, one service = out and back.
  if (m.pullman && m.pullman.legs && m.pullman.legs.length > 0) {
    if (y > FOOT_Y - 40) { newPage(c); y = pageStart(c, kicker, `Working turns and capacity spread — ${m.label} (continued)`, 18) }
    y = sectionLabel(c, 'Pullman dining — where it runs through this timetable', y + 3)
    y = para(c, `${m.pullman.workings} service${m.pullman.workings === 1 ? '' : 's'} (${m.pullman.description ?? 'out and back'}) on the Pullman's own path in the working timetable, shown against the standard services above. It runs on ${m.pullman.dates ?? 0} date${m.pullman.dates === 1 ? '' : 's'} this month on the like-day programme (the "P" markers on the schedule grid carry its covers); its covers and takings sit outside the daily-services P&L.`, y, 7)
    y = baseTable(c, {
      startY: y,
      head: [[{ content: 'TIMETABLE', styles: { halign: 'left' } }, { content: 'DEP', styles: { halign: 'left' } }, { content: 'FROM', styles: { halign: 'left' } }, { content: 'TO', styles: { halign: 'left' } }, { content: 'ARR', styles: { halign: 'left' } }]],
      body: m.pullman.legs.map((l) => [l.colour, l.time, l.from, l.to, l.arr]),
      styles: { fontSize: 7, cellPadding: { top: 1.4, bottom: 1.4, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold', textColor: '#6B4FA0' }, 1: { fontStyle: 'bold' } },
    }) + 4
  }
  newPage(c)
}

/** The fleet, month by month (Chris, 2026-09-11): which engines are out and why
 *  (washout weeks, exams, certificates, active dates), the fewest engines of each
 *  traction available on any running day, and what that did to the proposed
 *  timetable — steam turns run diesel for want of a steam engine, turns with no
 *  engine at all. Modelled side; today's side is what the railway actually ran. */
export function fleetAvailabilitySection(c: Ctx, months: ExecFleetMonth[]): void {
  let y = pageStart(c, 'Operational reality and risk', 'The fleet, month by month', 18)
  y = para(c, `The rolling-stock register (Cost model stage) carries each engine's washout dates (an engine is out for the week - cooling down, washing out, firing up), its exams and repairs, its boiler certificate and its active dates. The build takes them day by day: an engine out that day is not in the fleet; a steam turn no steam engine can take runs DIESEL (the railway swaps a diesel in); a turn no engine can take is reported here and on the schedule. "Available" is the fewest engines of that traction on any running day of the month.`, y, 8)
  const body = months.map((m) => [
    m.label, String(m.days),
    m.steamMin == null ? '-' : String(m.steamMin), m.dieselMin == null ? '-' : String(m.dieselMin),
    m.outDays.length === 0 ? 'none' : m.outDays.map((o) => `${o.label}: ${o.days} day${o.days === 1 ? '' : 's'} (${o.reasons.join('; ')})`).join('\n'),
    m.swapped === 0 ? '-' : String(m.swapped), (m.failureDays ?? 0) === 0 ? '-' : String(m.failureDays),
  ])
  y = baseTable(c, {
    startY: y + 1,
    head: [[{ content: 'MONTH', styles: { halign: 'left' } }, { content: 'DAYS', styles: { halign: 'right' } }, { content: 'STEAM AVAILABLE', styles: { halign: 'right' } }, { content: 'DIESEL AVAILABLE', styles: { halign: 'right' } }, { content: 'ENGINES OUT', styles: { halign: 'left' } }, { content: 'STEAM TURNS RUN DIESEL', styles: { halign: 'right' } }, { content: 'FAILURE DAYS', styles: { halign: 'right' } }]],
    body,
    styles: { fontSize: 6.6, cellPadding: { top: 1.2, bottom: 1.2, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 22 }, 1: { halign: 'right', cellWidth: 12 }, 2: { halign: 'right', cellWidth: 20 }, 3: { halign: 'right', cellWidth: 20 }, 4: { textColor: MUTED }, 5: { halign: 'right', cellWidth: 22 }, 6: { halign: 'right', cellWidth: 22, fontStyle: 'bold' } },
    didParseCell: hooks((dta) => {
      if (dta.section !== 'body' || dta.column.index !== 6) return
      if ((dta.cell.text[0] ?? '-') !== '-') dta.cell.styles.textColor = NEG
    }),
  }) + 6
  // Each engine's season on the roster: paid-for miles used in blocks, interleaved.
  const eng = c.d.fleetEngines ?? []
  if (eng.length > 0) {
    if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, 'Operational reality and risk', 'The fleet, month by month (continued)', 18) }
    y = sectionLabel(c, 'Each engine over the season - the roster', y + 1)
    y = para(c, `Which engine works which turn is decided over the whole season: an engine with a contracted minimum is used first, its paid-for miles spread over the running days it is available and run in blocks of about a week, the minimum-contract engines interleaving; then the owned engines; then the cheapest contract steam for the day; then a diesel. A failure day has no engine of any traction for a turn and is valued as a non-running day. "Blocks" is how many separate runs of days the engine worked - fewer means longer spells in steam.`, y, 7)
    const eb = eng.map((e) => [e.label, e.traction, e.minimum == null ? '-' : num0(e.minimum), num0(Math.round(e.miles)), e.minimum ? `${Math.round((e.miles / e.minimum) * 100)}%` : '-', String(e.days), String(e.blocks)])
    baseTable(c, {
      startY: y,
      head: [[{ content: 'ENGINE', styles: { halign: 'left' } }, { content: 'TRACTION', styles: { halign: 'left' } }, { content: 'CONTRACTED MINIMUM', styles: { halign: 'right' } }, { content: 'MILES ROSTERED', styles: { halign: 'right' } }, { content: 'OF MINIMUM', styles: { halign: 'right' } }, { content: 'DAYS', styles: { halign: 'right' } }, { content: 'BLOCKS', styles: { halign: 'right' } }]],
      body: eb,
      styles: { fontSize: 6.6, cellPadding: { top: 1.2, bottom: 1.2, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 }, 1: { textColor: MUTED, cellWidth: 20 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    })
  }
  newPage(c)
}

/** One month's cost table, before and after, each line itemised (appendix). */
/** One month's cost on the Management P&L, today against the proposal — the
 *  season table's own figures for the month (YearComparePanel.ts::monthFigures,
 *  the one reader of a month's money), fixed and variable apart. */
export interface ExecMonthPnlCosts { label: string; runningDays: number; cur: MonthFigures | null; mod: MonthFigures | null }
export function monthCostsSection(c: Ctx, m: ExecMonthPnlCosts, kicker: string): void {
  let y = pageStart(c, kicker, `Monthly costs — ${m.label}`, 18)
  y = para(c, `The month's cost on the Management P&L, today against the proposal, over its ${m.runningDays} running days. FIXED is the month's own fixed lines — the line runs all year, so they are charged whether or not trains run; VARIABLE is the trains' cost at the basis season's rates: per running day, per working turn, per train-mile, per passenger. These are the season table's figures for this month.`, y, 8)
  const days = Math.max(1, m.runningDays)
  const f = (v: number | null | undefined) => (v == null ? '—' : gbp0(v))
  const chg = (a: number | null | undefined, b: number | null | undefined) => (a == null || b == null ? '—' : signed(b - a))
  const body = [
    ['Fixed lines (the month)', f(m.cur?.fixed), f(m.mod?.fixed), chg(m.cur?.fixed, m.mod?.fixed)],
    ['Variable lines (the trains)', f(m.cur?.variable), f(m.mod?.variable), chg(m.cur?.variable, m.mod?.variable)],
    ['Total for the month', f(m.cur?.costs), f(m.mod?.costs), chg(m.cur?.costs, m.mod?.costs)],
    ['Per running day', m.cur ? gbp0(m.cur.costs / days) : '—', m.mod ? gbp0(m.mod.costs / days) : '—', m.cur && m.mod ? signed((m.mod.costs - m.cur.costs) / days) : '—'],
  ]
  baseTable(c, {
    startY: y + 1,
    head: [[{ content: 'COST', styles: { halign: 'left' } }, { content: 'TODAY', styles: { halign: 'right' } }, { content: 'PROPOSED', styles: { halign: 'right' } }, { content: 'CHANGE', styles: { halign: 'right' } }]],
    body,
    columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    didParseCell: hooks((dta) => {
      if (dta.section !== 'body' || dta.column.index !== 3) return
      const t = dta.cell.text[0] ?? ''
      if (t.startsWith('-')) { dta.cell.styles.textColor = POS; dta.cell.styles.fontStyle = 'bold' }
      else if (t.startsWith('+')) { dta.cell.styles.textColor = NEG; dta.cell.styles.fontStyle = 'bold' }
    }, totalsFill(2)),
    didDrawCell: totalsRule(c, 2),
  })
  newPage(c)
}

/** The rest of one month's appendix block: now/after, a day in the life,
 *  service by service (with loads), can‑we‑run, the working timetable, the
 *  running diagrams and the month's public poster. Reuses the report's own
 *  renderers so each month reads exactly like the whole‑season versions. */
export async function appendixMonthDetail(c: Ctx, am: ExecAppendixMonth): Promise<void> {
  const { doc } = c
  const net = c.d.net
  const p = am.proposed
  const kick = `Appendix · ${am.label}`
  const halfW = (CW - 6) / 2

  // Now and after — demand vs seats, this month.
  let y = pageStart(c, kick, `Demand and seats — ${am.label}`, 18)
  y = para(c, `The ${am.dayClassLabel.toLowerCase()} demand for ${am.label}, against the seats each timetable provides.`, y, 7)
  const fakeD = { demandProfile: am.demandProfile, current: am.current, proposed: am.proposed, net } as unknown as ExecReportData
  await chart(c, chartSeatsPanel(fakeD, 'current', 260, 170, `Today — ${am.label}`), M, y, halfW, 56)
  await chart(c, chartSeatsPanel(fakeD, 'proposed', 260, 170, `Proposed — ${am.label}`), M + halfW + 6, y, halfW, 56)
  // The proposed timetable's working turns and how full each runs, on the same
  // page as demand & seats, specific to this month (Chris, 2026-08-27).
  workingTurnsPanel(c, am.proposed, kick, y + 60, { title: 'Typical working turns and capacity spread' })
  newPage(c)

  // A day in the life — the representative day, both sides.
  y = pageStart(c, kick, `A day in the life — ${am.label}, ${am.dayClassLabel.toLowerCase()}`, 18)
  const propRows = dayRowsFor(c, am.proposed)
  const curRows = dayRowsFor(c, am.current)
  const yP = dayScheduleTable(c, M, halfW, y, BRAND, GOLD, 'THE PROPOSED DAY', `${propRows.filter((r) => !r.ecs).length} workings`, propRows)
  const yC = dayScheduleTable(c, M + halfW + 6, halfW, y, MUTED, '#e6e2d6', 'THE SAME DAY TODAY', `${curRows.filter((r) => !r.ecs).length} workings`, curRows)
  void Math.max(yP, yC)
  newPage(c)

  // Service by service (with loads), current vs proposed.
  y = pageStart(c, kick, `Service by service — ${am.label}`, 18)
  for (const [side, label] of [[am.current, 'Current (as drawn)'], [p, 'Proposed']] as const) {
    const rows = side.deps.filter((x) => x.enabled).sort((a, b) => a.minutes - b.minutes).map((x) => {
      const r = side.scenario.perDep.find((q) => q.id === x.id)
      const cap = r?.capacity ?? 0
      const load = cap > 0 && r ? Math.round((r.allocated / cap) * 100) : 0
      return [x.time, `${net.stations[x.o]?.tt ?? '?'}-${net.stations[x.d]?.tt ?? '?'}`, x.positioning ? 'ECS' : (r ? num0(r.allocated) : '0'), String(cap), x.positioning ? '—' : `${load}%`, x.positioning || !r ? '—' : gbp0(r.outRevenue)]
    })
    if (rows.length === 0) continue
    if (y > FOOT_Y - 40) { newPage(c); y = pageStart(c, kick, `Service by service — ${am.label} (continued)`, 18) }
    y = sectionLabel(c, label, y)
    y = baseTable(c, {
      startY: y,
      head: [[{ content: 'DEP', styles: { halign: 'left' } }, { content: 'ROUTE', styles: { halign: 'left' } }, { content: 'CARRIED', styles: { halign: 'right' } }, { content: 'SEATS', styles: { halign: 'right' } }, { content: 'LOAD', styles: { halign: 'right' } }, { content: 'REVENUE (OUT)', styles: { halign: 'right' } }]],
      body: rows,
      styles: { fontSize: 7, cellPadding: { top: 1.4, bottom: 1.4, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    }) + 6
  }
  newPage(c)

  // Can we actually run it — the constraints, this month.
  y = pageStart(c, kick, `Can we run it — ${am.label}`, 18)
  const checks = [
    { label: 'Nobody left behind', ok: p.scenario.unreturned <= 0.5, detail: `${num0(p.scenario.unreturned)} unreturned` },
    { label: 'Within the fleet', ok: p.costs.overCap.length === 0, detail: p.costs.overCap.length === 0 ? 'diagrams fit the engines' : p.costs.overCap.join('; ') },
    { label: 'Within the path budget', ok: !p.paths.some((x) => x.over), detail: p.paths.length === 0 ? 'no external cap' : p.paths.map((x) => `${x.used}/${x.available}`).join(', ') },
    { label: 'Single-line crossings', ok: p.conflicts.filter((x) => !x.resolvable).length === 0, detail: `${p.conflicts.filter((x) => !x.resolvable).length} non-workable` },
    { label: 'Stock balance', ok: p.imbalances.length === 0, detail: p.imbalances.length === 0 ? 'every rake stables' : `${p.imbalances.length} unresolved` },
  ]
  for (const k of checks) {
    drawChip(c, M, y + 2, k.ok ? 'PASS' : 'FAIL', k.ok ? POS : NEG, '#ffffff')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(INK); doc.text(k.label, M + 14, y + 3.2)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(MUTED); doc.text(k.detail, M + 70, y + 3.2)
    y += 9
  }
  newPage(c)

  // Working timetable — the month's proposed patterns.
  if (am.wtt.length > 0) {
    y = pageStart(c, kick, `Proposed working timetable — ${am.label}`, 18)
    for (const pat of am.wtt) {
      if (y > FOOT_Y - 70) { newPage(c); y = pageStart(c, kick, `Proposed working timetable — ${am.label} (continued)`, 18) }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(BRAND); doc.text(pat.name, M, y); y += 4
      if (pat.N.length > 0) { y = sectionLabel(c, 'Northbound', y); y = wttTable(c, pat.N, 'N', y, 'req/pass') + 4 }
      if (pat.S.length > 0) { y = sectionLabel(c, 'Southbound', y); y = wttTable(c, pat.S, 'S', y, 'req/pass') + 4 }
      y += 3
    }
    newPage(c)
  }

  // Running diagrams — the month's proposed patterns. planWttAppendix does not
  // end on a fresh page, so break before the next section.
  if (am.diagrams.length > 0) { planWttAppendix(c, am.diagrams); newPage(c) }

  // The proposed season as the public would see it — this month's calendar and
  // the patterns that run in it. (The full public times are the working
  // timetable above; the season-wide poster is in the body.)
  if (am.posterMonth) {
    y = pageStart(c, kick, `The public season — ${am.label}`, 18)
    y = para(c, `The days ${am.label} runs, coloured by their timetable pattern; the pattern key is below.`, y, 7) + 2
    const DOWH = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
    const cw = Math.min(24, (CW) / 7)
    const cellH = 12
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(MUTED)
    DOWH.forEach((dw, i) => doc.text(dw, M + i * cw + 2, y + 3))
    y += 5
    const cells = am.posterMonth.cells
    for (let w = 0; w * 7 < cells.length; w++) {
      const row = cells.slice(w * 7, w * 7 + 7)
      row.forEach((cell, i) => {
        const x = M + i * cw
        doc.setDrawColor(RULE_LIGHT); doc.setLineWidth(0.2); doc.rect(x, y, cw, cellH, 'S')
        if (!cell) return
        if (cell.colour) { doc.setFillColor(cell.colour); doc.rect(x, y, cw, 3, 'F') }
        else if (cell.special) { doc.setFillColor(GOLD); doc.rect(x, y, cw, 3, 'F') }
        doc.setFont('helvetica', cell.colour ? 'bold' : 'normal'); doc.setFontSize(7); doc.setTextColor(cell.colour ? INK : GREY_KIND)
        doc.text(String(cell.day), x + 2, y + 8)
      })
      y += cellH
    }
    y += 4
    y = sectionLabel(c, 'Patterns running this month', y)
    for (const pat of am.posterPatterns) {
      doc.setFillColor(pat.colour); doc.circle(M + 2, y - 1, 1.4, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(INK)
      doc.text(`${pat.name}`, M + 6, y)
      doc.setFont('helvetica', 'normal'); doc.setTextColor(MUTED)
      doc.text(`${pat.workingsPerDay} workings/day · ${pat.daysUsed} day${pat.daysUsed === 1 ? '' : 's'}`, M + 36, y)
      y += 5
    }
    newPage(c)
  }
}

// ---------------------------------------------------------------------------
//  Public entry point
// ---------------------------------------------------------------------------

export async function buildExecReport(d: ExecReportData): Promise<void> {
  const [{ jsPDF: JsPDF }, autoTableMod, svgMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    import('svg2pdf.js'),
  ])
  const autoTable = (autoTableMod as unknown as { default: Ctx['autoTable'] }).default
  const svg2pdf = (svgMod as unknown as { svg2pdf: Ctx['svg2pdf'] }).svg2pdf

  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:900px;height:600px;'
  document.body.appendChild(host)

  // WinAnsi-only fonts: scrub data-borne strings before anything prints.
  sanitiseWinAnsi(d)

  const c: Ctx = { doc, autoTable, svg2pdf, host, d }
  const propContrib = d.proposed.periodRevenue - d.proposed.periodCosts
  const curContrib = d.current.periodRevenue - d.current.periodCosts
  const delta = propContrib - curContrib
  const annual = delta * d.periodsPerSeason
  const err = d.backtest ? Math.max(0.1, d.backtest.mape) : 0.25
  const lo = annual * (1 - err)
  const hi = annual * (1 + err)
  const modeLabel = d.net.mode === 'grosmont' ? 'Connect at Grosmont' : 'Through running'
  const PERIOD = d.period.toUpperCase()
  // The proposed side is read by several sections and every appendix; hoisted
  // so reordering the sections (Chris, 2026-08-26) can never strand it.
  const p = d.proposed

  try {
    // ================= PAGE 1 — THE RECOMMENDATION ========================
    // The decision must be readable in ten seconds (Design spec rule one):
    // a filled deep-green panel, the uplift at 40pt gold, tiles with a clear
    // value / was / delta hierarchy, the two lists as labelled columns.
    // Report A (Chris, 2026-08-27) opens on the executive summary itself, not
    // on this cover: its four tiles read the LIVE slice (d.proposed/d.current),
    // which is empty for a designed season, so they printed as £0. Report B
    // (the original month/scenario paper) still leads with it.
    // `y` and `tileW` are shared with later sections (the situation-today stat
    // strip and the Report B appendix), so they live at function scope — not
    // inside the cover block that Report A skips.
    let y = 0
    const tileW = (CW - 9) / 4
    if (d.variant !== 'A') {
    const s = d.seasonSummary
    y = pageStart(c, 'North Yorkshire Moors Railway · Executive proposal', `The ${d.season} season operating plan`, 26)

    const seasonDelta = s ? s.profitProp - s.profitCur : delta
    const py = y + 2
    const panelH = 52
    doc.setFillColor(BRAND)
    doc.roundedRect(M, py, CW, panelH, 2, 2, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(GOLD)
    doc.text('RECOMMENDATION', M + 8, py + 9, { charSpace: 0.4 })
    doc.setFontSize(15); doc.setTextColor('#ffffff')
    doc.text(
      s
        ? (seasonDelta >= 0 ? `Adopt the proposed ${d.season} season timetable` : `Do not adopt the proposed ${d.season} season timetable`)
        : (delta >= 0 ? `Adopt the ${modeLabel.toLowerCase()} timetable for ${d.monthLabel}` : `Do not adopt the ${modeLabel.toLowerCase()} timetable for ${d.monthLabel}`),
      M + 8, py + 17
    )
    const heroV = s ? seasonDelta : annual
    doc.setFontSize(40); doc.setTextColor(GOLD)
    const hero = signed(heroV)
    doc.text(hero, M + 8, py + 32)
    const heroW = doc.getTextWidth(hero)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor('#dfe9e3')
    const subline = s
      ? `season profit ${seasonDelta >= 0 ? 'uplift' : 'reduction'} across ${s.days} running days`
      : (delta >= 0
        ? `estimated ${gbp0(lo)} to ${gbp0(hi)} a season in additional contribution`
        : `estimated ${gbp0(Math.abs(hi))} to ${gbp0(Math.abs(lo))} a season in reduced contribution`)
    doc.text(doc.splitTextToSize(subline, CW - heroW - 24) as string[], M + 8 + heroW + 6, py + 27.5)
    doc.setDrawColor(PANEL_RULE); doc.setLineWidth(0.2)
    doc.line(M + 8, py + 38, PW - M - 8, py + 38)
    // three proof stats: bold white figures in a #bcd0c5 line
    const proof: { v: string; label: string; vFirst: boolean }[] = s
      ? [
        { v: num0(s.turnsCur - s.turnsProp), label: ' fewer hauled workings', vFirst: true },
        { v: `${Math.round((s.carriedProp / Math.max(1, s.carriedCur)) * 100)}%`, label: " of today's passengers still carried", vFirst: true },
        { v: 'unchanged', label: 'fares and steam haulage ', vFirst: false },
      ]
      : [
        { v: String(d.proposed.deps.filter((x) => x.enabled && !x.positioning).length), label: ` workings against ${d.current.deps.filter((x) => x.enabled && !x.positioning).length} today`, vFirst: true },
        { v: `${Math.round((d.proposed.scenario.carried / Math.max(1, d.current.scenario.carried)) * 100)}%`, label: " of today's passengers still carried", vFirst: true },
        { v: 'unchanged', label: 'fares and steam haulage ', vFirst: false },
      ]
    let px = M + 8
    doc.setFontSize(8.5)
    for (const pstat of proof) {
      if (pstat.vFirst) {
        doc.setFont('helvetica', 'bold'); doc.setTextColor('#ffffff')
        doc.text(pstat.v, px, py + 45)
        px += doc.getTextWidth(pstat.v)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(HEADER_SUB)
        doc.text(pstat.label, px, py + 45)
        px += doc.getTextWidth(pstat.label) + 9
      } else {
        doc.setFont('helvetica', 'normal'); doc.setTextColor(HEADER_SUB)
        doc.text(pstat.label, px, py + 45)
        px += doc.getTextWidth(pstat.label)
        doc.setFont('helvetica', 'bold'); doc.setTextColor('#ffffff')
        doc.text(pstat.v, px, py + 45)
        px += doc.getTextWidth(pstat.v) + 9
      }
    }
    doc.setTextColor(INK)
    // model-credibility line under the panel
    const cred = d.backtest
      ? `Every day is modelled on its own timetable, sales and capacity; the model reproduces last season's revenue to within ±${(err * 100).toFixed(0)}%.` +
        (d.net.mode === 'grosmont' ? ' The interchange behaviour is a counterfactual and its parameters are assumptions — Appendix D states each one.' : '')
      : 'The model has NOT been calibrated against actual revenue for this scenario — treat the figures as a ranking of options, not a forecast.'
    y = para(c, cred, py + panelH + 5, 7, { color: MUTED })

    // four stat tiles: value / was / delta (gold top bar) — tileW is hoisted above
    const revD = d.proposed.periodRevenue - d.current.periodRevenue
    const costD = d.proposed.periodCosts - d.current.periodCosts
    const paxD = d.proposed.scenario.carried - d.current.scenario.carried
    const tiles = [
      { label: `REVENUE / ${PERIOD}`, value: gbp0(d.proposed.periodRevenue), was: `today ${gbp0(d.current.periodRevenue)}`,
        delta: Math.abs(revD) < 1 ? 'unchanged' : signed(revD), ink: Math.abs(revD) < 1 ? MUTED : revD > 0 ? POS : NEG },
      { label: `RUNNING COSTS / ${PERIOD}`, value: gbp0(d.proposed.periodCosts), was: `today ${gbp0(d.current.periodCosts)}`,
        delta: Math.abs(costD) < 1 ? 'unchanged' : costD < 0 ? `${gbp0(Math.abs(costD))} lower` : `${gbp0(costD)} higher`,
        ink: Math.abs(costD) < 1 ? MUTED : costD < 0 ? POS : NEG },
      { label: `CONTRIBUTION / ${PERIOD}`, value: gbp0(propContrib), was: `today ${gbp0(curContrib)}`,
        delta: signed(delta), ink: delta >= 0 ? POS : NEG },
      { label: `PASSENGERS / ${PERIOD}`, value: num0(d.proposed.scenario.carried), was: `today ${num0(d.current.scenario.carried)}`,
        delta: `${paxD >= 0 ? '+' : ''}${num0(paxD)}`, ink: paxD >= 0 ? POS : NEG },
    ]
    const tileY = y + 2
    tiles.forEach((t, i) => {
      const x = M + i * (tileW + 3)
      doc.setFillColor(PAPER); doc.setDrawColor(RULE); doc.setLineWidth(0.25)
      doc.roundedRect(x, tileY, tileW, 30, 1.5, 1.5, 'FD')
      doc.setFillColor(GOLD)
      doc.rect(x, tileY, tileW, 1.2, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(GOLD_TEXT)
      doc.text(t.label, x + 4, tileY + 6.5, { charSpace: 0.25 })
      doc.setFontSize(15); doc.setTextColor(BRAND)
      doc.text(t.value, x + 4, tileY + 14)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(MUTED)
      doc.text(t.was, x + 4, tileY + 20)
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(t.ink)
      doc.text(t.delta, x + 4, tileY + 25.5)
    })
    doc.setTextColor(INK)
    y = tileY + 30 + 8

    // "In this paper" | "What actually changes" — two labelled columns
    const colLW = 82, colRX = M + colLW + 8, colRW = CW - colLW - 8
    let yL = colHeader(c, 'In this paper', M, colLW, y)
    yL = numberedList(c, [
      'The situation today — what the current season costs, earns and loses, month by month.',
      'The proposal — what we would run instead, its costs, and where the money moves.',
      'A day in the life — an example daily service schedule, evidencing the same footfall is still carried.',
      `The public season timetable — the proposed ${d.season} calendar and timetables, as the railway would publish them.`,
      'What we cut, why, and what it saves — every withdrawn working, itemised.',
      'The executive summary and decision, followed by the full operational appendices.',
    ], yL, { x: M, width: colLW, size: 7.5 })
    if (d.whatsChanged.length > 0) {
      let yR = colHeader(c, 'What actually changes', colRX, colRW, y)
      yR = goldBullets(c, d.whatsChanged, yR, { x: colRX, width: colRW, size: 7.5 })
      void yR
    }
    void yL
    newPage(c)
    }

    // ================= THE SITUATION TODAY ================================
    // What the current season costs, earns and loses (Chris, 2026-08-21) —
    // now with the 4-stat hero strip and per-bar value labels (spec p2).
    const secSituation = async () => {
    if (d.yearRows && d.yearRows.length > 0) {
      let y = pageStart(c, 'Where the season stands', 'The situation today')
      const yr = d.yearRows
      const tot = yearTotals(yr)
      const lossMonths = yr.filter((r) => (monthFigures(r.cur, r.runningDays)?.profit ?? 0) < 0).length
      y = standfirst(c,
        `Today the railway runs ${tot.runningDays} days across the season, and the line costs money in all twelve months: a month with no standard services carries its fixed cost and nothing else. ` +
        (tot.cur
          ? 'Most running days run the same intensive timetable regardless of the day\'s demand: the losses below are concentrated exactly where the demand is thinnest.'
          : 'The current side has not been scored yet — open the Season view so every month computes.'),
        y)
      if (tot.cur) {
        const strip = [
          { label: 'SEASON REVENUE', value: gbp0(tot.cur.revenue), ink: BRAND, sub: `${tot.runningDays} running days` },
          { label: 'SEASON COST', value: gbp0(tot.cur.costs), ink: BRAND, sub: `up to ${tot.cur.maxLocos} locomotives` },
          { label: 'SEASON RETURN', value: signed(tot.cur.profit), ink: tot.cur.profit >= 0 ? POS : NEG,
            sub: `a ${((tot.cur.profit / Math.max(1, tot.cur.revenue)) * 100).toFixed(1)}% margin` },
          { label: 'LOSS-MAKING MONTHS', value: `${lossMonths} of ${tot.totalRows}`, ink: lossMonths > 0 ? NEG : POS, sub: 'on their own account' },
        ]
        strip.forEach((st, i) => creamStat(c, M + i * (tileW + 3), tileW, y, st.label, st.value, st.ink, st.sub))
        y += 18 + 5
      }
      const curBody = yr.map((r) => {
        const cur = r.cur, days = r.runningDays
        const f = monthFigures(cur, days)
        if (!cur || !f || days == null) return [r.monthLabel, days != null ? String(days) : '—', '—', '—', '—', '—', '—', '—', '—']
        const runs = cur.services > 0
        return [r.monthLabel, String(days),
          runs ? `${cur.servicesRange ? `${cur.servicesRange[0]}–${cur.servicesRange[1]}` : cur.services}/day · ${cur.steamN}S${cur.dieselN > 0 ? `+${cur.dieselN}D` : ''}` : 'no standard services',
          num0(f.pax), gbp0(f.revenue), f.fixed == null ? '—' : gbp0(f.fixed), f.variable == null ? '—' : gbp0(f.variable), gbp0(f.costs), signed(f.profit)]
      })
      const curTot = ['SEASON', String(tot.runningDays), tot.cur ? `up to ${tot.cur.maxLocos} locos` : '—',
        tot.cur ? num0(tot.cur.carried) : '—', tot.cur ? gbp0(tot.cur.revenue) : '—', tot.cur?.fixed != null ? gbp0(tot.cur.fixed) : '—', tot.cur?.variable != null ? gbp0(tot.cur.variable) : '—', tot.cur ? gbp0(tot.cur.costs) : '—', tot.cur ? signed(tot.cur.profit) : '—']
      y = baseTable(c, {
        startY: y,
        head: [[
          { content: 'MONTH', styles: { halign: 'left' } }, { content: 'DAYS', styles: { halign: 'right' } },
          { content: 'WORKINGS · LOCOS', styles: { halign: 'left' } }, { content: 'CARRIED', styles: { halign: 'right' } },
          { content: 'REVENUE', styles: { halign: 'right' } }, { content: 'FIXED', styles: { halign: 'right' } },
          { content: 'VARIABLE', styles: { halign: 'right' } }, { content: 'COST', styles: { halign: 'right' } },
          { content: 'PROFIT', styles: { halign: 'right' } },
        ]],
        body: [...curBody, curTot],
        styles: { fontSize: 7, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' } },
        didParseCell: hooks(signCols([8]), mutedCols([2]), totalsFill(curBody.length)),
        didDrawCell: totalsRule(c, curBody.length),
      }) + 6
      y = sectionLabel(c, 'Monthly profit and loss — today', y)
      const profRows = yr.map((r) => ({
        label: r.monthLabel.slice(0, 3),
        v: monthFigures(r.cur, r.runningDays)?.profit ?? null,
      }))
      await chart(c, chartProfitBars(profRows, 520, 150), M, y, CW, 51)
      y += 55
      const seasonProfit = tot.cur ? tot.cur.profit : 0
      para(c,
        `${lossMonths} of ${tot.totalRows} months are loss-making on their own account. ` +
        (tot.cur
          ? (seasonProfit >= 0
            ? `The season's whole return of ${gbp0(seasonProfit)} is earned in the peak months and given back in the shoulders.`
            : `Across the season the railway loses ${gbp0(Math.abs(seasonProfit))} at today's timetables.`)
          : ''),
        y, 7, { color: MUTED })
      newPage(c)
    }
    }

    // ================= THE PROPOSED SOLUTION ==============================
    // The mirror of "The situation today" (Chris, 2026-08-21): the same table
    // and season view, with the proposed timetables and costs in it.
    const secProposed = async () => {
    if (d.yearRows && d.yearRows.length > 0 && d.yearRows.some((r) => r.mod)) {
      let y = pageStart(c, 'What we would run instead', 'The proposed solution')
      const yr = d.yearRows
      const tot = yearTotals(yr)
      if (tot.mod && tot.cur) {
        y = richPara(c, [
          { t: 'The proposal keeps every running day and re-plans each one on its own demand: the right number of departures for the day, the right capacity behind them, fares unchanged, steam haulage unchanged. Across the same ' },
          { t: `${tot.runningDays} running days`, b: true },
          { t: ' the proposed season earns ' }, { t: `${gbp0(tot.mod.revenue)},`, b: true },
          { t: ' costs ' }, { t: gbp0(tot.mod.costs), b: true },
          { t: tot.mod.fixed != null && tot.mod.variable != null ? ` (${gbp0(tot.mod.fixed)} fixed, the line all year, + ${gbp0(tot.mod.variable)} variable, the trains)` : '' },
          { t: ' to run and returns ' }, { t: signed(tot.mod.profit), b: true },
          { t: ` — against ${signed(tot.cur.profit)} today — while carrying ` },
          { t: `${Math.round((tot.mod.carried / Math.max(1, tot.cur.carried)) * 100)}%`, b: true },
          { t: ' of the same passengers.' },
        ], y, 10, { width: 168 })
      } else {
        y = standfirst(c, 'The proposal keeps every running day and re-plans each one on its own demand: the right number of departures for the day, the right capacity behind them, fares unchanged, steam haulage unchanged.', y)
      }
      const modBody = yr.map((r) => {
        const mv = r.mod, days = r.runningDays
        const f = monthFigures(mv, days)
        if (!mv || !f || days == null) return [r.monthLabel, days != null ? String(days) : '—', '—', '—', '—', '—', '—', '—', '—']
        const runs = mv.services > 0
        return [r.monthLabel, String(days),
          runs ? `${mv.servicesRange ? `${mv.servicesRange[0]}–${mv.servicesRange[1]}` : mv.services}/day · ${mv.steamN}S${mv.dieselN > 0 ? `+${mv.dieselN}D` : ''}` : 'no standard services',
          num0(f.pax), gbp0(f.revenue), f.fixed == null ? '—' : gbp0(f.fixed), f.variable == null ? '—' : gbp0(f.variable), gbp0(f.costs), signed(f.profit)]
      })
      const modTot = ['SEASON', String(tot.runningDays), tot.mod ? `up to ${tot.mod.maxLocos} locos` : '—',
        tot.mod ? num0(tot.mod.carried) : '—', tot.mod ? gbp0(tot.mod.revenue) : '—', tot.mod?.fixed != null ? gbp0(tot.mod.fixed) : '—', tot.mod?.variable != null ? gbp0(tot.mod.variable) : '—', tot.mod ? gbp0(tot.mod.costs) : '—', tot.mod ? signed(tot.mod.profit) : '—']
      y = baseTable(c, {
        startY: y + 1,
        head: [[
          { content: 'MONTH', styles: { halign: 'left' } }, { content: 'DAYS', styles: { halign: 'right' } },
          { content: 'WORKINGS · LOCOS', styles: { halign: 'left' } }, { content: 'CARRIED', styles: { halign: 'right' } },
          { content: 'REVENUE', styles: { halign: 'right' } }, { content: 'FIXED', styles: { halign: 'right' } },
          { content: 'VARIABLE', styles: { halign: 'right' } }, { content: 'COST', styles: { halign: 'right' } },
          { content: 'PROFIT', styles: { halign: 'right' } },
        ]],
        body: [...modBody, modTot],
        styles: { fontSize: 7, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' } },
        didParseCell: hooks(signCols([8]), mutedCols([2]), totalsFill(modBody.length)),
        didDrawCell: totalsRule(c, modBody.length),
      }) + 6
      y = sectionLabel(c, 'Running cost by month — today (pale) against the proposal (solid)', y)
      const costRows = yr.map((r) => ({
        label: r.monthLabel.slice(0, 3),
        cur: monthFigures(r.cur, r.runningDays)?.costs ?? null,
        mod: monthFigures(r.mod, r.runningDays)?.costs ?? null,
      }))
      await chart(c, chartPairedBars(costRows, 520, 140), M, y, CW, 48)
      y += 52
      para(c, 'The saving is concentrated in the shoulder months, where today\'s intensive timetable runs far ahead of the demand.', y, 7, { color: MUTED })
      newPage(c)
    }
    }

    // ================= THE CASE IN ONE PICTURE ============================
    const secCaseShape = async () => {
    let y = pageStart(c, 'The case in one picture', 'The shape of the problem')
    y = standfirst(c,
      'Passengers do not demand services, they demand journeys at times. Bars are the demand we observed by desired departure time; ' +
      'outlines are the seats each timetable actually provides. Where a bar is taller than its outline we turned people away; where the ' +
      'outline towers over the bar we hauled empty coaches.', y)
    y += 1
    await chart(c, chartDemandVsTimetable(d, 520, 250), M, y, CW, 86)
    y += 90
    // three annotated takeaway cards (spec p4)
    {
      const peak = d.demandProfile.reduce(
        (best, p) => (p.north + p.south > best.v ? { t: p.time, v: p.north + p.south } : best),
        { t: '—', v: -1 }
      )
      const cards = [
        { stat: peak.t, ink: BRAND, text: 'the busiest departure time — the tallest bar, and where seats run short' },
        { stat: 'midday', ink: NEG, text: 'the grey outlines tower over the bars — seats hauled with no one in them' },
        { stat: 'proposed', ink: POS, text: 'the red outlines follow the bars — capacity re-shaped to the demand' },
      ]
      const cw3 = (CW - 8) / 3
      cards.forEach((card, i) => {
        const x = M + i * (cw3 + 4)
        doc.setFillColor(CREAM)
        doc.roundedRect(x, y, cw3, 24, 1.5, 1.5, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(card.ink)
        doc.text(card.stat, x + 5, y + 9.5)
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(INK)
        doc.text(doc.splitTextToSize(card.text, cw3 - 10) as string[], x + 5, y + 14)
      })
      doc.setTextColor(INK)
      y += 28
    }
    newPage(c)
    }

    // NOW and AFTER, side by side (Chris, 2026-08-21: the tab charts belong
    // in the paper) — the same demand against each timetable's seats.
    const secCaseNowAfter = async () => {
    let y = pageStart(c, 'The case in one picture', 'Now and after — the same demand, two timetables')
    {
      const halfW = (CW - 6) / 2
      await chart(c, chartSeatsPanel(d, 'current', 260, 170, 'Now — today\'s timetable'), M, y, halfW, 56)
      await chart(c, chartSeatsPanel(d, 'proposed', 260, 170, 'After — the proposal'), M + halfW + 6, y, halfW, 56)
      y += 62
      await chart(c, chartLoadFactor(d, 520, 110), M, y, CW, 24)
      y += 30
    }
    if (d.censoringNote) {
      doc.setFont('helvetica', 'italic')
      doc.setFontSize(7.5)
      doc.setTextColor(MUTED)
      doc.text(doc.splitTextToSize(d.censoringNote, CW) as string[], M, y)
      doc.setTextColor(INK)
      doc.setFont('helvetica', 'normal')
      y += 12
    }
    newPage(c)
    }

    // ================= THE MONEY =========================================
    const secMoney = async () => {
    let y = pageStart(c, 'The money', 'What it is worth')
    y = sectionLabel(c, `Contribution per ${PERIOD} — how today becomes the proposal (${d.monthLabel}, ${d.dayClassLabel} basis)`, y + 2)
    await chart(c, chartWaterfall(d, 520, 190), M, y, CW, 65)
    y += 70
    if (!d.yearRows || d.yearRows.length === 0) {
      // month-only sessions keep the by-month pairs here; season sessions
      // carry this story on the situation/proposal pages instead.
      if (d.monthly.length > 0) {
        y = sectionLabel(c, 'Contribution by month — today (pale) against the proposal (solid)', y)
        await chart(c, chartPairedBars(d.monthly.map((r) => ({ label: r.label, cur: r.current, mod: r.proposed })), 520, 130), M, y, CW, 44)
        y += 49
      }
    }
    if (d.sensitivity.length > 0) {
      y = sectionLabel(c, `Contribution per ${PERIOD} under alternative assumptions`, y + 2)
      y = sensitivityCard(c, y)
      const dRev = d.proposed.periodRevenue - d.current.periodRevenue
      const dCost = d.current.periodCosts - d.proposed.periodCosts
      const moneyStory = Math.abs(dRev) < 500
        ? `Revenue is held flat by design — the plan is not a fare rise. The whole gain is cost taken out: ${gbp0(dCost)} a ${d.period} of running cost that bought seats nobody was sitting in.`
        : `The change is ${signed(dRev)} of revenue and ${gbp0(dCost)} a ${d.period} of running cost taken out.`
      y = howToReadBox(c, y, 'How to read this page.', `${moneyStory} The sensitivity bars are the honesty check: where a bar crosses the dashed line, that assumption is load-bearing and should be confirmed before committing.`)
    } else {
      y = para(c,
        'No sensitivity runs were recorded for this scenario. Run the Claude tuning pass, or vary the assumptions by hand, before putting a figure in front of a board.',
        y, 8)
    }
    newPage(c)
    }

    // ============ SEASON BY MONTH + THE COSTS TABLE ======================
    // (Chris, 2026-08-20: the year summary and the costs table belong in the
    // PLAN, not only in an appendix.) Split Current/Proposed header bands
    // with a gold divider and a PROFIT CHANGE column (spec p6).
    const secSeasonCosts = async () => {
    let y = pageStart(c, 'The money', 'The season by month, and where the costs sit')
    {
      const yr = d.yearRows ?? []
      if (yr.length > 0) {
        const tot = yearTotals(yr)
        const sideCells = (sv: YearRow['cur'], days: number | null): string[] => {
          const f = monthFigures(sv, days)
          if (!sv || !f || days == null) return ['—', '—', '—', '—']
          const runs = sv.services > 0
          return [runs ? `${sv.services}/day · ${sv.steamN}S${sv.dieselN > 0 ? `+${sv.dieselN}D` : ''}` : 'no standard services',
            f.fixed == null ? '—' : gbp0(f.fixed), f.variable == null ? '—' : gbp0(f.variable), signed(f.profit)]
        }
        const body: string[][] = yr.map((r) => {
          const curCells = sideCells(r.cur, r.runningDays)
          const modCells = sideCells(r.mod, r.runningDays)
          const fc = monthFigures(r.cur, r.runningDays), fm = monthFigures(r.mod, r.runningDays)
          const dv = fc && fm ? signed(fm.profit - fc.profit) : '—'
          return [
            `${r.monthLabel}${r.designed ? '' : ` (${r.statusLabel.toLowerCase()})`}${r.issues && r.issues.length > 0 ? ' *' : ''}`,
            r.runningDays != null ? String(r.runningDays) : '—',
            ...curCells, ...modCells, dv,
          ]
        })
        const briefNotes = yr.filter((r) => r.issues && r.issues.length > 0)
          .map((r) => `* ${r.monthLabel}: ${r.issues!.map((i) => i.text).join('; ')}.`)
        const totCells = (t: typeof tot.cur): string[] => t
          ? [`up to ${t.maxLocos} locos`, t.fixed == null ? '—' : gbp0(t.fixed), t.variable == null ? '—' : gbp0(t.variable), signed(t.profit)]
          : ['—', '—', '—', '—']
        const totRow = ['SEASON', String(tot.runningDays), ...totCells(tot.cur), ...totCells(tot.mod), tot.cur && tot.mod ? signed(tot.mod.profit - tot.cur.profit) : '—']
        const subHead = { fontSize: 6, textColor: '#cfe0d6', fontStyle: 'bold' as const }
        y = baseTable(c, {
          startY: y,
          head: [
            [
              { content: 'MONTH', rowSpan: 2, styles: { halign: 'left', valign: 'bottom' } },
              { content: 'DAYS', rowSpan: 2, styles: { halign: 'right', valign: 'bottom' } },
              { content: 'CURRENT', colSpan: 4, styles: { halign: 'center', fillColor: BRAND_MID } },
              { content: 'PROPOSED', colSpan: 4, styles: { halign: 'center' } },
              { content: 'PROFIT CHANGE', rowSpan: 2, styles: { halign: 'right', valign: 'bottom', textColor: GOLD } },
            ],
            [
              { content: 'TURNS · LOCOS', styles: { ...subHead, halign: 'left', fillColor: BRAND_MID } },
              { content: 'FIXED', styles: { ...subHead, halign: 'right', fillColor: BRAND_MID } },
              { content: 'VARIABLE', styles: { ...subHead, halign: 'right', fillColor: BRAND_MID } },
              { content: 'PROFIT', styles: { ...subHead, halign: 'right', fillColor: BRAND_MID } },
              { content: 'TURNS · LOCOS', styles: { ...subHead, halign: 'left' } },
              { content: 'FIXED', styles: { ...subHead, halign: 'right' } },
              { content: 'VARIABLE', styles: { ...subHead, halign: 'right' } },
              { content: 'PROFIT', styles: { ...subHead, halign: 'right' } },
            ],
          ],
          body: [...body, totRow],
          styles: { fontSize: 6.6, cellPadding: { top: 1.6, bottom: 1.6, left: 1.8, right: 1.8 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
          columnStyles: {
            0: { fontStyle: 'bold' }, 1: { halign: 'right' },
            3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' },
            7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right', fontStyle: 'bold' },
          },
          didParseCell: hooks(signCols([5, 9, 10]), mutedCols([2, 6]), totalsFill(body.length)),
          didDrawCell: hooks(
            totalsRule(c, body.length),
            (dta) => {
              // the gold rules that separate today from the proposal
              if (dta.column.index === 2) {
                doc.setDrawColor(dta.section === 'head' ? '#ffffff' : RULE_LIGHT)
                doc.setLineWidth(0.5)
                doc.line(dta.cell.x, dta.cell.y, dta.cell.x, dta.cell.y + dta.cell.height)
              }
              if (dta.column.index === 5 || dta.column.index === 8) {
                doc.setDrawColor(dta.section === 'head' ? GOLD : '#e9dcbe')
                doc.setLineWidth(dta.section === 'head' ? 0.7 : 0.5)
                doc.line(dta.cell.x, dta.cell.y, dta.cell.x, dta.cell.y + dta.cell.height)
              }
            },
          ),
        }) + 4
        if (briefNotes.length > 0) {
          // Where the Brief bound — every month above is built and valued; these
          // are the points the planner has not settled, in the Brief's own terms.
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6.6); doc.setTextColor(MUTED)
          const noteLines = doc.splitTextToSize(`Where the Scope bound: ${briefNotes.join(' ')}`, CW)
          doc.text(noteLines, M, y)
          y += noteLines.length * 3.1 + 2
        }
        if (tot.mod?.turnedAway != null || tot.cur?.turnedAway != null) {
          // Turned away by timing (Chris, 2026-09-07) — the same season figure the
          // screen's season table and the workbook Summary print (yearTotals).
          const perDay = (v: number) => (tot.runningDays > 0 ? num0(v / tot.runningDays) : '-')
          const ta = `Turned away by timing: ${tot.mod?.turnedAway != null ? `${num0(tot.mod.turnedAway)} passengers over the season on the proposal (${perDay(tot.mod.turnedAway)} a running day)` : 'not yet computed for the proposal'}, against ${tot.cur?.turnedAway != null ? `${num0(tot.cur.turnedAway)} today (${perDay(tot.cur.turnedAway)} a running day)` : 'not yet computed for today'} - people in the like day's demand who did not travel on that side's timetable: no train within 75 minutes of their time once the Recapture discount applies, or the nearest train had no way home. They are not in the passengers above.`
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6.6); doc.setTextColor(MUTED)
          const taLines = doc.splitTextToSize(ta, CW)
          doc.text(taLines, M, y)
          y += taLines.length * 3.1 + 2
        }
        if (tot.coveredRows < tot.totalRows) {
          y = para(c, `Totals cover the ${tot.coveredRows} designed slice${tot.coveredRows === 1 ? '' : 's'} of ${tot.totalRows} — undesigned months contribute nothing (nothing is assumed on their behalf).`, y, 7, { color: MUTED })
        }
        y += 2
      } else {
        y = para(c, 'No season programme yet — design month slices and they roll into this table.', y, 8.5)
      }

      // The costs table — category by category, with the basis stated.
      // Report A demonstrates the per-day cost on a real RUNNING month (August),
      // not on whatever month is selected: March and the low-season months run
      // few or no services, so their per-day cost is a meaningless example
      // (Chris, 2026-08-27). Report B keeps the selected month/scenario.
      const emp = d.exampleMonthPnl
      if (emp && (emp.today.length > 0 || emp.proposed.length > 0)) {
        // ONE cost model in the paper (Chris, 2026-09-04: this table printed the
        // rate card's £1,136/working beside the P&L table). The example month's
        // cost LINE BY LINE on the Management P&L, per running day — exactly the
        // season table's row ÷ its running days.
        y = sectionLabel(c, `Running cost by P&L line — ${emp.label} (example month), £/day`, y + 3)
        const fmtAct = (a: typeof emp.todayActivity) => `${a.services} turns, ${Math.round(a.miles)} train-miles, ${Math.round(a.carriedPerDay)} passengers a day`
        y = para(c, `Every included line of the ${d.costModelPnl?.basis ?? 'basis'} Management P&L, priced for one running day of ${emp.label}: a FIXED line is the month's own figure divided by its ${emp.runningDays} running days; a VARIABLE line is the basis rate applied to the day's activity (today ${fmtAct(emp.todayActivity)}; proposed ${fmtAct(emp.proposedActivity)}). The totals are the season table's row for this month divided by its running days. No rate card is used.`, y, 7, { color: MUTED })
        const labels = [...new Set([...emp.proposed.map((l) => l.label), ...emp.today.map((l) => l.label)])]
        const find = (arr: ExecPnlDayLine[], label: string) => arr.find((l) => l.label === label)
        const rows = labels.map((label) => {
          const t = find(emp.today, label), p = find(emp.proposed, label)
          const a = t?.amount ?? 0, b = p?.amount ?? 0
          const ref = (p ?? t)!
          return [label, ref.kind, tidyBasis(ref.detail), gbp0(a), gbp0(b), signed(b - a)]
        })
        const sum = (arr: ExecPnlDayLine[], kind?: 'fixed' | 'variable') => arr.filter((l) => !kind || l.kind === kind).reduce((t, l) => t + l.amount, 0)
        const sub = (name: string, kind?: 'fixed' | 'variable') => [name, '', '', gbp0(sum(emp.today, kind)), gbp0(sum(emp.proposed, kind)), signed(sum(emp.proposed, kind) - sum(emp.today, kind))]
        const catBody = [...rows, sub("Fixed lines — the day's share of the month", 'fixed'), sub('Variable lines — the trains', 'variable'), sub('Total per operating day')]
        baseTable(c, {
          startY: y,
          head: [[
            { content: 'P&L LINE', styles: { halign: 'left' } },
            { content: 'BEHAVIOUR', styles: { halign: 'left' } },
            { content: 'BASIS (PROPOSED)', styles: { halign: 'left' } },
            { content: 'TODAY £/DAY', styles: { halign: 'right' } },
            { content: 'PROPOSED £/DAY', styles: { halign: 'right' } },
            { content: 'CHANGE', styles: { halign: 'right' } },
          ]],
          body: catBody,
          styles: { fontSize: 6.6, cellPadding: { top: 1.3, bottom: 1.3, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 44 }, 1: { textColor: MUTED, cellWidth: 21 }, 2: { cellWidth: 54 }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
          didParseCell: hooks(
            mutedCols([1, 2]),
            (dta) => { if (dta.section === 'body' && dta.row.index >= catBody.length - 3) { dta.cell.styles.fontStyle = 'bold'; dta.cell.styles.fillColor = CREAM } },
            (dta) => {
              // a cost REDUCTION is good news: minus is green here
              if (dta.section !== 'body' || dta.column.index !== 5) return
              const t = dta.cell.text[0] ?? ''
              if (t.startsWith('-')) { dta.cell.styles.textColor = POS; dta.cell.styles.fontStyle = 'bold' }
              else if (t === '+£0') { dta.cell.styles.textColor = MUTED }
              else if (t.startsWith('+')) { dta.cell.styles.textColor = NEG; dta.cell.styles.fontStyle = 'bold' }
            },
          ),
          didDrawCell: totalsRule(c, catBody.length - 1),
        })
      } else {
      const augAmC = d.variant === 'A' ? (d.appendixMonths ?? []).find((am) => /Aug/i.test(am.label)) : undefined
      const curL = costLines(augAmC ? augAmC.current.costs : d.current.costs, d.rates)
      const propL = costLines(augAmC ? augAmC.proposed.costs : d.proposed.costs, d.rates)
      y = sectionLabel(c, `Running cost by category — ${augAmC ? `${augAmC.label} (example month)` : `${d.monthLabel} ${d.dayClassLabel}`}, £/day`, y + 3)
      if (augAmC) y = para(c, 'August is used as an example month: a full running month whose per-day costs are representative. The low-season months (March among them) run few or no services, so their per-day cost is not a useful example.', y, 7, { color: MUTED })
      const catBody = [
        ...propL.map((pl, i) => {
          const cl = curL[i]
          const a = cl?.amount ?? 0
          const b = pl.amount
          return [pl.label, tidyBasis(pl.detail ?? cl?.detail ?? ''), gbp0(a), gbp0(b), signed(b - a)]
        }),
        ['Total per operating day', '',
          gbp0(curL.reduce((t, l) => t + l.amount, 0)),
          gbp0(propL.reduce((t, l) => t + l.amount, 0)),
          signed(propL.reduce((t, l) => t + l.amount, 0) - curL.reduce((t, l) => t + l.amount, 0))],
      ]
      baseTable(c, {
        startY: y,
        head: [[
          { content: 'COST LINE', styles: { halign: 'left' } },
          { content: 'BASIS (PROPOSED)', styles: { halign: 'left' } },
          { content: 'TODAY £/DAY', styles: { halign: 'right' } },
          { content: 'PROPOSED £/DAY', styles: { halign: 'right' } },
          { content: 'CHANGE', styles: { halign: 'right' } },
        ]],
        body: catBody,
        styles: { fontSize: 7.2, cellPadding: { top: 1.6, bottom: 1.6, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 44 }, 1: { cellWidth: 62 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
        didParseCell: hooks(
          mutedCols([1]),
          totalsFill(catBody.length - 1),
          (dta) => {
            // a cost REDUCTION is good news: minus is green here
            if (dta.section !== 'body' || dta.column.index !== 4) return
            const t = dta.cell.text[0] ?? ''
            if (t.startsWith('-')) { dta.cell.styles.textColor = POS; dta.cell.styles.fontStyle = 'bold' }
            else if (t === '+£0') { dta.cell.styles.textColor = MUTED }
            else if (t.startsWith('+')) { dta.cell.styles.textColor = NEG; dta.cell.styles.fontStyle = 'bold' }
          },
        ),
        didDrawCell: totalsRule(c, catBody.length - 1),
      })
      }
    }
    newPage(c)
    }

    // ================= OPERATIONAL REALITY ===============================
    // What-it-takes as a Today/Proposed table, constraint PASS chips, risks
    // as a Risk/Mitigation table, numbered next steps (spec p7).
    const secOperational = async () => {
    let y = pageStart(c, 'Operational reality and risk', 'Can we actually run it?')
    {
      const halfW = (CW - 6) / 2
      const rightX = M + halfW + 6
      const opsY = sectionLabel(c, 'What it takes', y + 1)
      const opsRows: string[][] = [
        ['Services per operating day', String(d.current.deps.filter((x) => x.enabled).length), String(p.deps.filter((x) => x.enabled).length)],
        ['Steam diagrams', String(d.current.costs.steamN), `${p.costs.steamN} of ${d.fleet.maxSteam} available`],
        ['Diesel diagrams on passenger trains', String(d.current.costs.dieselService + d.current.costs.railcars), `${p.costs.dieselService + p.costs.railcars} of ${d.fleet.maxDiesel + d.fleet.maxRailcar} available`],
        ['Diesel diagrams on empty-stock moves', String(d.current.costs.dieselEcs), `${p.costs.dieselEcs} of ${(d.fleet.maxEcsDiesel ?? 1) + Math.max(0, d.fleet.maxDiesel - p.costs.dieselService)} available`],
        ['Carriage sets in traffic', String(d.current.setsInTraffic), String(p.setsInTraffic)],
        ['Carriages out', String(d.current.costs.carriages), String(p.costs.carriages)],
        ['Footplate crews', String(d.current.costs.diagrams.length), String(p.costs.diagrams.length)],
        ...p.paths.map((pu) => [
          `NR paths — ${pu.segment}`,
          String(d.current.paths.find((x) => x.segment === pu.segment)?.used ?? 0),
          `${pu.used} of ${pu.available}${pu.over ? ' — OVER BUDGET' : ''}`,
        ]),
        ['Coal per operating day', `${d.current.costs.coalTonnes.toFixed(2)} t`, `${p.costs.coalTonnes.toFixed(2)} t`],
        ['CO2e per operating day', `${d.current.costs.co2eTonnes.toFixed(1)} t`, `${p.costs.co2eTonnes.toFixed(1)} t`],
      ]
      const leftEndY = baseTable(c, {
        startY: opsY,
        head: [[
          { content: 'MEASURE', styles: { halign: 'left' } },
          { content: 'TODAY', styles: { halign: 'right' } },
          { content: 'PROPOSED', styles: { halign: 'right' } },
        ]],
        body: opsRows,
        margin: { left: M, right: PW - M - halfW },
        tableWidth: halfW,
        styles: { fontSize: 7.2, cellPadding: { top: 1.7, bottom: 1.7, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 1: { halign: 'right', textColor: MUTED }, 2: { halign: 'right', fontStyle: 'bold' } },
      })
      // right column: the constraint-status card with PASS chips
      const checks: { label: string; ok: boolean; detail: string }[] = [
        { label: 'Single-line crossings', ok: p.conflicts.filter((x) => !x.resolvable).length === 0, detail: `${p.conflicts.filter((x) => !x.resolvable).length} non-workable, ${p.conflicts.filter((x) => x.resolvable).length} normal meets` },
        { label: 'Stock balance', ok: p.imbalances.length === 0, detail: p.imbalances.length === 0 ? 'every rake starts and ends where it can stable' : `${p.imbalances.length} unresolved` },
        { label: 'Nobody left behind', ok: p.scenario.unreturned <= 0.5, detail: `${num0(p.scenario.unreturned)} unreturned per ${d.period}` },
        { label: 'Within the fleet', ok: p.costs.overCap.length === 0, detail: p.costs.overCap.length === 0 ? 'diagrams fit the available engines' : p.costs.overCap.join('; ') },
        { label: 'Within the path budget', ok: !p.paths.some((x) => x.over), detail: p.paths.length === 0 ? 'no external cap applies' : p.paths.map((x) => `${x.used}/${x.available}`).join(', ') },
      ]
      let cy = sectionLabel(c, 'Constraint status', y + 1, rightX)
      const cardTop = cy
      doc.setFontSize(6.5)
      const rowHs = checks.map((k) => {
        const lines = doc.splitTextToSize(k.detail, halfW - 46) as string[]
        return Math.max(8, lines.length * 3 + 5)
      })
      const cardH = rowHs.reduce((t, h) => t + h, 0)
      checks.forEach((k, i) => {
        const rh = rowHs[i]
        if (i % 2 === 1) { doc.setFillColor(PAPER); doc.rect(rightX, cy, halfW, rh, 'F') }
        drawChip(c, rightX + 3, cy + rh / 2, k.ok ? 'PASS' : 'FAIL', k.ok ? POS : NEG, '#ffffff')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(INK)
        doc.text(k.label, rightX + 14, cy + rh / 2 + 1.2)
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(MUTED)
        const dl = doc.splitTextToSize(k.detail, halfW - 60) as string[]
        doc.text(dl, rightX + halfW - 3, cy + rh / 2 + 1.2 - (dl.length - 1) * 1.5, { align: 'right' })
        cy += rh
        if (i < checks.length - 1) { doc.setDrawColor(RULE_LIGHT); doc.setLineWidth(0.2); doc.line(rightX, cy, rightX + halfW, cy) }
      })
      doc.setDrawColor(RULE); doc.setLineWidth(0.25)
      doc.roundedRect(rightX, cardTop, halfW, cardH, 2, 2, 'S')
      cy = cardTop + cardH + 5
      // the honest amber note when a line moves the wrong way
      const coalUp = p.costs.coalTonnes > d.current.costs.coalTonnes + 0.005
      const co2Up = p.costs.co2eTonnes > d.current.costs.co2eTonnes + 0.05
      if (coalUp || co2Up) {
        doc.setFontSize(7.2)
        const warnTxt = `Coal moves ${d.current.costs.coalTonnes.toFixed(2)} t to ${p.costs.coalTonnes.toFixed(2)} t and CO2e ${d.current.costs.co2eTonnes.toFixed(1)} t to ${p.costs.co2eTonnes.toFixed(1)} t per operating day on the worked ${d.monthLabel} day — the remaining engines work harder, and the trade is declared openly in the executive summary.`
        const wl = doc.splitTextToSize(warnTxt, halfW - 8) as string[]
        const wh = wl.length * 3.2 + 9
        doc.setFillColor(WARN_BG)
        doc.roundedRect(rightX, cy, halfW, wh, 2, 2, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.2); doc.setTextColor(WARN_INK)
        doc.text('ONE LINE MOVES THE WRONG WAY', rightX + 4, cy + 4.5, { charSpace: 0.3 })
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.2); doc.setTextColor(INK)
        doc.text(wl, rightX + 4, cy + 8.5)
        cy += wh + 4
      }
      y = Math.max(leftEndY, cy) + 6
    }

    // Risks as a two-column Risk/Mitigation table. The narrative risks are
    // single sentences of "risk … mitigation" prose: split each at its first
    // sentence break; a risk with no split shows the whole text on the left.
    if (d.risks.length > 0) {
      if (y > FOOT_Y - 50) { newPage(c); y = pageStart(c, 'Operational reality and risk', 'Can we actually run it? (continued)') }
      y = sectionLabel(c, 'Risks and mitigations', y)
      const riskRows = d.risks.map((r) => {
        const cut1 = r.indexOf(' — ')
        const cut2 = r.indexOf('. ')
        const cut = cut1 > 15 ? cut1 : cut2 > 15 ? cut2 : -1
        if (cut < 0) return [r, '—']
        const risk = r.slice(0, cut + (cut === cut2 ? 1 : 0)).trim()
        const mit = r.slice(cut + (cut === cut2 ? 2 : 3)).trim()
        return [risk, mit || '—']
      })
      y = baseTable(c, {
        startY: y,
        head: [[
          { content: 'RISK', styles: { halign: 'left', fillColor: CREAM, textColor: MUTED } },
          { content: 'MITIGATION', styles: { halign: 'left', fillColor: CREAM, textColor: MUTED } },
        ]],
        body: riskRows,
        styles: { fontSize: 7.2, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 }, valign: 'top' },
        columnStyles: { 0: { cellWidth: CW / 2, fontStyle: 'bold', textColor: '#7a2b22' } },
      }) + 5
    }
    if (d.nextSteps.length > 0) {
      if (y > FOOT_Y - 35) { newPage(c); y = pageStart(c, 'Operational reality and risk', 'Can we actually run it? (continued)') }
      y = sectionLabel(c, 'Next steps', y)
      numberedList(c, d.nextSteps, y + 1, { size: 7.8 })
    }
    newPage(c)
    }

    // ================= A DAY IN THE LIFE ==================================
    // An example daily service schedule, the two days side by side, per-row
    // load bars colour-banded green/gold/red (spec p8). ECS rows grey.
    const secDayInLife = async () => {
      let y = pageStart(c, 'The example daily schedule', `A day in the life — ${d.monthLabel}, ${d.dayClassLabel.toLowerCase()}`)
      const carriedCur = d.current.scenario.carried
      const carriedProp = d.proposed.scenario.carried
      const curN = d.current.deps.filter((x) => x.enabled && !x.positioning).length
      const propN = d.proposed.deps.filter((x) => x.enabled && !x.positioning).length
      y = richPara(c, [
        { t: `A worked example: one ${d.dayClassLabel.toLowerCase()} in ${d.monthLabel}. Today it runs ` },
        { t: `${curN} workings`, b: true },
        { t: ` and carries ${num0(carriedCur)} passengers; the proposal runs ` },
        { t: String(propN), b: true },
        { t: ` and carries ${num0(carriedProp)} — ` },
        { t: `${Math.round((carriedProp / Math.max(1, carriedCur)) * 100)}% of the same footfall`, b: true },
        { t: ` on ${d.proposed.costs.diagrams.length} engine diagram${d.proposed.costs.diagrams.length === 1 ? '' : 's'} instead of ${d.current.costs.diagrams.length}. Every service below shows who it carries and how full it runs; nobody is left without a train home on either side.` },
      ], y, 10, { width: 168 })
      y += 1
      const halfW = (CW - 6) / 2
      const propRows = dayRowsFor(c, d.proposed)
      const curRows = dayRowsFor(c, d.current)
      const yProp = dayScheduleTable(c, M, halfW, y, BRAND, GOLD, 'THE PROPOSED DAY',
        `${propRows.filter((r) => !r.ecs).length} workings · ${num0(carriedProp)} carried`, propRows)
      const yCur = dayScheduleTable(c, M + halfW + 6, halfW, y, MUTED, '#e6e2d6', 'THE SAME DAY TODAY',
        `${curRows.filter((r) => !r.ecs).length} workings · ${num0(carriedCur)} carried`, curRows)
      y = Math.max(yProp, yCur) + 5
      const lowCur = curRows.filter((r) => !r.ecs && r.pct < 25).length
      const lowProp = propRows.filter((r) => r.pct < 25).length
      const lowPropAllEcs = propRows.filter((r) => r.pct < 25).every((r) => r.ecs)
      para(c,
        `Load bars: green 55% and over · gold under 55% · red under 10%. Today, ${lowCur} of ${curRows.filter((r) => !r.ecs).length} workings run below a 25% load; ` +
        `the proposal has ${lowProp}${lowProp > 0 && lowPropAllEcs ? ', and every one is a positioning move' : ''}.`,
        y, 7, { color: MUTED })
      newPage(c)
    }

    // ==== Fallback public poster (pre-season-plan sessions): active month ===
    // thebigplan's public season-timetable language (Chris attached the
    // Spring/Summer poster as the reference, 2026-08-20): one colour-banded
    // table per pattern and direction, PUBLIC rounded times, a traction line,
    // Newtondale as a request stop. Used only when there is no season poster.
    const secSeasonWttPublic = () => {
    if ((!d.poster || d.poster.patterns.length === 0) && d.seasonWtt && d.seasonWtt.length > 0) {
      let y = pageStart(c, 'Season timetable', 'The proposed season timetable, as the public would see it', 18)
      const POSTER: [RegExp, string, string][] = [
        [/Sunday/i, '#2C7FA6', '#ffffff'],
        [/Saturday/i, '#F2DC3F', '#1d1d1b'],
        [/Weekday|Mon/i, '#EA5B2D', '#ffffff'],
      ]
      const colourFor = (name: string): [string, string] => {
        for (const [re, bg, fg] of POSTER) if (re.test(name)) return [bg, fg]
        return ['#D9679F', '#ffffff']
      }
      const pubTok = (tok: string | null, kind: string): string => {
        if (!tok) return '–'
        if (tok === 'r') return 'R'
        if (tok === 'ns' || tok === 'RR') return '–'
        const digits = String(tok).replace(/[^0-9]/g, '').padStart(4, '0')
        const mins = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2))
        const v = kind === 'd' ? Math.ceil(mins / 5) * 5 : Math.round(mins / 5) * 5
        return fmtMin(v)
      }
      const tractionOf = (loco: string): string =>
        /^R/.test(loco) ? 'Railcar' : /^D/.test(loco) ? 'Diesel' : loco ? 'Steam' : '–'
      const PUB_ROWS: Record<'N' | 'S', [string, string, number][]> = {
        N: [['Pickering', 'd', 0], ['Levisham', 'd', 2], ['Newtondale (request)', 'f', 3], ['Goathland', 'd', 5], ['Grosmont arr', 'a', 6], ['Grosmont dep', 'd', 7], ['Whitby arr', 'a', 8]],
        S: [['Whitby', 'd', 0], ['Grosmont arr', 'a', 1], ['Grosmont dep', 'd', 2], ['Goathland', 'd', 4], ['Newtondale (request)', 'f', 5], ['Levisham', 'd', 7], ['Pickering arr', 'a', 8]],
      }
      for (const pat of d.seasonWtt) {
        const [bg, fg] = colourFor(pat.name)
        for (const [dir, dirLabel, works] of [['N', 'to Whitby', pat.N], ['S', 'to Pickering', pat.S]] as const) {
          if (works.length === 0) continue
          if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, 'Season timetable', 'The proposed season timetable (continued)', 18) }
          y = baseTable(c, {
            startY: y,
            head: [
              [{ content: `${pat.name} — ${dirLabel}`, colSpan: works.length + 1, styles: { fillColor: bg, textColor: fg, fontStyle: 'bold', fontSize: 8.5, halign: 'left' } }],
              [{ content: 'Traction', styles: { halign: 'left', fillColor: CREAM, textColor: MUTED } },
                ...works.map((w) => ({ content: tractionOf(w.loco), styles: { halign: 'center' as const, fillColor: CREAM, textColor: MUTED } }))],
            ],
            body: PUB_ROWS[dir].map(([label, kind, idx]) => [label, ...works.map((w) => pubTok(w.t[idx] ?? null, kind))]),
            styles: { fontSize: 7.2, cellPadding: { top: 1.4, bottom: 1.4, left: 2.4, right: 2.4 }, halign: 'center', lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
            columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 34 } },
          }) + 4
        }
        y += 3
      }
      y = para(c, 'Public times are auto-derived from the working timetable: departures round up to the next 5 minutes, arrivals to the nearest 5. R = stops on request. The exact working timetable, with crossings and stock workings, is Appendix A.', y + 1, 7, { color: MUTED })
      newPage(c)
    }
    }

    // ===================================================================
    //  REPORT A (Chris, 2026-08-27) — the full board paper, in his section
    //  order. The Weekly Service Schedule grid (§9) and the per-month appendix
    //  are the next increment; the body below is complete and self-contained.
    // ===================================================================
    if (d.variant === 'A') {
      execSummaryPage(c, d)                                    // 1  Executive summary (as report B)
      seasonRollupPage(c, d)                                   // 2  Season-by-month table, own page
      await secSituation()                                     // 3  The situation today
      await secSeasonCosts()                                   // 4  The season by month & where the costs sit
      await secProposed()                                      // 5  The proposed solution
      costsModellingSection(c, d)                              // 6  Costs & modelling data
      modellingCostsPnlSection(c, d)                           // 6b Modelling costs (Management P&L basis)
      if (d.poster && d.poster.patterns.length > 0) buildSeasonPosterPages(c, d.poster) // 7  The proposed season (build-tab view)
      if (d.cuts && d.cuts.length > 0) cutsSection(c, d.cuts)  // 8  What we cut and why it saves
      // 9  Weekly Service Schedule — one example month (August) in the body.
      const wss = d.wssMonths ?? []
      const bodyWss = wss.find((m) => /Aug/i.test(m.label)) ?? wss[0]
      // The body WSS is the example month (August): it carries the line-by-line
      // example-day cost table at its head, AND the working turns for BOTH the
      // optimised and unoptimised timetables (Chris, 2026-08-27). Appendix months
      // get the grids only — the worked-day breakdown is the example's alone.
      const bodyAug = bodyWss ? (d.appendixMonths ?? []).find((am) => am.key === bodyWss.key) : undefined
      const bodySides = bodyAug ? { proposed: bodyAug.proposed, current: bodyAug.current } : null
      if (bodyWss) wssMonthSection(c, bodyWss, 'The example month', 'This is one example month; every month is built into the appendix for the operating team to study. It uses actual data to demonstrate the changes and support the plan.', d.exampleDay, bodySides)
      if (d.monthPnl && d.monthPnl.length > 0) { monthPnlAppendix(c, d.monthPnl); newPage(c) } // 10 The plan, month by month
      // The fleet, month by month (the availability calendar) — Report A too.
      if (d.fleetMonths && d.fleetMonths.length > 0) fleetAvailabilitySection(c, d.fleetMonths)
      // monthPnlAppendix has NO trailing newPage by design (the B-branch adds one
      // at its call site); Report A must add it too or the first appendix month's
      // Weekly Service Schedule overprints the tail of the month-by-month P&L.
      // ---- APPENDIX — one complete block per month, in order (not interleaved) ----
      // EVERY month gets a full block, August included: the body §9 shows August
      // as "the example month", but the appendix is the complete per-month
      // reference and must not have a hole where August should be (Chris, 2026-08-27).
      for (const m of wss) {
        wssMonthSection(c, m, `Appendix · ${m.label}`)
        const am = (d.appendixMonths ?? []).find((x) => x.key === m.key)
        if (am) await appendixMonthDetail(c, am)
        // The month's costs on the P&L — the season table's own row for it.
        const mmA = Number(m.key.split('-')[1])
        const yrA = (d.yearRows ?? []).find((r) => r.monthNo === mmA && (r.runningDays ?? 0) > 0 && (r.cur || r.mod))
        if (yrA) monthCostsSection(c, { label: m.label, runningDays: yrA.runningDays ?? 0, cur: monthFigures(yrA.cur, yrA.runningDays), mod: monthFigures(yrA.mod, yrA.runningDays) }, `Appendix · ${m.label}`)
      }
      drawFooters(c)
      const safeA = (d.season || 'season').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
      const blobA = doc.output('blob')
      const urlA = URL.createObjectURL(blobA)
      const aA = document.createElement('a')
      aA.href = urlA
      aA.download = `NYMR-Timetable-Board-Paper-${safeA}.pdf`
      document.body.appendChild(aA); aA.click(); aA.remove()
      setTimeout(() => URL.revokeObjectURL(urlA), 4000)
      return
    }

    // ===================================================================
    //  THE BOARD PAPER, in the order Chris asked for (2026-08-26):
    //  1 executive summary (under the recommendation banner drawn above) ·
    //  2 the situation today · 3 the proposed solution ·
    //  4 the season by month & where the costs sit · 5 what we cut & why ·
    //  6 the plan month by month · 7 the proposed season timetable ·
    //  then EVERYTHING ELSE to the appendix.
    // ===================================================================
    execSummaryPage(c, d)
    await secSituation()
    await secProposed()
    await secSeasonCosts()
    if (d.cuts && d.cuts.length > 0) cutsSection(c, d.cuts)
    if (d.monthPnl && d.monthPnl.length > 0) { monthPnlAppendix(c, d.monthPnl); newPage(c) }
    if (d.poster && d.poster.patterns.length > 0) posterPages(c, d.poster)
    // ---- Appendix: the detail a director skips and an operations manager reads ----
    await secCaseNowAfter()
    await secCaseShape()
    await secMoney()
    await secOperational()
    if (d.fleetMonths && d.fleetMonths.length > 0) fleetAvailabilitySection(c, d.fleetMonths)
    await secDayInLife()
    secSeasonWttPublic()

    // ================= APPENDIX A — WORKING TIMETABLE ====================
    // The season timetable in thebigplan's TTBuilder format (Chris,
    // 2026-08-20): one section per proposed PATTERN, each with the headcode
    // band (gold on brand), Loco/Set meta rows (cream), the timing-point grid
    // and the Forms chain — the layout the railway builds timetables in.
    if (d.seasonWtt && d.seasonWtt.length > 0) {
      y = pageStart(c, 'Appendix A', `Proposed season working timetable — ${d.monthLabel}`, 18)
      for (const pat of d.seasonWtt) {
        if (y > FOOT_Y - 80) { newPage(c); y = pageStart(c, 'Appendix A', `Proposed season working timetable — ${d.monthLabel} (continued)`, 18) }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(BRAND)
        doc.text(pat.name, M, y); y += 4
        for (const [dirKey, dirLabel, works] of [['N', 'Northbound · up to Whitby', pat.N], ['S', 'Southbound · down to Pickering', pat.S]] as const) {
          if (works.length === 0) continue
          if (y > FOOT_Y - 70) {
            newPage(c); y = pageStart(c, 'Appendix A', `Proposed season working timetable — ${d.monthLabel} (continued)`, 18)
            doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(BRAND)
            doc.text(`${pat.name} (continued)`, M, y); y += 4
          }
          doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(GOLD_TEXT)
          doc.text(dirLabel.toUpperCase(), M, y, { charSpace: 0.3 }); doc.setTextColor(INK); y += 2.5
          y = wttTable(c, works as TtExportWorking[], dirKey, y, 'req/pass') + 5
        }
        y += 3
      }
      newPage(c)
      y = pageStart(c, 'Appendix A (detail)', 'Proposed workings — loads', 18)
    } else {
      y = pageStart(c, 'Appendix A', 'Proposed working timetable', 18)
    }
    for (const [dirKey, dirLabel] of [['Northbound', 'Northbound'], ['Southbound', 'Southbound']] as const) {
      const rows = p.deps
        .filter((x) => x.enabled && x.direction === dirKey)
        .sort((a, b) => a.minutes - b.minutes)
        .map((x) => {
          const r = p.scenario.perDep.find((q) => q.id === x.id)
          return [
            x.time,
            d.net.stations[x.o].tt,
            d.net.stations[x.d].tt,
            fmtMin(x.minutes + journeyMin(d.net, x.o, x.d)),
            x.group === 'shuttle' ? 'Shuttle' : 'Main',
            x.coaches === 0 ? 'Autocar' : String(x.coaches),
            x.positioning ? 'ECS' : (r ? num0(r.allocated) : '0'),
          ]
        })
      if (rows.length === 0) continue
      y = sectionLabel(c, dirLabel, y)
      y = baseTable(c, {
        startY: y,
        head: [[
          { content: 'DEP', styles: { halign: 'left' } }, { content: 'FROM', styles: { halign: 'left' } },
          { content: 'TO', styles: { halign: 'left' } }, { content: 'ARR', styles: { halign: 'left' } },
          { content: 'ROUTE', styles: { halign: 'left' } }, { content: 'COACHES', styles: { halign: 'right' } },
          { content: 'CARRIED', styles: { halign: 'right' } },
        ]],
        body: rows,
        styles: { fontSize: 7, cellPadding: { top: 1.4, bottom: 1.4, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
      }) + 6
      if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, 'Appendix A', 'Proposed working timetable (continued)', 18) }
    }
    newPage(c)

    // ================= APPENDIX B — SERVICE BY SERVICE ==================
    y = pageStart(c, 'Appendix B', 'Service by service — current vs proposed', 18)
    for (const [side, sideLabel] of [[d.current, 'Current (as drawn)'], [p, 'Proposed']] as const) {
      const rows = side.deps
        .filter((x) => x.enabled)
        .sort((a, b) => a.minutes - b.minutes)
        .map((x) => {
          const r = side.scenario.perDep.find((q) => q.id === x.id)
          const cap = r?.capacity ?? 0
          const load = cap > 0 && r ? Math.round((r.allocated / cap) * 100) : 0
          return [
            x.time,
            `${d.net.stations[x.o].tt}–${d.net.stations[x.d].tt}`,
            x.positioning ? 'ECS' : (r ? num0(r.allocated) : '0'),
            String(cap),
            x.positioning ? '—' : `${load}%`,
            x.positioning || !r ? '—' : gbp0(r.outRevenue),
          ]
        })
      if (rows.length === 0) continue
      y = sectionLabel(c, sideLabel, y)
      y = baseTable(c, {
        startY: y,
        head: [[
          { content: 'DEP', styles: { halign: 'left' } }, { content: 'ROUTE', styles: { halign: 'left' } },
          { content: 'CARRIED', styles: { halign: 'right' } }, { content: 'SEATS', styles: { halign: 'right' } },
          { content: 'LOAD', styles: { halign: 'right' } }, { content: 'REVENUE (OUT)', styles: { halign: 'right' } },
        ]],
        body: rows,
        styles: { fontSize: 7, cellPadding: { top: 1.4, bottom: 1.4, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: { 0: { fontStyle: 'bold' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
      }) + 6
      if (y > FOOT_Y - 60) { newPage(c); y = pageStart(c, 'Appendix B', 'Service by service (continued)', 18) }
    }
    newPage(c)

    // ================= APPENDIX C — COST MODEL ==========================
    y = pageStart(c, 'Appendix C', 'Cost model, unoptimised and optimised', 18)
    const curLines = costLines(d.current.costs, d.rates)
    const propLines = costLines(p.costs, d.rates)
    y = baseTable(c, {
      startY: y,
      head: [[
        { content: 'COST LINE', styles: { halign: 'left' } },
        { content: 'BASIS (PROPOSED)', styles: { halign: 'left' } },
        { content: 'TODAY £/DAY', styles: { halign: 'right' } },
        { content: 'PROPOSED £/DAY', styles: { halign: 'right' } },
        { content: 'CHANGE', styles: { halign: 'right' } },
      ]],
      body: curLines.map((cl, i) => [
        cl.label,
        tidyBasis(propLines[i]?.detail ?? cl.detail),
        gbp0(cl.amount),
        gbp0(propLines[i]?.amount ?? 0),
        signed((propLines[i]?.amount ?? 0) - cl.amount),
      ]),
      foot: [[
        'Total per operating day', '',
        gbp0(curLines.reduce((t, l) => t + l.amount, 0)),
        gbp0(propLines.reduce((t, l) => t + l.amount, 0)),
        signed(propLines.reduce((t, l) => t + l.amount, 0) - curLines.reduce((t, l) => t + l.amount, 0)),
      ]],
      styles: { fontSize: 7.2, cellPadding: { top: 1.5, bottom: 1.5, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
      columnStyles: { 0: { fontStyle: 'bold' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
      didParseCell: mutedCols([1]),
    }) + 8

    if (p.transfers.length > 0) {
      y = sectionLabel(c, 'Interchange performance', y)
      baseTable(c, {
        startY: y,
        head: [[
          { content: 'STATION', styles: { halign: 'left' } }, { content: 'CONNECTIONS', styles: { halign: 'right' } },
          { content: 'PEAK WAITING', styles: { halign: 'right' } }, { content: 'AT', styles: { halign: 'right' } },
          { content: 'MEAN WAIT', styles: { halign: 'right' } }, { content: 'PEAK TRAINLOADS', styles: { halign: 'right' } },
          { content: 'PLATFORMS', styles: { halign: 'right' } },
        ]],
        body: p.transfers.map((t) => [
          d.net.stations[t.station].name,
          num0(t.transferred),
          num0(t.peakWaiting),
          fmtMin(t.peakAtMin),
          `${Math.round(t.meanWaitMin)} min`,
          String(t.peakTrainloads),
          String(t.platforms),
        ]),
        columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
      })
    }
    newPage(c)

    // ================= APPENDIX D — ASSUMPTIONS ==========================
    y = pageStart(c, 'Appendix D', 'Assumptions register', 18)
    y = standfirst(c,
      'Every number the model runs on, and where it came from. A figure marked ASSUMED is not measured from the sales ' +
      'data; it is a judgement, and the headline is quoted as a range because of it.', y)
    const chipFor = (prov: ExecAssumption['provenance']) =>
      prov === 'measured' ? { bg: '#e6f0e6', ink: POS, label: 'MEASURED' }
        : prov === 'assumed' ? { bg: WARN_BG, ink: WARN_INK, label: 'ASSUMED' }
          : { bg: CLAUDE_BG, ink: CLAUDE_INK, label: 'CLAUDE' }
    const chipRows = d.assumptions.map((a) => chipFor(a.provenance))
    baseTable(c, {
      startY: y + 1,
      head: [[
        { content: 'PARAMETER', styles: { halign: 'left' } },
        { content: 'VALUE', styles: { halign: 'right' } },
        { content: 'BASIS', styles: { halign: 'center' } },
        { content: 'NOTE', styles: { halign: 'left' } },
      ]],
      body: d.assumptions.map((a) => [a.name, a.value, '', a.note]),
      styles: { fontSize: 7.2, cellPadding: { top: 1.6, bottom: 1.6, left: 2.4, right: 2.4 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 }, valign: 'top' },
      columnStyles: { 0: { cellWidth: 44, fontStyle: 'bold' }, 1: { cellWidth: 24, halign: 'right' }, 2: { cellWidth: 22, halign: 'center' }, 3: { cellWidth: 'auto', textColor: MUTED } },
      didDrawCell: (dta: ATHook) => {
        if (dta.section !== 'body' || dta.column.index !== 2) return
        const chip = chipRows[dta.row.index]
        if (!chip) return
        doc.setFont('helvetica', 'bold'); doc.setFontSize(5.5)
        const cwid = doc.getTextWidth(chip.label) + 4
        drawChip(c, dta.cell.x + (dta.cell.width - cwid) / 2, dta.cell.y + 3.4, chip.label, chip.bg, chip.ink)
      },
    })
    newPage(c)

    // ================= APPENDIX E — METHOD & LIMITS ======================
    y = pageStart(c, 'Appendix E', 'Method and limitations', 18)
    const method = [
      'The model treats observed sales as ORIGINATING demand by desired departure time in each direction. Each block of demand books the cheapest feasible journey within a tolerance window; if that train is full it spills to the next best, and only a fraction survives the shift. The full fare is captured once, on the outward leg. Return legs fill the seats left over and earn nothing extra, because it is the same ticket.',
      'Capacity is not a slider. A carriage set is a fixed rake for a whole day and may only be exchanged at a stable, so a train’s capacity IS its assigned set. Engine diagrams chain out and back all day. Single-line crossings are checked section by section, and stations with no stabling must receive a rake before one can depart.',
      d.censoringNote ??
        'Demand was NOT corrected for censoring in this run, so peak demand is understated wherever a service sold out: the model can only see what we managed to seat.',
      d.backtest
        ? `Calibration: replaying the observed timetable for ${d.backtest.season} reproduces actual revenue to within ${(d.backtest.mape * 100).toFixed(1)}% across ${d.backtest.sliceCount} month/day-type slices, ${d.backtest.bias >= 0 ? 'over' : 'under'}-predicting by ${Math.abs(d.backtest.bias * 100).toFixed(1)}% overall. That error is carried into the headline as a band.`
        : 'Calibration: NOT RUN for this report. The figures rank options against one another but should not be read as a forecast.',
      ...(d.backtest?.caveats ?? []),
      d.net.mode === 'grosmont'
        ? 'Mode B is a counterfactual. Every passenger in the data travelled on a through train, so interchange aversion, connection behaviour and platform transfer times cannot be measured from ticket sales — they are assumptions with industry-standard defaults, and the thirty-minute connection time is an operational measurement supplied by the railway rather than a modelled figure. Treat the interchange parameters as the thing most likely to be wrong.'
        : 'This option runs the network as it runs today, so every parameter is observable in the sales data.',
      'What would change the conclusion: a materially different connection time at Grosmont, a path budget different from the one we use today, a fleet availability lower than assumed, or a demand response to price outside the -0.3 elasticity used here.',
    ]
    for (const m of method) {
      y = para(c, m, y, 8.3, { leading: 1.5 })
      y += 2
      if (y > FOOT_Y - 30) { newPage(c); y = pageStart(c, 'Appendix E', 'Method and limitations (continued)', 18) }
    }

    // ================= APPENDIX F — CLAUDE RUN LOG =======================
    if (d.advisor && d.advisor.rounds.length > 0) {
      newPage(c)
      y = pageStart(c, 'Appendix F', 'Tuning run log', 18)
      y = standfirst(c,
        'Claude proposed each configuration below; this engine scored every one of them. The figures are the engine’s, ' +
        'not the model’s — which is what makes the recommendation auditable.', y)
      const rows: string[][] = []
      for (const r of d.advisor.rounds) {
        for (const res of r.results) {
          const cand = r.candidates.find((x) => x.id === res.id)
          rows.push([
            String(r.round),
            res.id,
            res.mode === 'grosmont' ? 'Grosmont' : 'Through',
            cand?.rationale ?? '',
            gbp0(res.revenue),
            gbp0(res.costs),
            gbp0(res.contribution),
            num0(res.unreturned),
          ])
        }
      }
      baseTable(c, {
        startY: y + 1,
        head: [[
          { content: 'RD', styles: { halign: 'right' } }, { content: 'CANDIDATE', styles: { halign: 'left' } },
          { content: 'MODE', styles: { halign: 'left' } }, { content: 'WHY IT WAS TRIED', styles: { halign: 'left' } },
          { content: 'REVENUE', styles: { halign: 'right' } }, { content: 'COSTS', styles: { halign: 'right' } },
          { content: 'CONTRIBUTION', styles: { halign: 'right' } }, { content: 'UNRET.', styles: { halign: 'right' } },
        ]],
        body: rows,
        styles: { fontSize: 6.6, cellPadding: { top: 1.2, bottom: 1.2, left: 2.2, right: 2.2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 }, valign: 'top' },
        columnStyles: { 0: { halign: 'right' }, 3: { textColor: MUTED }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' }, 7: { halign: 'right' } },
      })
    }

    // ============ APPENDIX G — THE MONTH, DAY BY DAY OF WEEK =============
    // The Weekly Service Schedule's month view (Chris, 2026-08-20): each day
    // of week both sides — services, locos, passengers, revenue, costs,
    // profit. The calendar repeats these across the month's weeks, so the
    // seven rows ARE the month view.
    if (d.monthView && d.monthView.some((r) => r.cur || r.mod)) {
      newPage(c)
      y = pageStart(c, 'Appendix G', `The month day by day — ${d.monthLabel}`, 18)
      const sideCells = (sv: ExecMonthViewSide | null): string[] =>
        sv ? [`${sv.services} (${sv.steamN}S${sv.dieselN > 0 ? `+${sv.dieselN}D` : ''})`, num0(sv.pax), gbp0(sv.revenue), gbp0(sv.costs), signed(sv.profit)] : ['—', '—', '—', '—', '—']
      const subHeadG = { fontSize: 6, textColor: '#cfe0d6', fontStyle: 'bold' as const }
      y = baseTable(c, {
        startY: y,
        head: [
          [
            { content: 'DAY', rowSpan: 2, styles: { halign: 'left', valign: 'bottom' } },
            { content: 'CURRENT', colSpan: 5, styles: { halign: 'center', fillColor: BRAND_MID } },
            { content: 'PROPOSED', colSpan: 5, styles: { halign: 'center' } },
          ],
          [
            { content: 'SVC (LOCOS)', styles: { ...subHeadG, halign: 'left', fillColor: BRAND_MID } },
            { content: 'PAX', styles: { ...subHeadG, halign: 'right', fillColor: BRAND_MID } },
            { content: 'REVENUE', styles: { ...subHeadG, halign: 'right', fillColor: BRAND_MID } },
            { content: 'COSTS', styles: { ...subHeadG, halign: 'right', fillColor: BRAND_MID } },
            { content: 'PROFIT', styles: { ...subHeadG, halign: 'right', fillColor: BRAND_MID } },
            { content: 'SVC (LOCOS)', styles: { ...subHeadG, halign: 'left' } },
            { content: 'PAX', styles: { ...subHeadG, halign: 'right' } },
            { content: 'REVENUE', styles: { ...subHeadG, halign: 'right' } },
            { content: 'COSTS', styles: { ...subHeadG, halign: 'right' } },
            { content: 'PROFIT', styles: { ...subHeadG, halign: 'right' } },
          ],
        ],
        body: d.monthView.map((r) => [r.dow, ...sideCells(r.cur), ...sideCells(r.mod)]),
        styles: { fontSize: 6.8, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 }, lineColor: RULE_LIGHT, lineWidth: { bottom: 0.2 } },
        columnStyles: {
          0: { fontStyle: 'bold' },
          ...Object.fromEntries([2, 3, 4, 5, 7, 8, 9, 10].map((i) => [i, { halign: 'right' }])),
        },
        didParseCell: hooks(signCols([5, 10]), mutedCols([1, 6])),
        didDrawCell: (dta: ATHook) => {
          if (dta.column.index === 1 || dta.column.index === 6) {
            doc.setDrawColor(dta.section === 'head' ? (dta.column.index === 6 ? GOLD : '#ffffff') : (dta.column.index === 6 ? '#e9dcbe' : RULE_LIGHT))
            doc.setLineWidth(0.5)
            doc.line(dta.cell.x, dta.cell.y, dta.cell.x, dta.cell.y + dta.cell.height)
          }
        },
      }) + 6
      para(c, 'Every calendar date of the month runs its day-of-week model above (same demand curves, sliders and prices both sides — the difference is purely the timetable). The interactive month calendar with per-date drill-downs lives on the Studio’s Compare stage.', y, 7, { color: MUTED })
    }

    // (The plan month by month — formerly Appendix H — now leads the body as
    //  section 6, called in the orchestration above.)
    // ============ APPENDIX I — RUNNING DIAGRAMS PER PATTERN ===============
    if (d.planWtt && d.planWtt.length > 0) {
      newPage(c)
      planWttAppendix(c, d.planWtt)
    }

    // Footers land last so every page reads "Page n of N".
    drawFooters(c)

    const safe = (d.seasonSummary ? `${d.season} season` : `${d.monthLabel} ${d.dayClassLabel} ${modeLabel}`).replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
    // A real download, never a popup — matches the Bookings / Master View pattern.
    const blob = doc.output('blob')
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `NYMR-Timetable-Executive-Report-${safe}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 4000)
  } finally {
    host.remove()
  }
}
